
import React, { useState, useEffect, useMemo, useContext, createContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import notificationSound from './assets/Notification.mp3';

const modelViewStateCache = new Map();
let modelViewerElementPromise = null;

function ensureModelViewerElementLoaded() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('model-viewer')) return Promise.resolve();
  if (!modelViewerElementPromise) {
    modelViewerElementPromise = import('@google/model-viewer').catch((error) => {
      modelViewerElementPromise = null;
      throw error;
    });
  }
  return modelViewerElementPromise;
}

const Icon = {
  Dashboard: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Package: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  ShoppingCart: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L23 6H6"/>
    </svg>
  ),
  Tag: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  Palette: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
    </svg>
  ),
  Download: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Settings: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Plus: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Edit: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Trash: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  Eye: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  Activity: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  Search: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
    </svg>
  ),
  Rocket: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 13l4 4M14 3c4.5 0 7 2.5 7 7 0 2.2-1.1 4.9-3 6.8-1.9 1.9-4.6 3-6.8 3-4.5 0-7-2.5-7-7 0-2.2 1.1-4.9 3-6.8C8.1 4.1 10.8 3 13 3Z"/>
      <path d="M9 15l6-6M12 12l3 3"/>
    </svg>
  ),
  Megaphone: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11v2a3 3 0 0 0 3 3h3l7 4V4l-7 4H6a3 3 0 0 0-3 3Z"/>
      <path d="M14 7v10"/>
    </svg>
  ),
  Folder: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>
    </svg>
  ),
  LogOut: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  User: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Loading: ({ className = '', ...props }) => (
    <svg
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={("animate-spin " + className).trim()}
    >
      <path d="M21 12a9 9 0 11-6.219-8.56"/>
    </svg>
  ),
  Upload: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Alert: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  X: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Calendar: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Percent: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>
    </svg>
  ),
  Mail: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  )
};

const API_BASE = '/shopapi';
const ADMIN_NOTIFICATION_SOUND_KEY = 'shopstarter_admin_notification_sound';
const DEFAULT_ADMIN_NOTIFICATION_SOUND = 'classic';
const PRODUCT_IMAGE_PLACEHOLDER_URL = `${API_BASE}/placeholder/120/120`;
const PRODUCT_IMAGE_INLINE_FALLBACK = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" role="img" aria-label="No image"><rect width="120" height="120" fill="#f3f4f6"/><rect x="1" y="1" width="118" height="118" fill="none" stroke="#d1d5db" stroke-width="2"/><g fill="#9ca3af"><circle cx="42" cy="44" r="10"/><path d="M18 86l24-24 18 16 18-12 24 20z"/></g><text x="60" y="105" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="12" fill="#6b7280">No image</text></svg>')}`;

function applyFaviconHref(href) {
  try {
    if (typeof document === 'undefined') return;
    const rawHref = String(href || '/logo.png').trim() || '/logo.png';
    const finalHref = resolveMediaUrl(rawHref);
    const lower = finalHref.toLowerCase();
    let iconType = '';
    if (lower.endsWith('.ico') || lower.includes('.ico?')) iconType = 'image/x-icon';
    else if (lower.endsWith('.svg') || lower.includes('.svg?')) iconType = 'image/svg+xml';
    else if (lower.endsWith('.png') || lower.includes('.png?')) iconType = 'image/png';
    else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.includes('.jpg?') || lower.includes('.jpeg?')) iconType = 'image/jpeg';
    const ensureLink = (relValue) => {
      let el = document.head.querySelector(`link[rel="${relValue}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', relValue);
        document.head.appendChild(el);
      }
      el.setAttribute('href', finalHref);
      if (iconType) el.setAttribute('type', iconType);
      else el.removeAttribute('type');
    };
    ensureLink('icon');
    ensureLink('shortcut icon');
  } catch {}
}

function applyProductImageFallback(event) {
  const img = event?.currentTarget;
  if (!img) return;
  const src = String(img.getAttribute('src') || '').trim();
  if (src && src !== PRODUCT_IMAGE_PLACEHOLDER_URL && img.dataset.placeholderTried !== '1') {
    img.dataset.placeholderTried = '1';
    img.src = PRODUCT_IMAGE_PLACEHOLDER_URL;
    return;
  }
  img.onerror = null;
  img.src = PRODUCT_IMAGE_INLINE_FALLBACK;
}

function readAdminNotificationSound() {
  try {
    const raw = String(localStorage.getItem(ADMIN_NOTIFICATION_SOUND_KEY) || '').trim().toLowerCase();
    if (['classic', 'custom', 'beep', 'off'].includes(raw)) return raw;
  } catch {}
  return DEFAULT_ADMIN_NOTIFICATION_SOUND;
}

function getStorefrontBaseUrl() {
  try {
    const loc = window.location;
    const protocol = loc.protocol;
    const hostname = loc.hostname;
    const port = String(loc.port || '');
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (port === '3001' || port === '3002') return `${protocol}//${hostname}:3000`;
    }
    if (hostname.startsWith('dosh-hazar.')) {
      return `${protocol}//${hostname.replace(/^dosh-hazar\./, '')}`;
    }
    return `${protocol}//${loc.host}`;
  } catch {
    return '';
  }
}

function resolveMediaUrl(u) {
  if (!u) return '';
  let s = String(u).trim();
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }
  s = s.replace(/\\/g, '/');
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (s.startsWith('/shopapi/uploads/')) return s;
  if (s.startsWith('/uploads/')) return `/shopapi${s}`;
  if (s.startsWith('uploads/')) return `/shopapi/${s}`;
  if (!s.startsWith('/')) return `/shopapi/uploads/${s}`;
  return s;
}

function parseStringJsonArray(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    return Array.isArray(raw) ? raw.map((entry) => String(entry || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function parseProductVariants(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .map((entry, index) => ({
        id: String(entry?.id || `variant_${index + 1}`).trim(),
        label: String(entry?.label || '').trim(),
        sku: String(entry?.sku || '').trim(),
        description: String(entry?.description || '').trim(),
        price: Math.max(0, Number(entry?.price ?? entry?.basePrice) || 0),
        basePrice: Math.max(0, Number(entry?.basePrice ?? entry?.price) || 0),
        originalPrice: Number(entry?.originalPrice) > Number(entry?.price ?? entry?.basePrice)
          ? Number(entry.originalPrice)
          : null,
        stock: Math.max(0, parseInt(entry?.stock, 10) || 0),
        active: entry?.active !== false && entry?.active !== 0 && entry?.active !== 'false',
        img: String(entry?.img || entry?.imageUrl || '').trim(),
      }))
      .filter((entry) => entry.label || entry.sku || entry.id);
  } catch {
    return [];
  }
}

function summarizeProductForAdmin(product) {
  const gallery = parseStringJsonArray(product?.images);
  const variants = parseProductVariants(product?.variants);
  const activeVariants = variants.filter((variant) => variant.active !== false);
  const basePrice = Number(product?.baseDisplayPrice ?? product?.basePrice ?? product?.price) || 0;
  const baseOriginalPrice = Number(product?.baseOriginalPrice) > basePrice ? Number(product.baseOriginalPrice) : null;
  const baseStock = Number(product?.baseStock ?? product?.base_stock ?? product?.stock) || 0;
  const hideBaseModel = product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true;
  const displayCandidate = [
    ...(!hideBaseModel ? [{ price: basePrice, originalPrice: baseOriginalPrice }] : []),
    ...activeVariants.map((variant) => ({
      price: Number(variant.price) || 0,
      originalPrice: Number(variant.originalPrice) || null
    }))
  ].reduce((best, current) => (
    !best || Number(current.price) < Number(best.price) ? current : best
  ), null);
  const displayPrice = Number(displayCandidate?.price ?? basePrice) || 0;
  const displayStock = activeVariants.length > 0
    ? (!hideBaseModel ? baseStock : 0) + activeVariants.reduce((sum, variant) => sum + (Number(variant.stock) || 0), 0)
    : baseStock;
  const title = String(product?.title || '').trim() || 'Untitled product';
  const category = String(product?.category || '').trim() || 'Uncategorized';
  const color = String(product?.color || '').trim() || '-';
  const primaryImage = String(product?.img || '').trim() || activeVariants[0]?.img || gallery[0] || PRODUCT_IMAGE_PLACEHOLDER_URL;
  return {
    ...product,
    images: gallery,
    variants,
    hideBaseModel: product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true,
    _title: title,
    _category: category,
    _color: color,
    _primaryImage: primaryImage,
    _displayPrice: displayPrice,
    _displayOriginalPrice: Number(displayCandidate?.originalPrice) > displayPrice ? Number(displayCandidate.originalPrice) : null,
    _displayStock: displayStock,
    _variantCount: activeVariants.length,
    _isActive: product?.active !== false,
    _searchHaystack: `${title} ${category} ${color} ${String(product?.dna_id || '')} ${activeVariants.map((variant) => `${variant.label} ${variant.sku}`).join(' ')}`.toLowerCase()
  };
}

function hasFormChanges(current, initial) {
  try {
    return JSON.stringify(current) !== JSON.stringify(initial);
  } catch {
    return true;
  }
}

function confirmDiscardChanges(isDirty) {
  if (!isDirty) return true;
  return window.confirm('Discard unsaved changes?');
}

const PRIVILEGED_ROLES = new Set(['admin']);
const isPrivilegedRole = (role) => PRIVILEGED_ROLES.has(String(role || '').trim().toLowerCase());
const USER_ROLE_OPTIONS = ['admin', 'manager', 'staff'];
const DEFAULT_PRODUCT_TAGS = ['general', 'new', 'trending', 'bestseller'];
const USER_PERMISSION_GROUPS = [
  {
    id: 'dashboard',
    title: 'Dashboard & Monitoring',
    permissions: ['dashboard:view', 'admin.stream:read', 'analytics:read', 'observability:read', 'metrics:read']
  },
  {
    id: 'users',
    title: 'Users & Access',
    permissions: ['users:read', 'users:write']
  },
  {
    id: 'products',
    title: 'Catalog & Orders',
    permissions: ['products:read', 'products:write', 'products:delete', 'orders:read', 'orders:write', 'orders:status', 'orders:tracking', 'orders:invoice', 'exports:read']
  },
  {
    id: 'marketing',
    title: 'Marketing & Content',
    permissions: ['campaigns:read', 'campaigns:write', 'campaigns:delete', 'promotions:read', 'promotions:write', 'promotions:delete']
  },
  {
    id: 'support',
    title: 'Support & Reviews',
    permissions: ['reviews:read', 'reviews:moderate', 'help:read', 'help:reply', 'help:status', 'audit:read', 'audit:undo']
  },
  {
    id: 'ops',
    title: 'Media, Settings & Backup',
    permissions: ['media:read', 'media:write', 'media:delete', 'settings:read', 'settings:write', 'customization:write', 'legal:write', 'backup:manage', 'backup:restore', 'system:restart', 'notifications:manage']
  }
];
const ALL_USER_PERMISSIONS = USER_PERMISSION_GROUPS.flatMap(group => group.permissions);
const makePermissionPreset = (enabledKeys = []) => {
  const enabled = new Set(enabledKeys);
  const out = {};
  for (const key of ALL_USER_PERMISSIONS) out[key] = enabled.has(key);
  return out;
};
const ROLE_PERMISSION_PRESETS = {
  admin: makePermissionPreset(ALL_USER_PERMISSIONS),
  manager: makePermissionPreset([
    'dashboard:view',
    'admin.stream:read',
    'analytics:read',
    'metrics:read',
    'products:read',
    'products:write',
    'orders:read',
    'orders:write',
    'orders:status',
    'orders:tracking',
    'orders:invoice',
    'exports:read',
    'campaigns:read',
    'campaigns:write',
    'promotions:read',
    'promotions:write',
    'reviews:read',
    'reviews:moderate',
    'help:read',
    'help:reply',
    'help:status',
    'media:read',
    'media:write',
    'settings:read',
    'customization:write'
  ]),
  staff: makePermissionPreset([
    'dashboard:view',
    'admin.stream:read',
    'analytics:read',
    'metrics:read',
    'orders:read',
    'orders:status',
    'orders:tracking',
    'orders:invoice',
    'reviews:read',
    'help:read',
    'help:reply',
    'media:read'
  ])
};
const getRolePresetPermissions = (role) => {
  const normalized = String(role || 'staff').trim().toLowerCase();
  return { ...(ROLE_PERMISSION_PRESETS[normalized] || ROLE_PERMISSION_PRESETS.staff) };
};
const normalizePermissions = (permissions, role = 'staff') => {
  const base = getRolePresetPermissions(role);
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) return base;
  for (const key of ALL_USER_PERMISSIONS) {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) base[key] = !!permissions[key];
  }
  return base;
};
const hasUserPermission = (user, permission) => isPrivilegedRole(user?.role) || !!(user?.permissions && user.permissions[permission]);
const prettyPermissionLabel = (key) => key.split(':').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

const HERO_BANNER_TEMPLATE = {
  id: '',
  headline: '',
  subheadline: '',
  image: '',
  ctaLabel: '',
  href: '',
  imageHref: '',
  active: true
};

const createHeroBanner = () => ({
  ...HERO_BANNER_TEMPLATE,
  id: `hero_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`
});

const normalizeHeroBanners = (banners) => {
  if (!Array.isArray(banners)) return [];
  const timestamp = Date.now();
  return banners.map((banner, idx) => ({
    ...HERO_BANNER_TEMPLATE,
    ...banner,
    id: banner?.id && typeof banner.id === 'string' && banner.id.trim().length > 0
      ? banner.id
      : `hero_${timestamp}_${idx}`,
    active: banner?.active === false ? false : true
  }));
};

const HOMEPAGE_BANNER_TEMPLATE = {
  id: '',
  image: '',
  href: '',
  position: 'between_featured',
  active: true
};

const HOMEPAGE_BANNER_POSITIONS = [
  'top_of_home',
  'below_hero',
  'above_featured',
  'between_featured',
  'below_featured',
  'above_closing',
  'below_closing'
];

const DEFAULT_HOME_HERO_DESCRIPTION = 'Discover standout products, fresh arrivals, and practical finds curated for everyday life.';
const DEFAULT_SITE_TITLE = 'ItemSmith — Products Worth Sharing';
const DEFAULT_SITE_TAGLINE = 'Discover standout products, seasonal drops, and everyday essentials from ItemSmith.';
const DEFAULT_META_PIXEL_ID = '';
const SMS_HELP_DISMISSED_KEY = 'itemsmith_sms_help_dismissed_v1';
const SMS_STATUS_EVENTS = ['confirmed', 'shipped', 'delivered', 'cancelled'];
const SMS_TEMPLATE_TOKENS = ['{orderId}', '{invoiceUrl}', '{trackingUrl}', '{status}', '{siteName}'];
const SMS_ESTIMATE_INVOICE_TOKEN = 'WRfpNh4lWRj';
const DEFAULT_SMS_TEMPLATES = {
  confirmed: 'ItemSmith: আপনার অর্ডার {orderId} নিশ্চিত হয়েছে। দেখুন: {invoiceUrl}',
  shipped: 'ItemSmith: আপনার অর্ডার {orderId} পাঠানো হয়েছে। দেখুন: {invoiceUrl}',
  delivered: 'ItemSmith: আপনার অর্ডার {orderId} ডেলিভারি হয়েছে। ধন্যবাদ।',
  cancelled: 'ItemSmith: আপনার অর্ডার {orderId} বাতিল হয়েছে। প্রয়োজনে আমাদের সাথে যোগাযোগ করুন।'
};
const DEFAULT_SMS_NOTIFICATIONS = {
  enabled: false,
  dryRun: true,
  oneSegmentOnly: true,
  phoneFormat: 'e164',
  statuses: {
    confirmed: true,
    shipped: true,
    delivered: false,
    cancelled: true
  },
  templates: DEFAULT_SMS_TEMPLATES
};

const normalizeSmsNotifications = (value = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const statuses = source.statuses && typeof source.statuses === 'object' && !Array.isArray(source.statuses)
    ? source.statuses
    : {};
  const templates = source.templates && typeof source.templates === 'object' && !Array.isArray(source.templates)
    ? source.templates
    : {};
  return {
    enabled: source.enabled === true,
    dryRun: source.dryRun !== false,
    oneSegmentOnly: source.oneSegmentOnly !== false,
    phoneFormat: source.phoneFormat === 'local' ? 'local' : 'e164',
    statuses: Object.fromEntries(SMS_STATUS_EVENTS.map((status) => [
      status,
      Object.prototype.hasOwnProperty.call(statuses, status)
        ? statuses[status] !== false
        : DEFAULT_SMS_NOTIFICATIONS.statuses[status] === true
    ])),
    templates: Object.fromEntries(SMS_STATUS_EVENTS.map((status) => [
      status,
      Object.prototype.hasOwnProperty.call(templates, status) && typeof templates[status] === 'string'
        ? templates[status].slice(0, 500)
        : String(DEFAULT_SMS_TEMPLATES[status] || '').slice(0, 500)
    ]))
  };
};

const getSmsTemplateValue = (smsNotifications = {}, status = 'confirmed') => {
  const templates = smsNotifications?.templates && typeof smsNotifications.templates === 'object'
    ? smsNotifications.templates
    : {};
  return Object.prototype.hasOwnProperty.call(templates, status) && typeof templates[status] === 'string'
    ? templates[status]
    : (DEFAULT_SMS_TEMPLATES[status] || '');
};

const estimateSmsSegmentsForText = (message) => {
  const text = String(message || '');
  const unicode = Array.from(text).some((ch) => ch.charCodeAt(0) > 127);
  const length = text.length;
  if (length === 0) return { length: 0, encoding: unicode ? 'unicode' : 'gsm7', segments: 0 };
  if (unicode) return { length, encoding: 'unicode', segments: length <= 70 ? 1 : Math.ceil(length / 67) };
  return { length, encoding: 'gsm7', segments: length <= 160 ? 1 : Math.ceil(length / 153) };
};

const getSmsEstimateStorefrontBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://itemsmith.com';
  try {
    const url = new URL(window.location.href);
    const host = String(url.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${url.protocol}//${url.hostname}:3000`;
    }
    if (host.startsWith('dosh-hazar.')) {
      return `${url.protocol}//${url.hostname.replace(/^dosh-hazar\./, '')}`;
    }
    if (host.startsWith('admin.')) {
      return `${url.protocol}//${url.hostname.replace(/^admin\./, '')}`;
    }
    return `${url.protocol}//${url.host}`;
  } catch {
    return 'https://itemsmith.com';
  }
};

const stripSmsUrlProtocol = (url) => String(url || '').trim().replace(/^https?:\/\//i, '');

const getSmsTemplateEstimateValues = (settings = {}, status = 'confirmed') => {
  const prefix = String(settings.orderIdPrefix ?? 'IS');
  const suffix = String(settings.orderIdSuffix ?? '');
  const orderId = `${prefix}15291147${suffix}`;
  const baseUrl = getSmsEstimateStorefrontBaseUrl().replace(/\/+$/, '');
  const invoiceUrl = stripSmsUrlProtocol(`${baseUrl}/invoice/${SMS_ESTIMATE_INVOICE_TOKEN}`);
  return {
    orderId,
    invoiceUrl,
    trackingUrl: invoiceUrl,
    status,
    siteName: String(settings.siteName || 'ItemSmith').trim() || 'ItemSmith'
  };
};

const renderSmsTemplateEstimate = (template, status = 'confirmed', settings = {}) => {
  const values = getSmsTemplateEstimateValues(settings, status);
  return String(template || '')
    .replace(/\{orderId\}/g, values.orderId)
    .replace(/\{invoiceUrl\}/g, values.invoiceUrl)
    .replace(/\{trackingUrl\}/g, values.trackingUrl)
    .replace(/\{status\}/g, values.status)
    .replace(/\{siteName\}/g, values.siteName);
};

const estimateSmsTemplate = (template, status = 'confirmed', settings = {}) => (
  estimateSmsSegmentsForText(renderSmsTemplateEstimate(template, status, settings))
);

const createHomepageBanner = () => ({
  ...HOMEPAGE_BANNER_TEMPLATE,
  id: `home_banner_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`
});

const normalizeHomepageBanners = (banners) => {
  if (!Array.isArray(banners)) return [];
  const timestamp = Date.now();
  return banners.map((banner, idx) => {
    const rawPosition = String(banner?.position || '').trim();
    return {
      ...HOMEPAGE_BANNER_TEMPLATE,
      ...banner,
      id: banner?.id && typeof banner.id === 'string' && banner.id.trim().length > 0
        ? banner.id
        : `home_banner_${timestamp}_${idx}`,
      position: HOMEPAGE_BANNER_POSITIONS.includes(rawPosition) ? rawPosition : 'between_featured',
      active: banner?.active === false ? false : true
    };
  });
};

function normalizeCssColorToHex(input) {
  if (typeof input === 'undefined') return null;
  if (input === null) return null;
  const s0 = String(input).trim();
  if (!s0) return null;

  const hexMatch = s0.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3 || h.length === 4) {
      h = h.split('').map(ch => ch + ch).join('');
    }
    if (h.length === 8) {
      h = h.slice(0, 6);
    }
    if (h.length === 6) return `#${h.toLowerCase()}`;
    return null;
  }

  const fnMatch = s0.match(/^rgba?\((.*)\)$/i);
  if (!fnMatch) return null;
  let body = String(fnMatch[1] || '').trim();
  if (!body) return null;
  body = body.split('/')[0].trim();
  const parts = body.replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 3) return null;

  const parseChannel = (token) => {
    const t = String(token || '').trim();
    if (!t) return null;
    if (t.endsWith('%')) {
      const p = Number.parseFloat(t.slice(0, -1));
      if (!Number.isFinite(p)) return null;
      return Math.max(0, Math.min(255, Math.round((p / 100) * 255)));
    }
    const n = Number.parseFloat(t);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(255, Math.round(n)));
  };

  const r = parseChannel(parts[0]);
  const g = parseChannel(parts[1]);
  const b = parseChannel(parts[2]);
  if (r === null || g === null || b === null) return null;
  const toHex2 = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toLowerCase();
}

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function getMediaKind(src = '') {
  const s = String(src || '').toLowerCase();
  if (!s) return 'unknown';
  if (s.match(/\.(mp4|webm|mov|mkv)(\?.*)?$/)) return 'video';
  if (s.match(/\.(mp3|wav|m4a|aac|ogg)(\?.*)?$/)) return 'audio';
  if (s.match(/\.(glb|gltf|obj|stl|usdz)(\?.*)?$/)) return 'model';
  if (s.match(/\.(pdf|txt|csv|md|doc|docx|xls|xlsx|ppt|pptx)(\?.*)?$/)) return 'document';
  if (s.match(/\.(jpg|jpeg|png|gif|webp|avif|svg|ico)(\?.*)?$/)) return 'image';
  return 'unknown';
}

function isModelPreviewable(src = '') {
  const s = String(src || '').toLowerCase();
  return s.match(/\.(glb|gltf|usdz|stl)(\?.*)?$/);
}

// --- CONTEXT PROVIDERS ---
const AuthContext = createContext();
const ApiContext = createContext();
const ToastsContext = createContext();

export function AuthProvider({ children }) {
  const value = useProvideAuth();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function ApiProvider({ children }) {
  const auth = useAuth();
  const value = useProvideApi(auth);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function ToastsProvider({ children }) {
  const value = useProvideToasts();
  return <ToastsContext.Provider value={value}>{children}</ToastsContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
export function useApi() {
  return useContext(ApiContext);
}
export function useToasts() {
  return useContext(ToastsContext);
}

// --- PROVIDER HOOKS ---
const getStoredAdminToken = () => {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length !== 3) {
      localStorage.removeItem('admin_token');
      return '';
    }
    const payload = JSON.parse(atob(parts[1] || ''));
    if (payload?.exp && (payload.exp * 1000) <= Date.now()) {
      localStorage.removeItem('admin_token');
      return '';
    }
    return token;
  } catch {
    try { localStorage.removeItem('admin_token'); } catch {}
    return '';
  }
};

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(() => {
    try {
      return sessionStorage.getItem('admin_csrf') || '';
    } catch {
      return '';
    }
  });

  const persistCsrfToken = React.useCallback((value) => {
    const nextToken = String(value || '').trim();
    setCsrfToken(nextToken);
    try {
      if (nextToken) {
        sessionStorage.setItem('admin_csrf', nextToken);
      } else {
        sessionStorage.removeItem('admin_csrf');
      }
    } catch {}
    return nextToken;
  }, []);

  const refreshCsrfToken = React.useCallback(async () => {
    const res = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to refresh CSRF token');
    const data = await res.json().catch(() => ({}));
    return persistCsrfToken(data?.csrfToken || '');
  }, [persistCsrfToken]);

  useEffect(() => {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      try { setUser(JSON.parse(userData)); } catch {}
    }
    (async () => {
      try {
        await refreshCsrfToken().catch(() => '');
        const fallbackToken = getStoredAdminToken();
        const meRes = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
          headers: fallbackToken ? { Authorization: `Bearer ${fallbackToken}` } : {}
        });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData?.user) {
            setUser(meData.user);
            localStorage.setItem('admin_user', JSON.stringify(meData.user));
          }
        } else {
          localStorage.removeItem('admin_user');
          setUser(null);
        }
      } catch {}
      setLoading(false);
    })();
  }, [refreshCsrfToken]);

  const login = async (username, password, remember = false) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, remember })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }
    const data = await response.json();
    if (data?.token) {
      localStorage.setItem('admin_token', data.token);
    } else {
      try { localStorage.removeItem('admin_token'); } catch {}
    }
    localStorage.setItem('admin_user', JSON.stringify(data.user));
    setUser(data.user);
    if (data?.csrfToken) {
      persistCsrfToken(data.csrfToken);
    }
    return data;
  };

  const logout = () => {
    try { fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    persistCsrfToken('');
    setUser(null);
  };

  const getAuthHeaders = () => {
    const token = getStoredAdminToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    return headers;
  };

  const getCsrfToken = () => csrfToken;
  return { user, loading, login, logout, getAuthHeaders, getCsrfToken, refreshCsrfToken };
}

function useProvideApi(auth) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = React.useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const isFormData = options.body instanceof FormData;
      const method = (options.method || 'GET').toUpperCase();

      const executeRequest = async () => {
        const headers = {
          ...auth.getAuthHeaders(),
          ...options.headers,
        };
        if (method !== 'GET' && auth.getCsrfToken) {
          const csrf = auth.getCsrfToken();
          if (csrf) headers['X-CSRF-Token'] = csrf;
        }
        if (!isFormData && !headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
        return fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include'
        });
      };

      let response = await executeRequest();
      let errorData = null;
      let csrfRetried = false;

      while (!response.ok) {
        errorData = await response.json().catch(() => ({ error: 'Request failed' }));

        const canRetryCsrf =
          !csrfRetried &&
          method !== 'GET' &&
          response.status === 403 &&
          errorData?.error === 'CSRF token missing or invalid' &&
          typeof auth.refreshCsrfToken === 'function';

        if (canRetryCsrf) {
          const refreshedToken = await auth.refreshCsrfToken().catch(() => '');
          if (refreshedToken) {
            csrfRetried = true;
            response = await executeRequest();
            continue;
          }
        }

        if (response.status === 401) {
          try { auth.logout(); } catch {}
        }
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const downloadFile = React.useCallback(async (endpoint, filename, options = {}) => {
    try {
      const method = String(options?.method || 'GET').toUpperCase();
      const extraHeaders = options?.headers && typeof options.headers === 'object' ? options.headers : {};
      const isFormData = options?.body instanceof FormData;
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: {
          ...auth.getAuthHeaders(),
          ...extraHeaders,
          ...((!isFormData && method !== 'GET' && !extraHeaders['Content-Type']) ? { 'Content-Type': 'application/json' } : {})
        },
        body: options?.body,
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 401) {
          try { auth.logout(); } catch {}
        }
        throw new Error('Download failed');
      }
      const total = Number.parseInt(response.headers.get('content-length') || '0', 10);
      const reader = response.body?.getReader ? response.body.getReader() : null;
      let blob;
      if (!reader) {
        blob = await response.blob();
        if (typeof options?.onProgress === 'function') {
          options.onProgress(blob.size || 0, blob.size || total || 0);
        }
      } else {
        const chunks = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            received += value.length || value.byteLength || 0;
            if (typeof options?.onProgress === 'function') {
              options.onProgress(received, total || 0);
            }
          }
        }
        blob = new Blob(chunks);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }, [auth]);

  return React.useMemo(() => ({ request, downloadFile, loading, error }), [request, downloadFile, loading, error]);
}

function useProvideToasts() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    if (!toasts.length) return;
    const timer = setTimeout(() => setToasts(prev => prev.slice(1)), 4000);
    return () => clearTimeout(timer);
  }, [toasts]);
  const addToast = React.useCallback((message, type = 'info', options = {}) => {
    const toastOptions = options && typeof options === 'object' ? options : {};
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts(prev => [...prev, { id, message, type, ...toastOptions }]);
  }, []);
  const removeToast = React.useCallback((toastId) => {
    setToasts(prev => prev.filter((toast) => toast.id !== toastId));
  }, []);
  return React.useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast, removeToast]);
}

function VideoPlayer({ src, className = '', frameClassName = '' }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    if (mediaEl.paused) {
      mediaEl.play().catch(() => {});
    } else {
      mediaEl.pause();
    }
  };

  const toggleMute = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    mediaEl.muted = !mediaEl.muted;
    setMuted(mediaEl.muted);
  };

  const requestFullscreen = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    if (mediaEl.requestFullscreen) {
      mediaEl.requestFullscreen().catch(() => {});
      return;
    }
    if (mediaEl.webkitEnterFullscreen) {
      try { mediaEl.webkitEnterFullscreen(); } catch {}
    }
  };

  useEffect(() => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return undefined;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onLoaded = () => setMuted(mediaEl.muted);
    mediaEl.addEventListener('play', onPlay);
    mediaEl.addEventListener('pause', onPause);
    mediaEl.addEventListener('loadedmetadata', onLoaded);
    return () => {
      mediaEl.removeEventListener('play', onPlay);
      mediaEl.removeEventListener('pause', onPause);
      mediaEl.removeEventListener('loadedmetadata', onLoaded);
    };
  }, []);

  return (
    <div className={`w-full ${className}`}>
      <div className={`relative w-full overflow-hidden bg-black ${frameClassName || 'aspect-video'}`}>
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full object-contain"
          playsInline
          muted
          preload="metadata"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
        />

        {!playing && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="absolute inset-0 flex items-center justify-center text-white/90"
            aria-label="Play video"
          >
            <span className="h-14 w-14 rounded-full bg-black/55 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </span>
          </button>
        )}

        <div
          className="absolute right-3 bottom-3 z-20 flex items-center gap-2 rounded-full bg-black/55 px-3 py-2 text-white backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={togglePlay}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={playing ? 'Pause video' : 'Play video'}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            )}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={muted ? 'Unmute video' : 'Mute video'}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M16.5 12L19 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M19 9.5L16.5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M5 10v4h3l4 4V6L8 10H5z"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M5 10v4h3l4 4V6L8 10H5z"/><path d="M14.5 8.5a5 5 0 010 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/></svg>
            )}
          </button>
          <button
            type="button"
            onClick={requestFullscreen}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label="Fullscreen"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M9 3H3v6" />
              <path d="M15 3h6v6" />
              <path d="M9 21H3v-6" />
              <path d="M15 21h6v-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

}

function STLViewer({ src, interactive = true, className = '', immersive = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let disposed = false;
    let frame = 0;
    let resizeObserver = null;
    let controls = null;
    let renderer = null;
    let mesh = null;
    let camera = null;

    const boot = async () => {
      try {
        const [THREE, { STLLoader }, { OrbitControls }] = await Promise.all([
          import('three'),
          import('three/examples/jsm/loaders/STLLoader.js'),
          import('three/examples/jsm/controls/OrbitControls.js')
        ]);
        if (disposed || !containerRef.current) return;

        const activeContainer = containerRef.current;
        const cacheKey = `stl:${src}`;
        const scene = new THREE.Scene();
        scene.background = immersive ? null : new THREE.Color(0xf3f4f6);
        camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
        camera.position.set(0, 0, 4);

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        activeContainer.appendChild(renderer.domElement);

        if (interactive) {
          controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.08;
        }

        const ambient = new THREE.HemisphereLight(0xffffff, 0xaaaaaa, 1);
        scene.add(ambient);
        const dir = new THREE.DirectionalLight(0xffffff, 0.8);
        dir.position.set(5, 8, 6);
        scene.add(dir);

        const loader = new STLLoader();
        loader.load(
          src,
          (geometry) => {
            if (disposed) {
              geometry.dispose();
              return;
            }
            geometry.computeBoundingBox();
            geometry.computeVertexNormals();
            const material = new THREE.MeshStandardMaterial({ color: 0x9ca3af, metalness: 0.1, roughness: 0.6 });
            mesh = new THREE.Mesh(geometry, material);
            scene.add(mesh);

            const box = geometry.boundingBox;
            if (box) {
              const size = new THREE.Vector3();
              box.getSize(size);
              const center = new THREE.Vector3();
              box.getCenter(center);
              mesh.position.sub(center);
              const maxDim = Math.max(size.x, size.y, size.z) || 1;
              camera.position.set(0, 0, maxDim * 2.2);
              camera.near = maxDim / 100;
              camera.far = maxDim * 20;
              camera.updateProjectionMatrix();
            }

            const cached = modelViewStateCache.get(cacheKey);
            if (cached && cached.position && cached.target && controls) {
              camera.position.copy(cached.position);
              controls.target.copy(cached.target);
              controls.update();
            }
          },
          undefined,
          () => {}
        );

        const resize = () => {
          const targetContainer = containerRef.current;
          if (!targetContainer || !renderer || !camera) return;
          const { clientWidth, clientHeight } = targetContainer;
          const width = clientWidth || 1;
          const height = clientHeight || 1;
          renderer.setSize(width, height, false);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        };
        resize();
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(activeContainer);

        const animate = () => {
          frame = requestAnimationFrame(animate);
          if (controls) controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch {
        // Keep layout stable if viewer libraries fail to load.
      }
    };

    boot();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      if (resizeObserver) resizeObserver.disconnect();
      if (controls && camera) {
        modelViewStateCache.set(`stl:${src}`, {
          position: camera.position.clone(),
          target: controls.target.clone()
        });
      }
      if (controls) controls.dispose();
      if (renderer) renderer.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach(m => m.dispose());
        else mesh.material.dispose();
      }
      if (renderer?.domElement?.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src, interactive, immersive]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-[60vh] ${immersive ? 'bg-black rounded-none' : 'bg-gray-50 rounded-lg'} overflow-hidden ${className}`}
    />
  );
}

function ModelViewer({ src, interactive = false }) {
  const viewerRef = useRef(null);
  const [viewerReady, setViewerReady] = useState(() => {
    if (typeof window === 'undefined' || !window.customElements) return false;
    return !!window.customElements.get('model-viewer');
  });

  useEffect(() => {
    let cancelled = false;
    ensureModelViewerElementLoaded()
      .then(() => {
        if (!cancelled) setViewerReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!viewerReady) return undefined;
    const el = viewerRef.current;
    if (!el || !src) return undefined;
    const cacheKey = `model:${src}`;
    const cached = modelViewStateCache.get(cacheKey);
    if (cached) {
      if (cached.orbit) el.cameraOrbit = cached.orbit;
      if (cached.target) el.cameraTarget = cached.target;
      if (cached.fov) el.fieldOfView = cached.fov;
    }
    const handleCameraChange = () => {
      if (!interactive) return;
      try {
        modelViewStateCache.set(cacheKey, {
          orbit: el.cameraOrbit,
          target: el.cameraTarget,
          fov: el.fieldOfView
        });
      } catch {}
    };
    if (interactive) el.addEventListener('camera-change', handleCameraChange);
    return () => {
      if (interactive) {
        handleCameraChange();
        el.removeEventListener('camera-change', handleCameraChange);
      }
    };
  }, [src, interactive, viewerReady]);

  if (!viewerReady) {
    return (
      <div className="w-full h-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center">
        Loading 3D preview...
      </div>
    );
  }

  return (
    <model-viewer
      ref={viewerRef}
      src={src}
      camera-controls={interactive ? true : undefined}
      ar
      interaction-prompt="none"
      style={{ width: '100%', height: '100%', pointerEvents: interactive ? 'auto' : 'none' }}
    />
  );
}

function MediaPreviewModal({ item, onClose }) {
  if (!item) return null;
  const src = resolveMediaUrl(item.url || item.src || item);
  const kind = getMediaKind(src);
  const isStl = String(src || '').toLowerCase().match(/\.(stl)(\?.*)?$/);
  const modalContent = (
    <div className="fixed inset-0 bg-black/90 z-[300]" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70"
        aria-label="Close preview"
      >
        <Icon.X className="w-5 h-5" />
      </button>

      <div className="w-full h-full flex items-center justify-center p-2 sm:p-6 pointer-events-none">
        {kind === 'image' && (
          <img
            src={src}
            alt="Preview"
            className="max-w-full max-h-[92vh] object-contain pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {kind === 'video' && (
          <div className="w-full max-w-[1400px] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <VideoPlayer src={src} className="w-full h-full" frameClassName="w-full h-[80vh] sm:h-[86vh] max-h-[92vh]" />
          </div>
        )}
        {kind === 'audio' && (
          <div className="w-full max-w-2xl px-4 py-5 rounded-lg bg-black/35 border border-white/20 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <audio src={src} controls className="w-full" />
          </div>
        )}
        {kind === 'model' && (
          isStl ? (
            <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <STLViewer src={src} interactive className="h-full" immersive />
            </div>
          ) : isModelPreviewable(src) ? (
            <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] bg-black overflow-hidden pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <ModelViewer src={src} interactive />
            </div>
          ) : (
            <div className="text-sm text-white/90 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              Preview is available for .glb, .gltf, .usdz, or .stl models. For textured models, use .glb (embedded textures).
            </div>
          )
        )}
        {kind === 'document' && (
          String(src).toLowerCase().includes('.pdf') ? (
            <iframe
              src={src}
              title="PDF Preview"
              className="w-full max-w-[1400px] h-[85vh] border border-white/20 bg-white pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="text-sm text-white space-y-3 bg-black/35 border border-white/20 rounded-lg px-4 py-4 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div>Preview is limited for this document type.</div>
              <a href={src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded border border-white/40 hover:bg-white/10">
                Open document
              </a>
            </div>
          )
        )}
        {kind === 'unknown' && (
          <div className="text-sm text-white pointer-events-auto" onClick={(e) => e.stopPropagation()}>Unsupported media type.</div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
}

// Reusable Media Picker for image fields
function MediaPicker({ label = 'Image', value, onChange, helpText, accept = '*/*', disabled = false, ratioHint }) {
  const { request } = useApi();
  const auth = useAuth();
  const { addToast } = useToasts();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('library'); // 'library' | 'upload' | 'url'
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || '');
  const [selected, setSelected] = useState(value || '');
  const panelId = React.useId();
  const [currentPath, setCurrentPath] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const pickerOffsetRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchTerm, 220);
  const PICKER_PAGE_SIZE = 72;
  const getKind = (src) => getMediaKind(src);

  const loadMedia = useCallback(async ({ reset = false } = {}) => {
    if (!open || tab !== 'library') return;
    const nextOffset = reset ? 0 : pickerOffsetRef.current;
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      const params = new URLSearchParams();
      params.set('includeMeta', '1');
      params.set('limit', String(PICKER_PAGE_SIZE));
      params.set('offset', String(nextOffset));
      if (currentPath) params.set('path', currentPath);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (typeFilter) params.set('type', typeFilter);

      const data = await request(`/media?${params.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
      const nextHasMore = typeof data?.hasMore === 'boolean'
        ? data.hasMore
        : (items.length >= PICKER_PAGE_SIZE);

      pickerOffsetRef.current = nextOffset + items.length;
      setFiles((prev) => reset ? items : [...prev, ...items]);
      setTotalItems(total);
      setHasMore(nextHasMore);
    } catch {
      addToast('Failed to load media', 'error');
      if (reset) {
        setFiles([]);
        setTotalItems(0);
        setHasMore(false);
      }
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, [open, tab, request, addToast, currentPath, debouncedSearch, typeFilter]);

  // Load media files when opened and tab is library
  useEffect(() => {
    if (!open || tab !== 'library') return;
    pickerOffsetRef.current = 0;
    loadMedia({ reset: true });
  }, [open, tab, currentPath, debouncedSearch, typeFilter, loadMedia]);

  useEffect(() => { setUrlInput(value || ''); setSelected(value || ''); }, [value]);

  const handleUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append('files', f));
      const xhr = new XMLHttpRequest();
      await new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error('Upload failed'));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', '/shopapi/media');
        xhr.withCredentials = true;
        try { const headers = auth.getAuthHeaders(); Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v)); } catch {}
        xhr.send(fd);
      });
      let uploaded = null;
      try { const payload = JSON.parse(xhr.responseText || '{}'); uploaded = payload?.files?.[0]?.url || null; } catch {}
      if (uploaded) {
        setSelected(resolveMediaUrl(uploaded));
        addToast('File uploaded', 'success');
        setTab('library');
        // Refresh library
        try {
          pickerOffsetRef.current = 0;
          await loadMedia({ reset: true });
        } catch {}
      } else {
        addToast('Upload completed but URL not returned', 'error');
      }
    } catch {
      addToast('Failed to upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const applySelection = () => {
    let finalUrl = selected || urlInput || '';
    if (finalUrl) {
      // strip surrounding quotes
      finalUrl = finalUrl.replace(/^['"]|['"]$/g, '').trim();
    }
    onChange && onChange(finalUrl);
    setOpen(false);
  };

  const preview = (src) => {
    const resolvedSrc = resolveMediaUrl(src);
    if (!src) {
      return (
        <div className="h-10 w-10 rounded border bg-gray-50 flex items-center justify-center text-xs text-gray-400">No media</div>
      );
    }
    const kind = getKind(resolvedSrc);
    if (kind === 'video') {
      return (
        <video
          src={resolvedSrc}
          className="h-10 w-10 rounded object-cover border bg-gray-50"
          muted
          playsInline
          preload="metadata"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
        />
      );
    }
    if (kind === 'model') {
      return (
        <div className="h-10 w-10 rounded border bg-gray-50 flex items-center justify-center text-[10px] text-gray-500">3D</div>
      );
    }
    if (kind === 'audio') {
      return (
        <div className="h-10 w-10 rounded border bg-purple-50 text-purple-700 text-[10px] font-semibold flex items-center justify-center">AUDIO</div>
      );
    }
    if (kind === 'document') {
      return (
        <div className="h-10 w-10 rounded border bg-blue-50 text-blue-700 text-[10px] font-semibold flex items-center justify-center">DOC</div>
      );
    }
    return (
      <img src={resolvedSrc} alt="preview" className="h-10 w-10 rounded object-cover border" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
    );
  };

  const filteredFiles = files || [];
  const aspectHintText = (typeof ratioHint === 'string' && ratioHint.trim())
    ? ratioHint.trim()
    : 'Flexible';

  return (
    <div className="space-y-1">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className="flex items-center gap-3">
        {preview(value)}
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder="/uploads/... or https://..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={disabled}
        />
        <button type="button" onClick={() => setOpen(o => !o)} className="px-3 py-2 border rounded-md text-sm">
          {open ? 'Close' : 'Select media'}
        </button>
        <span className="inline-flex items-center whitespace-nowrap rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600">
          Ratio: {aspectHintText}
        </span>
        {value && (
          <button type="button" onClick={() => onChange && onChange('')} className="px-2 py-2 border rounded-md text-sm text-red-600">
            Clear
          </button>
        )}
      </div>
      {(helpText || ratioHint) && (
        <p className="text-xs text-gray-500">
          {helpText || ''}
          {helpText && ratioHint ? ' • ' : ''}
          {ratioHint || ''}
        </p>
      )}

      {open && (
        <div id={panelId} className="mt-2 border rounded-lg p-3 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button type="button" onClick={() => setTab('library')} className={`px-3 py-1.5 rounded ${tab==='library'?'bg-gray-900 text-white':'border'}`}>Library</button>
            <button type="button" onClick={() => setTab('upload')} className={`px-3 py-1.5 rounded ${tab==='upload'?'bg-gray-900 text-white':'border'}`}>Upload</button>
            <button type="button" onClick={() => setTab('url')} className={`px-3 py-1.5 rounded ${tab==='url'?'bg-gray-900 text-white':'border'}`}>URL</button>
            <div className="ml-auto text-xs text-gray-500">{selected ? 'Selected ready' : 'Choose a source'}</div>
          </div>
          {ratioHint && <div className="mb-2 text-xs text-gray-500">Display ratio: {ratioHint}</div>}

          {tab === 'library' && (
            <div className="max-h-64 overflow-auto">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2 text-xs text-gray-600">
                <div className="break-all">Path: {currentPath || '/'}</div>
                <button type="button" className="px-2 py-1 border rounded disabled:opacity-50" onClick={() => setCurrentPath(prev => prev.split('/').slice(0, -1).join('/'))} disabled={!currentPath}>Up</button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Search media..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-[160px] px-2 py-1 border rounded text-xs"
                />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-2 py-1 border rounded text-xs"
                >
                  <option value="all">All</option>
                  <option value="image">Images</option>
                  <option value="video">Videos</option>
                  <option value="model">3D Models</option>
                  <option value="folders">Folders</option>
                </select>
              </div>
              {loading ? (
                <div className="text-sm text-gray-500 p-3">Loading media…</div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-sm text-gray-500 p-3">No media yet. Upload a file.</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[11px] text-gray-500">
                    Showing {filteredFiles.length} of {totalItems}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {filteredFiles.map((f) => {
                      const fileUrl = resolveMediaUrl(f.url);
                      return (
                      <div key={f.path || f.name} className={`relative border rounded overflow-hidden ${selected===fileUrl?'ring-2 ring-blue-500':''}`} title={f.name}>
                        {f.type === 'dir' ? (
                          <button type="button" onClick={() => setCurrentPath(f.path)} className="block w-full text-left h-20 flex items-center justify-center bg-gray-50">
                            <Icon.Folder className="w-6 h-6 text-gray-500" />
                            <div className="text-[10px] p-1 truncate w-full text-center">{f.name}</div>
                          </button>
                        ) : (
                          <>
                            <button type="button" onClick={() => setSelected(fileUrl)} className="block w-full text-left">
                              {(() => {
                                const kind = getKind(fileUrl);
                                if (kind === 'video') {
                                  return (
                                    <div className="w-full h-20 bg-gray-900/5 flex items-center justify-center text-[10px] text-gray-600">
                                      VIDEO
                                    </div>
                                  );
                                }
                                if (kind === 'model') {
                                  return <div className="w-full h-20 bg-gray-900/5 flex items-center justify-center text-[10px] text-gray-600">3D Model</div>;
                                }
                                if (kind === 'audio') {
                                  return <div className="w-full h-20 bg-purple-50/70 flex items-center justify-center text-[10px] text-purple-700">AUDIO</div>;
                                }
                                if (kind === 'document') {
                                  return <div className="w-full h-20 bg-blue-50/70 flex items-center justify-center text-[10px] text-blue-700">DOC</div>;
                                }
                                return (
                                  <img
                                    src={fileUrl}
                                    alt={f.name}
                                    className="w-full h-20 object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e)=>{e.currentTarget.style.display='none';}}
                                  />
                                );
                              })()}
                              <div className="text-[10px] p-1 truncate">{f.name}</div>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPreviewItem(f); }}
                              aria-label="Preview"
                              className="absolute top-1 right-1 inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-gray-700 border shadow"
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 5c5 0 9 5 9 7s-4 7-9 7-9-5-9-7 4-7 9-7Zm0 2c-3.6 0-6.7 3.2-6.9 5 .3 1.8 3.3 5 6.9 5s6.6-3.2 6.9-5c-.3-1.8-3.3-5-6.9-5Zm0 2.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z"/></svg>
                            </button>
                          </>
                        )}
                      </div>
                    )})}
                  </div>
                  {hasMore && (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => loadMedia({ reset: false })}
                        disabled={loadingMore}
                        className="px-3 py-1.5 border rounded text-xs disabled:opacity-50"
                      >
                        {loadingMore ? 'Loading more…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === 'upload' && (
            <div className="space-y-2">
              <input type="file" accept={accept} multiple onChange={(e) => handleUpload(e.target.files)} disabled={uploading} />
              {uploading && <div className="text-xs text-gray-500">Uploading…</div>}
            </div>
          )}

          {tab === 'url' && (
            <div className="space-y-2">
              <input type="text" value={urlInput} onChange={(e)=>setUrlInput(e.target.value)} placeholder="https://example.com/file.ext" className="w-full px-3 py-2 border rounded" />
              <div className="text-xs text-gray-500">Paste any media URL (https recommended)</div>
              <div className="mt-1">{preview(urlInput)}</div>
              <div className="text-right">
                <button type="button" onClick={()=>setSelected(urlInput)} className="px-3 py-1.5 border rounded">Use this URL</button>
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Current:</span>
              <code className="px-1.5 py-0.5 bg-gray-50 border rounded">{(selected||'').slice(0,64) || 'None'}</code>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 border rounded">Cancel</button>
              <button type="button" onClick={applySelection} disabled={!selected && !urlInput} className="px-3 py-1.5 bg-blue-600 text-white rounded disabled:bg-gray-400">Apply</button>
            </div>
          </div>
        </div>
      )}
      {previewItem && (
        <MediaPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
      )}
    </div>
  );
}

const currency = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '৳0';
  try {
    return `৳${num.toLocaleString()}`;
  } catch {
    return `৳${num}`;
  }
};

function AdminPriceDisplay({ price, originalPrice, showFrom = false, compact = false, align = 'left' }) {
  const current = Math.max(0, Number(price || 0) || 0);
  const original = Math.max(0, Number(originalPrice || 0) || 0);
  const hasDiscount = original > current;
  const alignment = align === 'right' ? 'items-end text-right' : 'items-start text-left';
  return (
    <div className={`flex flex-col justify-end ${alignment}`}>
      <span className={`${compact ? 'h-3 text-[10px]' : 'h-4 text-xs'} text-gray-400 line-through leading-none`}>
        {hasDiscount ? (showFrom ? `From ${currency(original)}` : currency(original)) : ''}
      </span>
      <span className={`${compact ? 'text-sm leading-5' : 'text-base leading-6'} font-semibold text-gray-900`}>
        {showFrom ? `From ${currency(current)}` : currency(current)}
      </span>
    </div>
  );
}

function getAdminOrderOptionLabel(item, productLookup) {
  const explicitLabel = String(item?.variantLabel || '').trim();
  if (explicitLabel) return explicitLabel;
  const productId = String(item?.productId || item?.id || '').trim();
  const product = productLookup?.get(productId);
  if (product && (Array.isArray(product.variants) ? product.variants.length > 0 : Number(product._variantCount || 0) > 0)) {
    return 'Base model';
  }
  return '';
}

const REQUIRED_PAYMENT_OVERRIDE_LABEL = '__required_payment_override__';
 
 
 // Open invoice in a new window for a given order ID
 function openInvoice(orderId) {
   try {
     const w = window.open('', '_blank');
      if (!w) { alert('Pop-up blocked. Allow pop-ups for this site.'); return; }
      w.document.write('<!doctype html><html><head><title>Invoice</title></head><body>Loading invoice...</body></html>');
      const token = getStoredAdminToken();
      fetch(`/shopapi/orders/${encodeURIComponent(orderId)}/invoice-link`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      })
        .then(async (r) => {
          if (!r.ok) throw new Error('Unable to generate invoice link');
          return r.json();
        })
        .then((data) => {
          const target = String(data?.url || '').trim();
          if (!/^https?:\/\//i.test(target)) throw new Error('Invoice link missing');
          w.location.replace(target);
          w.focus();
        })
        .catch(() => { w.document.body.innerText = 'Failed to load invoice.'; });
    } catch {}
  }

 // Open shipping paper (admin-only) in a new window for a given order ID
 function openShippingPaper(orderId) {
   try {
     const w = window.open('', '_blank');
     if (!w) { alert('Pop-up blocked. Allow pop-ups for this site.'); return; }
     w.document.write('<!doctype html><html><head><title>Shipping Slip</title></head><body>Loading shipping slip...</body></html>');
     const token = getStoredAdminToken();
     fetch(`/shopapi/orders/${encodeURIComponent(orderId)}/shipping-paper`, {
       headers: token ? { Authorization: `Bearer ${token}` } : {},
       credentials: 'include'
     })
       .then(async (r) => {
         if (!r.ok) throw new Error('Unable to generate shipping slip');
         return r.text();
       })
       .then((html) => {
         w.document.open();
         w.document.write(html);
         w.document.close();
         w.focus();
       })
       .catch(() => { w.document.body.innerText = 'Failed to load shipping slip.'; });
   } catch {}
 }
 
 function LoginForm() {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '', remember: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(credentials.username, credentials.password, credentials.remember);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Sign in to access the admin panel</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              required
              value={credentials.username}
              onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              required
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={credentials.remember}
              onChange={(e) => setCredentials(prev => ({ ...prev, remember: e.target.checked }))}
              disabled={loading}
            />
            Remember this device
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Icon.Loading className="w-4 h-4" />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}


function Dashboard({ onNavigate = () => {} }) {
  const [analytics, setAnalytics] = useState(null);
  const [observability, setObservability] = useState(null);
  const [observabilityError, setObservabilityError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { request } = useApi();
  const { addToast } = useToasts();

  // Stock alerts merged into dashboard
  //const [stockAlerts, setStockAlerts] = useState([]);
  //const [stockAlertsLoading, setStockAlertsLoading] = useState(true);

  // Current discounts/promotions
  const [activePromotions, setActivePromotions] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);

  const loadObservability = React.useCallback(async () => {
    try {
      const observabilityData = await request('/observability');
      setObservability(observabilityData);
      setObservabilityError(null);
    } catch (err) {
      console.warn('Failed to load observability metrics:', err);
      setObservabilityError('Failed to load observability data');
    }
  }, [request]);

  const loadAnalytics = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsData = await request('/analytics');
      setAnalytics(analyticsData);
      await loadObservability();
    } catch (err) {
      setError('Failed to load analytics');
      addToast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, loadObservability]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadObservability();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadObservability]);

  const loadPromotions = React.useCallback(async () => {
    try {
      setPromosLoading(true);
      const list = await request('/deals');
      const now = new Date();
      const actives = (Array.isArray(list) ? list : []).filter(p => {
        if (p.active === false) return false;
        const starts = p.start_date ? new Date(p.start_date) : null;
        const ends = p.end_date ? new Date(p.end_date) : null;
        if (starts && starts > now) return false;
        if (ends && ends < now) return false;
        return true;
      });
      setActivePromotions(actives.slice(0, 5));
    } catch (e) {
      // ignore on dashboard
    } finally {
      setPromosLoading(false);
    }
  }, [request]);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  const formatBytes = (value) => {
    if (!Number.isFinite(value) || value < 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let bytes = value;
    let unitIndex = 0;
    while (bytes >= 1024 && unitIndex < units.length - 1) {
      bytes /= 1024;
      unitIndex += 1;
    }
    const formatted = bytes >= 100 ? bytes.toFixed(0) : bytes >= 10 ? bytes.toFixed(1) : bytes.toFixed(2);
    return `${formatted} ${units[unitIndex]}`;
  };

  const formatPercent = (value) => {
    if (!Number.isFinite(value)) return '—';
    return `${value.toFixed(1)}%`;
  };

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds)) return '—';
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${Math.floor(seconds % 60)}s`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const ObservabilityStat = ({ title, primary, secondary, caption, icon }) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
          <p className="mt-2 text-xl font-semibold text-gray-900">{primary}</p>
          {secondary && <p className="text-xs text-gray-500 mt-1">{secondary}</p>}
          {caption && <p className="text-xs text-gray-400 mt-1">{caption}</p>}
        </div>
        {icon}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        {error}
        <button onClick={loadAnalytics} className="ml-4 px-3 py-1 bg-blue-600 text-white rounded">Retry</button>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-12 text-gray-500">No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your store performance</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{currency(analytics.totalRevenue)}</p>
              <p className="text-xs text-gray-500">Average: {currency(analytics.averageOrderValue)}/order</p>
            </div>
            <Icon.Tag className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">This Month Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{currency(analytics.thisMonthRevenue)}</p>
              <p className="text-xs text-gray-500">Orders: {analytics.thisMonthOrders}</p>
            </div>
            <Icon.Tag className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalOrders}</p>
              <p className="text-xs text-gray-500">Pending: {analytics.pendingOrders}</p>
            </div>
            <Icon.ShoppingCart className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('products')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalProducts}</p>
              <p className="text-xs text-gray-500">Low stock: {analytics.lowStock}</p>
            </div>
            <Icon.Package className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','pending'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100">
            <p className="text-2xl font-bold text-yellow-700">{analytics.pendingOrders}</p>
            <p className="text-xs text-yellow-600 mt-1">Pending</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','confirmed'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100">
            <p className="text-2xl font-bold text-blue-700">{analytics.confirmedOrders}</p>
            <p className="text-xs text-blue-600 mt-1">Confirmed</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','processing'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100">
            <p className="text-2xl font-bold text-purple-700">{analytics.processingOrders}</p>
            <p className="text-xs text-purple-600 mt-1">Processing</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','shipped'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100">
            <p className="text-2xl font-bold text-indigo-700">{analytics.shippedOrders}</p>
            <p className="text-xs text-indigo-600 mt-1">Shipped</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','delivered'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-green-50 rounded-lg hover:bg-green-100">
            <p className="text-2xl font-bold text-green-700">{analytics.deliveredOrders}</p>
            <p className="text-xs text-green-600 mt-1">Delivered</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','cancelled'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-red-50 rounded-lg hover:bg-red-100">
            <p className="text-2xl font-bold text-red-700">{analytics.cancelledOrders}</p>
            <p className="text-xs text-red-600 mt-1">Cancelled</p>
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {analytics.lowStock > 0 && (
        <div onClick={() => onNavigate('products')} className="cursor-pointer bg-orange-50 border-l-4 border-orange-400 p-4 rounded hover:bg-orange-100">
          <div className="flex items-center">
            <Icon.Alert className="w-5 h-5 text-orange-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-orange-800">
                {analytics.lowStock} product{analytics.lowStock !== 1 ? 's' : ''} running low on stock
              </p>
              <p className="text-xs text-orange-700 mt-1">Check the Products page to restock items with less than 5 units.</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
          <button type="button" onClick={() => onNavigate('orders')} className="text-sm text-blue-600 underline">View all</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.recentOrders && analytics.recentOrders.length > 0 ? (
                analytics.recentOrders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.display_order_id || order.id.slice(0, 8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.customer?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {currency(order.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'processing' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'confirmed' ? 'bg-indigo-100 text-indigo-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Old stats preserved below for reference */}
      <div style={{ display: 'none' }} className="bg-white rounded-lg shadow-sm p-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.lowStock}</p>
            </div>
            <Icon.Package className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Server Health</h2>
            <p className="text-sm text-gray-600">Live snapshot of VPS performance and reliability</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {observability ? `Last update: ${new Date(observability.timestamp).toLocaleTimeString()}` : ''}
            <button type="button" onClick={() => onNavigate('settings')} className="text-blue-600 underline">Settings</button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {observabilityError && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-700 flex items-start justify-between gap-3">
              <span>{observabilityError}</span>
              <button
                type="button"
                onClick={loadAnalytics}
                className="rounded-full border border-yellow-400 px-3 py-1 text-xs font-semibold hover:bg-yellow-100"
              >
                Retry
              </button>
            </div>
          )}

          {observability && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ObservabilityStat
                  title="CPU load"
                  primary={formatPercent(
                    Number.isFinite(observability.cpu?.systemPercent)
                      ? observability.cpu.systemPercent
                      : ((observability.cpu?.load1 ?? 0) / Math.max(1, observability.cpu?.cores || 1) * 100)
                  )}
                  secondary={`Avg (5m): ${observability.cpu?.load5?.toFixed(2) ?? '—'}`}
                  caption={`Process: ${formatPercent(observability.cpu?.processPercent)}`}
                  icon={<Icon.Settings className="w-5 h-5 text-blue-600" />}
                />
                <ObservabilityStat
                  title="Memory usage"
                  primary={formatPercent(observability.memory?.usedPercent)}
                  secondary={`${formatBytes(observability.memory?.used)} / ${formatBytes(observability.memory?.total)}`}
                  caption={`Process RSS: ${formatBytes(observability.memory?.processRss)}`}
                  icon={<Icon.Package className="w-5 h-5 text-purple-600" />}
                />
                <ObservabilityStat
                  title="Disk usage"
                  primary={formatPercent(observability.disk?.usedPercent)}
                  secondary={`${formatBytes(observability.disk?.used)} / ${formatBytes(observability.disk?.total)}`}
                  caption={observability.disk?.mount ? `Mount: ${observability.disk.mount}` : ''}
                  icon={<Icon.Download className="w-5 h-5 text-amber-600" />}
                />
                <ObservabilityStat
                  title="Network"
                  primary={`${formatBytes(observability.network?.inboundRate)} /s`}
                  secondary={`Outbound: ${formatBytes(observability.network?.outboundRate)} /s`}
                  caption={observability.network?.interfaceCount ? `${observability.network.interfaceCount} interfaces monitored` : ''}
                  icon={<Icon.Tag className="w-5 h-5 text-green-600" />}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  <div className="font-semibold text-gray-900 mb-2">Uptime</div>
                  <p className="text-sm">Process: {formatDuration(observability.uptime?.processSeconds)}</p>
                  <p className="text-sm">System: {formatDuration(observability.uptime?.systemSeconds)}</p>
                  {observability.temperature && (
                    <p className="mt-2 text-sm">
                      Temperature: {observability.temperature.celsius.toFixed(1)}°C
                    </p>
                  )}
                </div>
                {observability.alerts?.length ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <div className="font-semibold text-red-800 mb-2">Active alerts</div>
                    <ul className="space-y-1">
                      {observability.alerts.map((alert, idx) => (
                        <li key={idx}>• {alert.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                    <div className="font-semibold text-emerald-800 mb-2">All clear</div>
                    <p>No health alerts at the moment.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {!observability && !observabilityError && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              Observability data is not yet available. Please try again shortly.
            </div>
          )}
        </div>
      </div>


      {/* Current Discounts */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Current Discounts</h2>
            <p className="text-sm text-gray-600">Live promotions and discount codes</p>
          </div>
          <button type="button" onClick={() => onNavigate('campaigns')} className="text-sm text-blue-600 underline">Manage</button>
        </div>
        {promosLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading promotions...</div>
        ) : activePromotions.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No active discounts right now</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ends</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activePromotions.map(promo => (
                  <tr key={promo.id}>
                    <td className="px-6 py-3 text-sm text-gray-900">{promo.title}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.type}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.type === 'percentage' ? `${promo.value}%` : `${promo.value}?`}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.code || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{promo.end_date ? new Date(promo.end_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const hasLoadedRef = useRef(false);
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();

  const loadProducts = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        if (hasLoadedRef.current) setRefreshing(true);
        else setLoading(true);
      }
      const data = await request('/products');
      const rows = Array.isArray(data) ? data : [];
      const normalized = rows.map((product) => ({
        ...summarizeProductForAdmin(product),
        _gallery: parseStringJsonArray(product?.images)
      }));
      setProducts(normalized);
      hasLoadedRef.current = true;
    } catch (err) {
      if (!silent) addToast('Failed to load products', 'error');
    } finally {
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [request, addToast]);

  useEffect(() => {
    loadProducts();
    const interval = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        loadProducts({ silent: true });
      }
    }, 90000);
    return () => clearInterval(interval);
  }, [loadProducts]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (products || []).forEach((product) => {
      if (product._category) set.add(product._category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const productStats = useMemo(() => {
    const total = products.length;
    const active = products.filter((product) => product._isActive).length;
    const lowStock = products.filter((product) => {
      const stock = Number(product._displayStock || 0);
      return stock > 0 && stock <= 5;
    }).length;
    const outOfStock = products.filter((product) => Number(product._displayStock || 0) <= 0).length;
    return { total, active, lowStock, outOfStock };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();
    let list = products;
    if (query) {
      list = list.filter((product) => String(product._searchHaystack || '').includes(query));
    }
    if (statusFilter !== 'all') {
      list = list.filter((product) => (statusFilter === 'active' ? product._isActive : !product._isActive));
    }
    if (categoryFilter !== 'all') {
      list = list.filter((product) => product._category === categoryFilter);
    }
    if (stockFilter !== 'all') {
      list = list.filter((product) => {
        const stock = Number(product._displayStock || 0);
        if (stockFilter === 'in') return stock > 5;
        if (stockFilter === 'low') return stock > 0 && stock <= 5;
        if (stockFilter === 'out') return stock <= 0;
        return true;
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortBy === 'title_asc') return a._title.localeCompare(b._title);
      if (sortBy === 'price_high') return Number(b._displayPrice || 0) - Number(a._displayPrice || 0);
      if (sortBy === 'price_low') return Number(a._displayPrice || 0) - Number(b._displayPrice || 0);
      if (sortBy === 'stock_high') return Number(b._displayStock || 0) - Number(a._displayStock || 0);
      if (sortBy === 'stock_low') return Number(a._displayStock || 0) - Number(b._displayStock || 0);
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return dateB - dateA;
    });
    return sorted;
  }, [products, debouncedSearchTerm, statusFilter, stockFilter, categoryFilter, sortBy]);

  const isInteractiveProductRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-product-row-ignore="true"]'));
  }, []);

  const openProductEditor = useCallback((product) => {
    setEditingProduct(product);
    setShowForm(true);
  }, []);

  const openProductFromRow = useCallback((event, product) => {
    if (isInteractiveProductRowTarget(event.target)) return;
    openProductEditor(product);
  }, [isInteractiveProductRowTarget, openProductEditor]);

  const handleProductRowKeyDown = useCallback((event, product) => {
    if (isInteractiveProductRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openProductEditor(product);
    }
  }, [isInteractiveProductRowTarget, openProductEditor]);

  const handleDelete = async (productId) => {
    const shouldDelete = window.confirm('Are you sure you want to delete this product?');
    if (!shouldDelete) return;

    try {
      await request(`/products/${productId}`, { method: 'DELETE' });
      setProducts((prev) => prev.filter((product) => product.id !== productId));
      addToast('Product deleted successfully', 'success');
    } catch (err) {
      addToast('Failed to delete product', 'error');
    }
  };

  const toggleProductStatus = async (product) => {
    const nextActive = !product._isActive;
    try {
      const formData = new FormData();
      formData.append('active', String(nextActive));
      
      await request(`/products/${product.id}`, {
        method: 'PUT',
        body: formData
      });
      setProducts((prev) => prev.map((item) => (
        item.id === product.id ? { ...item, _isActive: nextActive, active: nextActive } : item
      )));
      addToast(`Product ${nextActive ? 'activated' : 'deactivated'}`, 'success');
    } catch (err) {
      addToast('Failed to update product status', 'error');
      loadProducts({ silent: true });
    }
  };

  const handleExport = async () => {
    try {
      await downloadFile('/products.csv', `products_${new Date().toISOString().split('T')[0]}.csv`);
      addToast('Products exported successfully', 'success');
    } catch (err) {
      addToast('Failed to export products', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-1">Catalog control with fast filters and one-click editing.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => loadProducts()}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
          >
            {refreshing && <Icon.Loading className="w-4 h-4" />}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Icon.Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{productStats.total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Active</div>
          <div className="text-2xl font-semibold text-emerald-900 mt-1">{productStats.active}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs uppercase tracking-wide text-amber-700">Low Stock</div>
          <div className="text-2xl font-semibold text-amber-900 mt-1">{productStats.lowStock}</div>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
          <div className="text-xs uppercase tracking-wide text-rose-700">Out of Stock</div>
          <div className="text-2xl font-semibold text-rose-900 mt-1">{productStats.outOfStock}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by title, category, color, or custom ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All stock levels</option>
              <option value="in">In stock ({'>'}5)</option>
              <option value="low">Low stock (1-5)</option>
              <option value="out">Out of stock</option>
            </select>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="updated_desc">Newest first</option>
              <option value="title_asc">Title A-Z</option>
              <option value="price_high">Price high to low</option>
              <option value="price_low">Price low to high</option>
              <option value="stock_high">Stock high to low</option>
              <option value="stock_low">Stock low to high</option>
            </select>
          </div>
          <div className="text-xs text-gray-500">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>

        <div className="md:hidden space-y-3 p-3">
          {filteredProducts.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-10">
              No products match the current filters.
            </div>
          ) : (
            filteredProducts.map((product) => {
              const stock = Number(product._displayStock || 0);
              return (
              <div
                key={product.id}
                role="button"
                tabIndex={0}
                onClick={(e) => openProductFromRow(e, product)}
                onKeyDown={(e) => handleProductRowKeyDown(e, product)}
                className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex items-start gap-3">
                  <img
                    src={product._primaryImage}
                    alt={product._title}
                    className="w-14 h-14 rounded-lg object-cover bg-gray-100"
                    onError={applyProductImageFallback}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{product._title}</div>
                    {product.dna_id && <div className="text-xs text-gray-500 truncate">ID: {product.dna_id}</div>}
                    <div className="text-xs text-gray-500 truncate">{product._category}</div>
                    <div className="mt-1">
                      <AdminPriceDisplay
                        price={product._displayPrice}
                        originalPrice={product._displayOriginalPrice}
                        showFrom={product._variantCount > 0}
                        compact
                      />
                    </div>
                    <div className="text-xs mt-1">
                      <span className={stock <= 0 ? 'text-red-600' : stock <= 5 ? 'text-amber-600' : 'text-emerald-600'}>Stock: {stock}</span>
                    </div>
                    {product._variantCount > 0 && (
                      <div className="text-xs text-blue-600 mt-1">{product._variantCount} variant{product._variantCount === 1 ? '' : 's'}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => toggleProductStatus(product)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      product._isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {product._isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => openProductEditor(product)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );})
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No products match the current filters.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const stock = Number(product._displayStock || 0);
                  return (
                  <tr
                    key={product.id}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => openProductFromRow(e, product)}
                    onKeyDown={(e) => handleProductRowKeyDown(e, product)}
                    className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-blue-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img
                          src={product._primaryImage}
                          alt={product._title}
                          className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                          onError={applyProductImageFallback}
                        />
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{product._title}</div>
                          {product.dna_id && <div className="text-xs text-gray-500 truncate">ID: {product.dna_id}</div>}
                          <div className="text-sm text-gray-500 truncate">{product._color}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product._category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <AdminPriceDisplay
                        price={product._displayPrice}
                        originalPrice={product._displayOriginalPrice}
                        showFrom={product._variantCount > 0}
                      />
                      {product._variantCount > 0 && (
                        <div className="text-xs text-blue-600">{product._variantCount} variant{product._variantCount === 1 ? '' : 's'}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={stock <= 0 ? 'text-red-600' : stock <= 5 ? 'text-amber-600' : 'text-emerald-600'}>{stock}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        data-product-row-ignore="true"
                        onClick={() => toggleProductStatus(product)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          product._isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {product._isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          type="button"
                          data-product-row-ignore="true"
                          onClick={() => openProductEditor(product)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Icon.Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          data-product-row-ignore="true"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <ProductForm
          product={editingProduct}
          categories={categoryOptions}
          onClose={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingProduct(null);
            loadProducts();
          }}
        />
      )}
    </div>
  );
}

function ProductForm({ product, categories = [], onClose, onSave }) {
  const initialFormData = useMemo(() => ({
    title: product?.title || '',
    description: product?.description || '',
    price: product?.basePrice ?? product?.price ?? '',
    color: product?.color || '',
    category: product?.category || '',
    stock: product?.baseStock ?? product?.stock ?? 0,
    hideBaseModel: product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true,
    active: product?.active !== false,
    imageUrl: product?.img || '',
    dnaId: product?.dna_id || '',
    tags: (() => { try { const t = product?.tags; return Array.isArray(t) ? t : (t ? JSON.parse(t) : []);} catch { return []; } })()
  }), [product]);
  const [formData, setFormData] = useState(initialFormData);
  const initialVariants = useMemo(() => parseProductVariants(product?.variants), [product]);
  const [variants, setVariants] = useState(initialVariants);

  const initialGalleryItems = useMemo(() => {
    try {
      if (!product?.images) return [];
      const imgs = Array.isArray(product.images) ? product.images : JSON.parse(product.images);
      return Array.isArray(imgs) ? imgs.map((src) => String(src || '').trim()).filter(Boolean) : [];
    } catch { return []; }
  }, [product]);
  const [galleryItems, setGalleryItems] = useState(initialGalleryItems);
  const [galleryInputUrl, setGalleryInputUrl] = useState('');
  const [galleryPreview, setGalleryPreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showTagComposer, setShowTagComposer] = useState(false);
  const [loading, setLoading] = useState(false);
  const categoryListId = React.useId();
  
  const { addToast } = useToasts();
  const { request } = useApi();

  const isDirty = useMemo(() => {
    return hasFormChanges(formData, initialFormData)
      || hasFormChanges(galleryItems, initialGalleryItems)
      || hasFormChanges(variants, initialVariants)
      || String(galleryInputUrl || '').trim().length > 0;
  }, [formData, initialFormData, galleryItems, initialGalleryItems, variants, initialVariants, galleryInputUrl]);

  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);

  const slugify = useCallback((value) => String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80), []);

  const addGalleryItems = useCallback((incoming) => {
    setGalleryItems((prev) => {
      const next = [...prev];
      const known = new Set(prev.map((url) => String(url || '').trim()));
      (incoming || []).forEach((entry) => {
        const url = String(entry || '').trim();
        if (!url || known.has(url)) return;
        known.add(url);
        next.push(url);
      });
      return next;
    });
  }, []);

  const addGalleryUrl = useCallback(() => {
    const url = galleryInputUrl.trim();
    if (!url) return;
    addGalleryItems([url]);
    setGalleryInputUrl('');
  }, [galleryInputUrl, addGalleryItems]);

  const moveGalleryItem = useCallback((index, direction) => {
    setGalleryItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  }, []);

  const availableProductTags = useMemo(() => {
    const merged = new Set(DEFAULT_PRODUCT_TAGS);
    (Array.isArray(formData.tags) ? formData.tags : []).forEach((tag) => {
      const value = String(tag || '').trim().toLowerCase();
      if (value) merged.add(value);
    });
    return Array.from(merged);
  }, [formData.tags]);

  const addCustomTag = useCallback(() => {
    const normalized = String(tagInput || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (!normalized) return;
    setFormData((prev) => {
      const set = new Set(Array.isArray(prev.tags) ? prev.tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean) : []);
      set.add(normalized);
      return { ...prev, tags: Array.from(set) };
    });
    setTagInput('');
    setShowTagComposer(false);
  }, [tagInput]);

  const addVariant = useCallback(() => {
    setVariants((prev) => ([
      ...prev,
      {
        id: `variant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: '',
        sku: '',
        description: '',
        price: Number(formData.price || 0) || 0,
        stock: 0,
        active: true,
        img: ''
      }
    ]));
  }, [formData.price]);

  const updateVariant = useCallback((variantId, patch) => {
    setVariants((prev) => prev.map((variant) => (
      variant.id === variantId ? { ...variant, ...patch } : variant
    )));
  }, []);

  const removeVariant = useCallback((variantId) => {
    setVariants((prev) => prev.filter((variant) => variant.id !== variantId));
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('title', String(formData.title || '').trim());
      submitData.append('description', String(formData.description || ''));
      submitData.append('price', String(Number(formData.price || 0)));
      submitData.append('color', String(formData.color || '').trim());
      submitData.append('category', String(formData.category || '').trim());
      submitData.append('stock', String(Math.max(0, parseInt(formData.stock, 10) || 0)));
      submitData.append('hideBaseModel', String(!!formData.hideBaseModel));
      submitData.append('active', String(!!formData.active));
      submitData.append('dnaId', String(formData.dnaId || '').trim());
      if (formData.imageUrl) submitData.append('imageUrl', formData.imageUrl);
      submitData.append('imagesJson', JSON.stringify(galleryItems || []));
      submitData.append('tagsJson', JSON.stringify(Array.isArray(formData.tags) ? formData.tags : []));
      submitData.append('variantsJson', JSON.stringify((variants || []).map((variant) => ({
        id: String(variant.id || '').trim(),
        label: String(variant.label || '').trim(),
        sku: String(variant.sku || '').trim(),
        description: String(variant.description || '').trim(),
        price: Math.max(0, Number(variant.price) || 0),
        stock: Math.max(0, parseInt(variant.stock, 10) || 0),
        active: variant.active !== false,
        img: String(variant.img || '').trim() || null
      }))));
      submitData.append('variantOptionsJson', JSON.stringify([]));

      
      if (product) {
        await request(`/products/${product.id}`, {
          method: 'PUT',
          body: submitData
        });
        addToast('Product updated successfully', 'success');
      } else {
        await request('/products', {
          method: 'POST',
          body: submitData
        });
        addToast('Product created successfully', 'success');
      }
      
      onSave();
    } catch (err) {
      addToast(`Failed to ${product ? 'update' : 'create'} product`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/55 flex items-start sm:items-center justify-center z-[210] p-4"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon.X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{variants.length > 0 ? 'Base Price / fallback (৳)' : 'Price (৳)'}</label>
              <input
                type="number"
                min="0"
                step="1"
                required
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="text"
                required
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                list={categoryListId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <datalist id={categoryListId}>
                {(categories || []).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {categories.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">Start typing to use existing categories.</p>
              )}
            </div>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                <input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {variants.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">Total storefront stock adds active variant stock on top of this.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'active' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Tags</label>
            <div className="flex flex-wrap gap-3 text-sm">
              {availableProductTags.map(tag => (
                <label key={tag} className="inline-flex items-center gap-2 border rounded-full px-3 py-1">
                  <input
                    type="checkbox"
                    checked={Array.isArray(formData.tags) && formData.tags.includes(tag)}
                    onChange={(e) => {
                      setFormData(prev => {
                        const set = new Set(Array.isArray(prev.tags) ? prev.tags : []);
                        if (e.target.checked) set.add(tag); else set.delete(tag);
                        return { ...prev, tags: Array.from(set) };
                      });
                    }}
                  />
                  <span className="capitalize">{tag}</span>
                </label>
              ))}
              <button
                type="button"
                onClick={() => setShowTagComposer((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700 hover:bg-blue-100"
                aria-expanded={showTagComposer}
                aria-label="Add custom tag"
              >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-blue-300 bg-white text-sm font-semibold leading-none text-blue-700">+</span>
                <span>Add tag</span>
              </button>
            </div>
            <div className={`overflow-hidden transition-all duration-200 ease-out ${showTagComposer ? 'max-h-20 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                  placeholder="Add tag"
                  className="w-40 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={addCustomTag}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Save tag
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Use tags to power filters like New, Trending, and Best Sellers on the storefront.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Product description (optional)"
            />
          </div>

          {/* Product ID (human-friendly) - optional */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Product ID (optional)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.dnaId}
                onChange={(e) => setFormData(prev => ({ ...prev, dnaId: e.target.value }))}
                placeholder="e.g. classic-black-tote"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, dnaId: slugify(prev.title) }))}
                className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                Use title
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">If left blank, it’s auto-generated from the title.</p>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-900">Variants</div>
                <p className="text-xs text-gray-500 mt-1">
                  Turn this off to hide the base model everywhere and show only variant options on the storefront.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium ${variants.length > 0 ? 'border-gray-300 bg-white text-gray-700' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                  <input
                    type="checkbox"
                    checked={!formData.hideBaseModel}
                    onChange={(e) => setFormData(prev => ({ ...prev, hideBaseModel: !e.target.checked }))}
                    disabled={variants.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                  <span>{formData.hideBaseModel ? 'Base model hidden' : 'Base model shown'}</span>
                </label>
                {variants.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setVariants([])}
                    className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Remove all
                  </button>
                )}
                <button
                  type="button"
                  onClick={addVariant}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add variant
                </button>
              </div>
            </div>

            {variants.length === 0 ? (
              <div className="text-sm text-gray-500">No variants added. This product will behave like a normal single-option product.</div>
            ) : (
              <div className="space-y-4">
                {variants.map((variant, index) => (
                  <div key={variant.id || index} className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-900">Variant {index + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeVariant(variant.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={variant.label}
                          onChange={(e) => updateVariant(variant.id, { label: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Black / Low Light"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">SKU</label>
                        <input
                          type="text"
                          value={variant.sku}
                          onChange={(e) => updateVariant(variant.id, { sku: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="LANTERN-BLK-LOW"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Price (৳)</label>
                        <input
                          type="number"
                          min="0"
                          value={variant.price}
                          onChange={(e) => updateVariant(variant.id, { price: Number(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Stock</label>
                        <input
                          type="number"
                          min="0"
                          value={variant.stock}
                          onChange={(e) => updateVariant(variant.id, { stock: parseInt(e.target.value, 10) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Variant Description</label>
                      <textarea
                        rows={2}
                        value={variant.description || ''}
                        onChange={(e) => updateVariant(variant.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Optional description override for this option"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
                      <MediaPicker
                        label="Variant Image"
                        value={variant.img}
                        onChange={(url) => updateVariant(variant.id, { img: url })}
                        helpText="Optional image override for this specific variant"
                        ratioHint="1:1 square"
                      />
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={variant.active !== false}
                          onChange={(e) => updateVariant(variant.id, { active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <MediaPicker
              label="Product Image"
              value={formData.imageUrl}
              onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
              helpText="Main product image"
              ratioHint="1:1 square"
            />
          </div>

          {/* Gallery images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Media</label>
            <p className="text-xs text-gray-500 mb-3">Add multiple images, videos, or 3D models. Display ratio: 1:1 square.</p>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                type="text"
                value={galleryInputUrl}
                onChange={(e) => setGalleryInputUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addGalleryUrl();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Paste media URL..."
              />
              <button
                type="button"
                onClick={addGalleryUrl}
                className="px-4 py-2 border rounded-md"
              >
                Add URL
              </button>
            </div>
              <MediaPicker
                label="Add from Media Library"
                value=""
                onChange={(url) => addGalleryItems(url ? [url] : [])}
                helpText="Pick media to add to gallery"
                accept="*/*"
                ratioHint="1:1 square"
              />
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {galleryItems.map((u, i) => {
                const kind = getMediaKind(u);
                const isVideo = kind === 'video';
                const isModel = kind === 'model';
                return (
                  <div key={`${u}-${i}`} className="border rounded-lg overflow-hidden group bg-white">
                    <div className="h-24 bg-gray-50 flex items-center justify-center relative">
                      {isVideo ? (
                        <video
                          src={u}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          controls={false}
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                        />
                      ) : isModel ? (
                        <div className="text-xs text-gray-600">3D Model</div>
                      ) : (
                        <img src={u} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
                      )}
                      <span className="absolute left-2 top-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] uppercase">{kind}</span>
                      <button
                        type="button"
                        onClick={() => setGalleryPreview(u)}
                        className="absolute top-2 right-2 px-2 py-1 rounded bg-white/90 text-[10px] text-gray-700 border shadow"
                      >
                        Preview
                      </button>
                    </div>
                    <div className="p-2 space-y-2">
                      <div className="text-[11px] text-gray-600 truncate" title={u}>{u}</div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => moveGalleryItem(i, -1)}
                            disabled={i === 0}
                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => moveGalleryItem(i, 1)}
                            disabled={i === galleryItems.length - 1}
                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                          >
                            Down
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setGalleryItems(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {galleryItems.length === 0 && (
              <div className="mt-2 text-xs text-gray-500">No gallery items yet.</div>
            )}
          </div>
          {galleryPreview && (
            <MediaPreviewModal item={galleryPreview} onClose={() => setGalleryPreview(null)} />
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={requestClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Icon.Loading className="w-4 h-4" />}
              {loading ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
  return modalContent;
}

// Continue with the rest of the components...
function UsersManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const { user } = useAuth();
  const canManageAdmins = isPrivilegedRole(user?.role);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'staff',
    permissions: getRolePresetPermissions('staff')
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserInitial, setEditingUserInitial] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('/users');
      setUsers((Array.isArray(data) ? data : []).map((u) => {
        const normalizedRole = USER_ROLE_OPTIONS.includes(String(u.role || '').toLowerCase())
          ? String(u.role).toLowerCase()
          : 'staff';
        return {
          ...u,
          role: normalizedRole,
          permissions: normalizePermissions(u.permissions, normalizedRole)
        };
      }));
    } catch (err) {
      addToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const role = USER_ROLE_OPTIONS.includes(newUser.role) ? newUser.role : 'staff';
      const payload = {
        username: String(newUser.username || '').trim(),
        email: String(newUser.email || '').trim(),
        password: String(newUser.password || ''),
        role,
        permissions: normalizePermissions(newUser.permissions, role)
      };
      await request('/users', { method: 'POST', body: JSON.stringify(payload) });
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'staff',
        permissions: getRolePresetPermissions('staff')
      });
      addToast('User created', 'success');
      load();
    } catch (err) {
      addToast(err?.message || 'Failed to create user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const openUserEditor = (u) => {
    if (!canManageAdmins && isPrivilegedRole(u.role)) {
      addToast('Only admins can manage admin users', 'error');
      return;
    }
    const role = USER_ROLE_OPTIONS.includes(String(u.role || '').toLowerCase())
      ? String(u.role).toLowerCase()
      : 'staff';
    const nextEditingUser = {
      id: u.id,
      username: String(u.username || ''),
      email: String(u.email || ''),
      role,
      permissions: normalizePermissions(u.permissions, role),
      password: ''
    };
    setEditingUser(nextEditingUser);
    setEditingUserInitial(nextEditingUser);
  };

  const closeUserEditor = () => {
    const isDirty = !!editingUser && !!editingUserInitial && hasFormChanges(editingUser, editingUserInitial);
    if (!confirmDiscardChanges(isDirty)) return;
    setEditingUser(null);
    setEditingUserInitial(null);
  };

  const updateEditingRole = (role) => {
    if (!editingUser) return;
    const nextRole = USER_ROLE_OPTIONS.includes(role) ? role : 'staff';
    setEditingUser(prev => prev ? {
      ...prev,
      role: nextRole,
      permissions: getRolePresetPermissions(nextRole)
    } : prev);
  };

  const toggleEditingPermission = (permissionKey) => {
    setEditingUser(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionKey]: !prev.permissions?.[permissionKey]
        }
      };
    });
  };

  const saveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const role = USER_ROLE_OPTIONS.includes(editingUser.role) ? editingUser.role : 'staff';
      const payload = {
        username: String(editingUser.username || '').trim(),
        email: String(editingUser.email || '').trim(),
        role,
        permissions: normalizePermissions(editingUser.permissions, role)
      };
      const nextPassword = String(editingUser.password || '').trim();
      if (nextPassword) payload.password = nextPassword;
      await request(`/users/${editingUser.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      addToast('User updated', 'success');
      setEditingUser(null);
      setEditingUserInitial(null);
      load();
    } catch (err) {
      addToast('Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!editingUser) return;
    if (!window.confirm(`Delete user "${editingUser.username}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await request(`/users/${editingUser.id}`, { method: 'DELETE' });
      addToast('User deleted', 'success');
      setEditingUser(null);
      setEditingUserInitial(null);
      load();
    } catch (err) {
      addToast('Failed to delete user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading users...</span>
      </div>
    );
  }

  const filteredUsers = users.filter((u) => {
    const normalizedRole = String(u.role || '').toLowerCase();
    const roleMatch = roleFilter === 'all' || normalizedRole === roleFilter;
    const q = searchTerm.trim().toLowerCase();
    const queryMatch = !q || String(u.username || '').toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q);
    return roleMatch && queryMatch;
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600 mt-1">Manage admin and staff access</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900">Add User</h2>
        <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <input className="border rounded px-3 py-2" placeholder="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
          <input className="border rounded px-3 py-2" placeholder="Email" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
          <input className="border rounded px-3 py-2" placeholder="Password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
          <select
            className="border rounded px-3 py-2"
            value={newUser.role}
            onChange={e => setNewUser(prev => ({
              ...prev,
              role: e.target.value,
              permissions: getRolePresetPermissions(e.target.value)
            }))}
          >
            {canManageAdmins && <option value="admin">Admin</option>}
            <option value="manager">Manager</option>
            <option value="staff">Staff</option>
          </select>
          <div className="md:col-span-4">
            <p className="text-xs text-gray-500 mb-3">
              Role presets are applied automatically. Use Manage on a user row to customize granular permissions.
            </p>
            <button type="submit" disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{creating ? 'Creating...' : 'Create User'}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Existing Users</h2>
          <div className="flex gap-2">
            <input
              className="border rounded px-3 py-2 text-sm"
              placeholder="Search username/email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select className="border rounded px-3 py-2 text-sm" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">{u.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                  <td className="px-6 py-4 text-sm">
                    <button onClick={() => openUserEditor(u)} className="px-3 py-1.5 border rounded-md text-gray-700 hover:bg-gray-50">
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td className="px-6 py-8 text-sm text-gray-500 text-center" colSpan={3}>No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <div
          className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
          onClick={closeUserEditor}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Manage User</h3>
                <p className="text-sm text-gray-500">Role presets auto-apply; you can then fine-tune individual permissions.</p>
              </div>
              <button onClick={closeUserEditor} className="text-gray-500 hover:text-gray-800">
                <Icon.X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={editingUser.username}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, username: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    type="email"
                    value={editingUser.email}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select className="border rounded px-3 py-2 w-full" value={editingUser.role} onChange={e => updateEditingRole(e.target.value)}>
                    {canManageAdmins && <option value="admin">Admin</option>}
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    type="password"
                    placeholder="Leave blank to keep current password"
                    value={editingUser.password || ''}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, password: e.target.value } : prev)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Permissions</h4>
                <button
                  type="button"
                  onClick={() => setEditingUser(prev => prev ? { ...prev, permissions: getRolePresetPermissions(prev.role) } : prev)}
                  className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50"
                >
                  Reset to Role Preset
                </button>
              </div>

              <div className="space-y-4">
                {USER_PERMISSION_GROUPS.map(group => (
                  <div key={group.id} className="border rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-800">{group.title}</h5>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.permissions.map(permissionKey => (
                        <label key={permissionKey} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!editingUser.permissions?.[permissionKey]}
                            onChange={() => toggleEditingPermission(permissionKey)}
                          />
                          <span>{prettyPermissionLabel(permissionKey)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <div>
                {!isPrivilegedRole(editingUser.role) && (
                  <button
                    type="button"
                    onClick={deleteUser}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete User'}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={closeUserEditor} className="px-4 py-2 border rounded-md">Cancel</button>
                <button type="button" onClick={saveUser} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Admin error boundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-white border border-red-200 text-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm">{String(this.state.error)}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminDashboard() {
  return (
    <AuthProvider>
      <ApiProvider>
        <ToastsProvider>
          <ErrorBoundary>
            <AdminDashboardContent />
          </ErrorBoundary>
        </ToastsProvider>
      </ApiProvider>
    </AuthProvider>
  );
}

function AdminDashboardContent() {
  const { request } = useApi();
  const { user, loading: authLoading, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toasts, addToast, removeToast } = useToasts();
  const urlInitRef = useRef(false);
  const notificationAudioRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState('unknown');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notificationSoundPreference, setNotificationSoundPreference] = useState(() => readAdminNotificationSound());
  const [notificationSoundUrl, setNotificationSoundUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const autoEnableAttemptedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await request('/settings');
        if (!mounted) return;
        setNotificationSoundUrl(String(settings?.notificationSoundUrl || '').trim());
        setFaviconUrl(String(settings?.faviconUrl || '').trim());
      } catch {}
    })();
    return () => { mounted = false; };
  }, [request]);

  useEffect(() => {
    applyFaviconHref(String(faviconUrl || '').trim() || '/logo.png');
  }, [faviconUrl]);

  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_NOTIFICATION_SOUND_KEY, notificationSoundPreference);
    } catch {}
  }, [notificationSoundPreference]);

  const playNotificationSound = useCallback(() => {
    if (notificationSoundPreference === 'off') {
      setAudioEnabled(false);
      return false;
    }
    if (notificationSoundPreference === 'beep') {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
          setAudioEnabled(false);
          return false;
        }
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = 880;
        gain.gain.value = 0.08;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.12);
        oscillator.onended = () => {
          try { ctx.close(); } catch {}
        };
        setAudioEnabled(true);
        return true;
      } catch {
        setAudioEnabled(false);
        return false;
      }
    }
    const audio = notificationAudioRef.current;
    if (!audio) return false;
    if (notificationSoundPreference === 'custom' && !String(notificationSoundUrl || '').trim()) {
      setAudioEnabled(false);
      return false;
    }
    try {
      audio.volume = 0.9;
      audio.currentTime = 0;
      const maybePromise = audio.play();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise
          .then(() => setAudioEnabled(true))
          .catch(() => setAudioEnabled(false));
      } else {
        setAudioEnabled(true);
      }
      return true;
    } catch {}
    setAudioEnabled(false);
    return false;
  }, [notificationSoundPreference, notificationSoundUrl, setAudioEnabled]);

  const enableAlerts = useCallback(async (silent = false) => {
    let notifOk = notificationPermission === 'granted';
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        notifOk = true;
        setNotificationPermission('granted');
      } else {
        try {
          const permission = await Notification.requestPermission();
          notifOk = permission === 'granted';
          setNotificationPermission(permission);
        } catch {
          notifOk = false;
          setNotificationPermission(Notification.permission || 'default');
        }
      }
    } else {
      setNotificationPermission('unsupported');
    }

    let audioOk = audioEnabled;
    if (notificationSoundPreference === 'off') {
      audioOk = false;
    } else if (!silent) {
      audioOk = playNotificationSound();
    }
    setAudioEnabled(audioOk);

    if (!silent) {
      if (!notifOk) {
        addToast('Desktop notifications are blocked. Enable them in your browser settings.', 'info');
      }
      if (!audioOk) {
        addToast('Sound alerts are blocked. Click the page and try again.', 'info');
      }
    }
  }, [notificationPermission, audioEnabled, addToast, setNotificationPermission, setAudioEnabled, notificationSoundPreference, playNotificationSound]);

  useEffect(() => {
    if (!user || !hasUserPermission(user, 'admin.stream:read')) return;
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    } else {
      setNotificationPermission('unsupported');
    }
    const autoEnable = () => {
      if (autoEnableAttemptedRef.current) return;
      autoEnableAttemptedRef.current = true;
      enableAlerts(true);
    };
    window.addEventListener('click', autoEnable, { once: true });
    return () => window.removeEventListener('click', autoEnable);
  }, [user, enableAlerts]);

  const showDesktopNotification = useCallback((title, body, targetPage = null) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') {
      setNotificationPermission(Notification.permission || 'default');
      return;
    }
    try {
      const notification = new Notification(title, { body });
      if (targetPage) {
        notification.onclick = () => {
          try { window.focus(); } catch {}
          setCurrentPage(targetPage);
          try { notification.close(); } catch {}
        };
      }
      setNotificationPermission('granted');
    } catch {}
  }, [setNotificationPermission, setCurrentPage]);

  const handleAdminEvent = useCallback((eventType, payload = {}) => {
    let title = 'New activity';
    let body = '';
    let toastMessage = 'New activity';
    let targetPage = null;
    if (eventType === 'order.created') {
      const displayId = payload.displayOrderId || payload.orderId || '';
      const itemCount = payload.itemsCount != null ? `${payload.itemsCount} item${payload.itemsCount === 1 ? '' : 's'}` : '';
      const total = payload.total != null ? `Total ${payload.total}` : '';
      title = 'New order received';
      body = [displayId && `Order ${displayId}`, itemCount, total].filter(Boolean).join(' • ');
      toastMessage = displayId ? `New order received (${displayId})` : 'New order received';
      targetPage = hasUserPermission(user, 'orders:read') ? 'orders' : null;
    } else if (eventType === 'review.created') {
      title = 'New review submitted';
      if (payload.productTitle) {
        body = `Product: ${payload.productTitle}`;
        toastMessage = `New review: ${payload.productTitle}`;
      } else if (payload.productSlug) {
        body = `Product: ${payload.productSlug}`;
        toastMessage = `New review: ${payload.productSlug}`;
      } else if (payload.productId) {
        body = `Product ${String(payload.productId).slice(0, 8)}...`;
        toastMessage = 'New review submitted';
      } else {
        body = 'New product review';
        toastMessage = 'New review submitted';
      }
      targetPage = hasUserPermission(user, 'reviews:read') ? 'reviews' : null;
    } else if (eventType === 'help.created') {
      title = 'New support ticket';
      body = payload.topic ? `Topic: ${payload.topic}` : 'New help request';
      toastMessage = 'New support ticket';
      targetPage = hasUserPermission(user, 'help:read') ? 'help' : null;
    }
    playNotificationSound();
    addToast(toastMessage, 'info', targetPage ? { targetPage } : undefined);
    showDesktopNotification(title, body, targetPage);
  }, [addToast, playNotificationSound, showDesktopNotification, user]);

  useEffect(() => {
    if (!user || !hasUserPermission(user, 'admin.stream:read')) return;
    let source = null;
    let closed = false;
    let retryTimer = null;

    const getStreamUrl = () => {
      const token = getStoredAdminToken();
      if (token) {
        return `/shopapi/admin/stream?access_token=${encodeURIComponent(token)}`;
      }
      return '/shopapi/admin/stream';
    };

    const connect = () => {
      if (closed) return;
      try {
        source = new EventSource(getStreamUrl(), { withCredentials: true });
      } catch {
        return;
      }

      const onEvent = (type) => (event) => {
        let payload = {};
        try { payload = JSON.parse(event.data || '{}'); } catch {}
        handleAdminEvent(type, payload);
      };

      source.addEventListener('order.created', onEvent('order.created'));
      source.addEventListener('review.created', onEvent('review.created'));
      source.addEventListener('help.created', onEvent('help.created'));

      source.onerror = async () => {
        if (source) {
          source.close();
        }
        if (closed) return;
        // If no token is available and cookie auth is unavailable, stop retrying to avoid 401 spam.
        if (!getStoredAdminToken()) {
          try {
            const cookieProbe = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
            if (!cookieProbe.ok) return;
          } catch {
            return;
          }
        }
        retryTimer = window.setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (source) {
        source.close();
      }
    };
  }, [user, handleAdminEvent]);

  // Overlay scrollbar (invisible until scrolling) – mirrors client behavior
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let styleTag = document.getElementById('pv-admin-scrollbar-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'pv-admin-scrollbar-style';
      styleTag.innerHTML = `
html{scrollbar-gutter:stable both-edges}html,body{scrollbar-width:none}body::-webkit-scrollbar{width:0;height:0}
#pv-scrollbar-admin{position:fixed;right:10px;top:10px;bottom:10px;width:10px;pointer-events:none;z-index:1000;opacity:0;transform:translateX(10px);transition:opacity .24s ease-out,transform .24s ease-out}
#pv-scrollbar-admin.show{opacity:1;transform:translateX(0)}
#pv-scrollbar-admin .thumb{position:absolute;left:0;width:10px;border-radius:9999px;background:rgba(17,24,39,.35);box-shadow:0 2px 8px rgba(0,0,0,.12)}
@media(hover:hover){#pv-scrollbar-admin .thumb:hover{background:rgba(17,24,39,.5)}}`;
      document.head.appendChild(styleTag);
    }

    let bar = document.getElementById('pv-scrollbar-admin');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'pv-scrollbar-admin';
      const t = document.createElement('div');
      t.className = 'thumb';
      bar.appendChild(t);
      document.body.appendChild(bar);
    }
    const thumb = bar.querySelector('.thumb');

    let hideTimer = null;
    const showBar = () => {
      bar.classList.add('show');
      if (hideTimer) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => bar.classList.remove('show'), 700);
    };
    const updateThumb = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const scrollHeight = Math.max(body.scrollHeight, doc.scrollHeight, body.offsetHeight, doc.offsetHeight, body.clientHeight, doc.clientHeight);
      const vh = window.innerHeight;
      const barPadding = 20; // top+bottom from CSS
      const barHeight = Math.max(0, vh - barPadding);
      const maxScroll = Math.max(1, scrollHeight - vh);
      if (scrollHeight <= vh + 1) {
        bar.style.display = 'none';
        return;
      }
      bar.style.display = 'block';
      const minThumb = 28;
      const thumbH = Math.max(minThumb, Math.round((vh * vh) / scrollHeight));
      const ratio = Math.min(1, Math.max(0, scrollTop / maxScroll));
      const top = 10 + Math.round((barHeight - thumbH) * ratio);
      thumb.style.height = thumbH + 'px';
      thumb.style.transform = `translateY(${top}px)`;
    };
    const onScroll = () => { updateThumb(); showBar(); };
    const onResize = () => { updateThumb(); };
    updateThumb();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      const inst = document.getElementById('pv-scrollbar-admin');
      if (inst && inst.isConnected) inst.remove();
    };
  }, []);

  const navigation = React.useMemo(() => {
    const can = (perm) => hasUserPermission(user, perm);
    const canManagePromotions = can('campaigns:read') || can('promotions:read');
    // Reordered navigation: remove Newsletter and merge stock alerts into dashboard.
    return [
      can('dashboard:view') ? { id: 'dashboard', name: 'Dashboard', icon: Icon.Dashboard } : null,
      can('products:read') ? { id: 'products', name: 'Products', icon: Icon.Package } : null,
      can('orders:read') ? { id: 'orders', name: 'Orders', icon: Icon.ShoppingCart } : null,
      canManagePromotions ? { id: 'campaigns', name: 'Promotions', icon: Icon.Tag } : null,
      // Requests moved under Settings submenu
      can('reviews:read') ? { id: 'reviews', name: 'Reviews', icon: Icon.Eye } : null,
      can('audit:read') ? { id: 'audit', name: 'Audit Logs', icon: Icon.Activity } : null,
      can('help:read') ? { id: 'help', name: 'Help', icon: Icon.Palette } : null,
      can('media:read') ? { id: 'media', name: 'Media', icon: Icon.Upload } : null,
      (can('settings:read') || can('settings:write') || can('users:read') || can('backup:manage') || can('legal:write')) ? { id: 'settings', name: 'Settings', icon: Icon.Settings } : null,
    ].filter(Boolean);
  }, [user]);

  useEffect(() => {
    if (navigation.length && !navigation.find(n => n.id === currentPage)) {
      setCurrentPage(navigation[0].id);
    }
  }, [currentPage, navigation]);

  useEffect(() => {
    if (urlInitRef.current) return;
    if (typeof window === 'undefined') return;
    if (!navigation.length) return;
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    if (page && navigation.find(n => n.id === page)) {
      setCurrentPage(page);
    }
    urlInitRef.current = true;
  }, [navigation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!urlInitRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') !== currentPage) {
      params.set('page', currentPage);
      if (currentPage !== 'settings') {
        params.delete('tab');
      }
      const qs = params.toString();
      const nextUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [currentPage]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Icon.Loading className="w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={setCurrentPage} />;
      case 'products':
        return <ProductsManager />;
      case 'orders':
        return <OrdersManager />;
      case 'campaigns':
        return <PromotionsManager />;
      case 'reviews':
        return <ReviewsManager storefrontBaseUrl={getStorefrontBaseUrl()} />;
      case 'audit':
        return <AuditLogsManager />;
      case 'help':
        return <HelpNotesManager />;
      case 'media':
        return <MediaLibrary />;
      case 'settings':
        return (
          <SettingsManager
            notificationSoundPreference={notificationSoundPreference}
            onNotificationSoundPreferenceChange={setNotificationSoundPreference}
            notificationSoundUrl={notificationSoundUrl}
            onNotificationSoundUrlChange={setNotificationSoundUrl}
            onFaviconUrlChange={setFaviconUrl}
            onPreviewNotificationSound={() => {
              const ok = playNotificationSound();
              if (!ok) {
                addToast('Could not play preview sound. Browser might be blocking audio.', 'info');
              }
            }}
          />
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <audio
        ref={notificationAudioRef}
        src={notificationSoundPreference === 'custom' && String(notificationSoundUrl || '').trim() ? notificationSoundUrl : notificationSound}
        preload="auto"
      />
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-gray-900 text-gray-100 flex items-center justify-between px-4 shadow">
        <button
          onClick={() => setSidebarOpen(true)}
          className="inline-flex items-center gap-2 text-sm"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          Menu
        </button>
        <div className="text-sm font-semibold">Admin Panel</div>
        <div className="w-5 h-5" />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-gray-900 text-gray-100 shadow-xl z-50 transform transition-transform duration-200 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <h1 className="text-xl font-bold">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-gray-200"
            aria-label="Close sidebar"
          >
            <Icon.X className="w-5 h-5" />
          </button>
        </div>
        <nav className="mt-4 flex-1 overflow-y-auto pb-24">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setCurrentPage(item.id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center px-6 py-3 text-left transition-colors ${
                currentPage === item.id 
                  ? 'bg-gray-800 text-white border-r-2 border-blue-400' 
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </button>
          ))}
        </nav>
        <div className="mt-auto p-6 border-t border-gray-800">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-300">
              {user.username}
            </div>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-red-400"
              title="Logout"
            >
              <Icon.LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="md:ml-64">
        <main className="p-4 md:p-8 pt-20 md:pt-8">
          {hasUserPermission(user, 'admin.stream:read') && notificationPermission === 'denied' && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                Enable desktop and sound alerts for realtime updates.
              </div>
              <button
                type="button"
                onClick={() => enableAlerts(false)}
                className="px-3 py-1.5 rounded-md border border-amber-300 bg-white hover:bg-amber-100"
              >
                Enable alerts
              </button>
            </div>
          )}
          {renderCurrentPage()}
        </main>
      </div>
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => {
          const baseTone =
            toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200';
          return (
            <button
              key={toast.id}
              type="button"
              onClick={() => {
                if (toast.targetPage) {
                  setCurrentPage(toast.targetPage);
                  setSidebarOpen(false);
                }
                removeToast(toast.id);
              }}
              className={`px-4 py-3 rounded-lg shadow-lg max-w-sm text-left transition cursor-pointer hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 ${baseTone}`}
            >
              <span>{toast.message}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useState('all');
  const [bkashOnly, setBkashOnly] = useState(false);
  const [bkashPendingOnly, setBkashPendingOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCustomOrderForm, setShowCustomOrderForm] = useState(false);
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();

  useEffect(() => {
    try {
      const preset = sessionStorage.getItem('pv_admin_orders_status');
      if (preset) {
        setStatusFilter(preset);
        sessionStorage.removeItem('pv_admin_orders_status');
      }
    } catch {}
  }, []);

  const loadOrders = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String((page - 1) * pageSize));
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (paymentStatusFilter !== 'all') params.set('paymentStatus', paymentStatusFilter);
      if (fulfillmentStatusFilter !== 'all') params.set('fulfillmentStatus', fulfillmentStatusFilter);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      const data = await request(`/orders?${params.toString()}`);
      setOrders(data);
      setHasMore(Array.isArray(data) && data.length === pageSize);
    } catch (err) {
      addToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, page, pageSize, statusFilter, paymentStatusFilter, fulfillmentStatusFilter, debouncedSearchTerm]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, paymentStatusFilter, fulfillmentStatusFilter, searchTerm, pageSize]);

  const isInteractiveOrderRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-order-row-ignore="true"]'));
  }, []);

  const openOrderDetailsFromRow = useCallback((event, order) => {
    if (isInteractiveOrderRowTarget(event.target)) return;
    setSelectedOrder(order);
  }, [isInteractiveOrderRowTarget]);

  const handleOrderRowKeyDown = useCallback((event, order) => {
    if (isInteractiveOrderRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedOrder(order);
    }
  }, [isInteractiveOrderRowTarget]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await request(`/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      addToast('Order status updated', 'success');
      loadOrders();
    } catch (err) {
      addToast('Failed to update order status', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const selectedIds = filteredOrders.filter((order) => selected[order.id]).map((order) => order.id);
      const query = selectedIds.length > 0
        ? `?selectedIds=${encodeURIComponent(selectedIds.join(','))}`
        : '';
      await downloadFile(`/export/orders${query}`, `orders_steadfast_${new Date().toISOString().split('T')[0]}.xlsx`);
      addToast('Orders exported successfully', 'success');
    } catch (err) {
      addToast('Failed to export orders', 'error');
    }
  };

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (bkashOnly) list = list.filter(o => (o.payment?.method === 'bkash'));
    if (bkashPendingOnly) list = list.filter(o => (o.payment?.method === 'bkash' && o.status === 'pending'));
    return list;
  }, [orders, bkashOnly, bkashPendingOnly]);

  const allSelected = useMemo(() => filteredOrders.length > 0 && filteredOrders.every(o => selected[o.id]), [filteredOrders, selected]);
  const selectedCount = useMemo(() => filteredOrders.filter((order) => selected[order.id]).length, [filteredOrders, selected]);
  const toggleSelectAll = () => {
    if (allSelected) {
      const next = { ...selected };
      filteredOrders.forEach(o => { delete next[o.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filteredOrders.forEach(o => { next[o.id] = true; });
      setSelected(next);
    }
  };

  const bulkTogglePaymentStatus = async () => {
    const targets = filteredOrders.filter(o => selected[o.id]);
    if (targets.length === 0) { addToast('Select orders first', 'error'); return; }
    try {
      for (const order of targets) {
        const current = order.payment_status || (order.payment?.method === 'bkash' ? 'pending' : 'unpaid');
        const next = current === 'paid' ? 'unpaid' : 'paid';
        await request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify({ paymentStatus: next }) });
      }
      addToast(`Updated payment status for ${targets.length} order(s)`, 'success');
      setSelected({});
      loadOrders();
    } catch (err) {
      addToast('Bulk update failed', 'error');
    }
  };

  const bulkUpdateOrderStatus = async () => {
    const orderIds = filteredOrders.filter((order) => selected[order.id]).map((order) => order.id);
    if (orderIds.length === 0) { addToast('Select orders first', 'error'); return; }
    if (!bulkStatusValue) { addToast('Choose a status first', 'error'); return; }
    try {
      const result = await request('/orders/bulk-status', {
        method: 'PUT',
        body: JSON.stringify({ orderIds, status: bulkStatusValue })
      });
      const updatedCount = Number(result?.updatedCount || orderIds.length);
      const autoMarkedPaidCount = Number(result?.autoMarkedPaidCount || 0);
      addToast(
        autoMarkedPaidCount > 0
          ? `Updated ${updatedCount} order(s); ${autoMarkedPaidCount} delivered order(s) were also marked paid`
          : `Updated status for ${updatedCount} order(s)`,
        'success'
      );
      setSelected({});
      setBulkStatusValue('');
      loadOrders();
    } catch (err) {
      addToast('Failed to bulk update order status', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading orders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage customer orders</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCustomOrderForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Create
          </button>
          <button
            onClick={loadOrders}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2"
          >
            <Icon.Activity className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Icon.Download className="w-4 h-4" />
            Export Steadfast XLSX
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          {['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded-full ${
                statusFilter === status
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
          <label className="ml-2 inline-flex items-center gap-1 text-sm text-gray-700">
            <input type="checkbox" checked={bkashOnly} onChange={(e) => setBkashOnly(e.target.checked)} /> bKash only
          </label>
          <label className="inline-flex items-center gap-1 text-sm text-gray-700">
            <input type="checkbox" checked={bkashPendingOnly} onChange={(e) => setBkashPendingOnly(e.target.checked)} /> Pending bKash
          </label>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[170px]"
          >
            <option value="all">Payment: All</option>
            <option value="unpaid">Payment: Unpaid</option>
            <option value="pending">Payment: Pending</option>
            <option value="paid">Payment: Paid</option>
            <option value="refunded">Payment: Refunded</option>
            <option value="partial_refund">Payment: Partial Refund</option>
          </select>
          <select
            value={fulfillmentStatusFilter}
            onChange={(e) => setFulfillmentStatusFilter(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[180px]"
          >
            <option value="all">Fulfillment: All</option>
            <option value="unfulfilled">Fulfillment: Unfulfilled</option>
            <option value="processing">Fulfillment: Processing</option>
            <option value="shipped">Fulfillment: Shipped</option>
            <option value="delivered">Fulfillment: Delivered</option>
            <option value="returned">Fulfillment: Returned</option>
            <option value="cancelled">Fulfillment: Cancelled</option>
          </select>
          <select
            value={bulkStatusValue}
            onChange={(e) => setBulkStatusValue(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[170px]"
            title="Choose the order status to apply to selected orders"
          >
            <option value="">Set status (selected)</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={bulkUpdateOrderStatus}
            disabled={!bulkStatusValue || selectedCount === 0}
            className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            title="Apply the chosen status to selected orders"
          >
            Apply status ({selectedCount})
          </button>
          <button
            onClick={bulkTogglePaymentStatus}
            className="ml-auto bg-gray-900 text-white px-3 py-1 rounded-md hover:bg-gray-800 text-sm"
            title="Toggle payment status for selected orders"
          >
            Toggle payment (selected)
          </button>
        </div>
        {/* Search */}
        <div className="mt-3 relative">
          <Icon.Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search orders by ID, name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="text-sm text-gray-600">Page {page}</div>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
          >
            Next
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value) || 50)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md"
            >
              {[25, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        </div>

        <div className="md:hidden space-y-3 p-3">
          {filteredOrders.map(order => (
            <div
              key={order.id}
              role="button"
              tabIndex={0}
              onClick={(e) => openOrderDetailsFromRow(e, order)}
              onKeyDown={(e) => handleOrderRowKeyDown(e, order)}
              className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-mono text-gray-700">{order.display_order_id || order.id.slice(0, 8) + '...'}</div>
                  <div className="text-sm font-medium text-gray-900">
                    {order.customer.name || `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() || '-'}
                  </div>
                  <div className="text-xs text-gray-500">{order.customer.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">{currency(order.total)}</div>
                  {order.discount_amount > 0 && (
                    <div className="text-xs text-green-600">-{currency(order.discount_amount)} discount</div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600">
                {order.items.length} items • {new Date(order.created_at).toLocaleDateString()}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <select
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                  className="text-xs font-semibold rounded-full px-2 py-1 border"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="text-xs px-3 py-1 rounded border"
                >
                  View
                </button>
              </div>
            </div>
          ))}
          {filteredOrders.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-6">No orders found</div>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map(order => (
                <tr
                  key={order.id}
                  tabIndex={0}
                  onClick={(e) => openOrderDetailsFromRow(e, order)}
                  onKeyDown={(e) => handleOrderRowKeyDown(e, order)}
                  className={`${order.status === 'pending' ? 'bg-orange-50 hover:bg-orange-100 focus:bg-orange-100' : 'hover:bg-gray-50 focus:bg-gray-50'} cursor-pointer focus:outline-none`}
                >
                  <td className="px-3 py-4"><input type="checkbox" checked={!!selected[order.id]} onChange={(e) => setSelected(prev => ({ ...prev, [order.id]: e.target.checked }))} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {order.display_order_id || order.id.slice(0, 8) + '...'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.customer.name || `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() || '-'}
                    </div>
                    <div className="text-sm text-gray-500">{order.customer.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.items.length} items
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {currency(order.total)}
                    {order.discount_amount > 0 && (
                      <div className="text-xs text-green-600">-{currency(order.discount_amount)} discount</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="text-gray-900">{order.payment?.method?.toUpperCase() || '-'}</div>
                    <div className="mt-1">
                      {(() => {
                        const ps = order.payment_status || (order.payment?.method === 'bkash' ? 'pending' : 'unpaid');
                        const cls =
                          ps === 'paid' ? 'bg-green-100 text-green-800' :
                          ps === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          ps === 'refunded' ? 'bg-red-100 text-red-800' :
                          ps === 'partial_refund' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-700';
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                            Payment: {ps.replace('_', ' ')}
                          </span>
                        );
                      })()}
                    </div>
                    {order.payment?.method === 'bkash' && (
                      <>
                        <div className="text-xs text-gray-600">Txn: {order.payment?.bkashTxn || '-'}</div>
                        {order.payment?.bkashScreenshotUrl && (
                          <div className="mt-1">
                            <a
                              href={(String(order.payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${order.payment.bkashScreenshotUrl}` : order.payment.bkashScreenshotUrl)}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-600 underline"
                              title="Open bKash receipt"
                            >
                              View receipt
                            </a>
                          </div>
                        )}
                      </>
                      )}
                    <div className="mt-1 space-x-1">
                      {order.payment?.method === 'bkash' && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'pending' ? 'bg-pink-100 text-pink-800' : 'bg-pink-200 text-pink-900'}`}>
                          {order.status === 'pending' ? 'Pending verify' : 'Verified'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'confirmed' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <div className="mt-1 text-xs text-gray-500">
                      Fulfillment: {order.fulfillment_status || 'unfulfilled'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center gap-3">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Icon.Eye className="w-4 h-4" />
                    </button>
                    {(order.payment?.method === 'bkash' && order.status === 'pending') && (
                      <button
                        onClick={() => request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify({ status: 'confirmed', paymentStatus: 'paid' }) }).then(loadOrders)}
                        className="text-pink-700 hover:text-pink-900"
                        title="Mark as paid (bKash)"
                      >
                        Mark paid
                      </button>
                    )}
                    <button
                      onClick={() => openInvoice(order.id)}
                      className="text-gray-700 hover:text-gray-900"
                      title="Print Invoice"
                    >
                      Invoice
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    {/* Order Details Modal */}
    {selectedOrder && (
      <OrderDetailsModal 
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onUpdate={(updatedOrder) => {
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setSelectedOrder(updatedOrder);
        }}
      />
    )}
    
    {/* Custom Order Form Modal */}
    {showCustomOrderForm && (
      <CustomOrderForm
        onClose={() => setShowCustomOrderForm(false)}
        onCreated={() => {
          loadOrders();
          setShowCustomOrderForm(false);
        }}
      />
    )}
  </div>
);
}

function CustomOrderForm({ onClose, onCreated }) {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({
    shippingRates: { inside: 60, outside: 120 },
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20
  });
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    address: '',
    city: '',
    items: [],
    shippingLocation: 'inside',
    paymentMethod: 'cod',
    adminDiscountAmount: '',
    notes: ''
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const isDirty = useMemo(() => {
    return Boolean(
      String(formData.customerName || '').trim()
      || String(formData.customerEmail || '').trim()
      || String(formData.customerPhone || '').trim()
      || String(formData.address || '').trim()
      || String(formData.city || '').trim()
      || String(formData.notes || '').trim()
      || (Array.isArray(formData.items) && formData.items.length > 0)
      || String(selectedProduct || '').trim()
      || String(selectedVariant || '').trim()
      || Number(quantity || 1) !== 1
      || formData.shippingLocation !== 'inside'
      || formData.paymentMethod !== 'cod'
      || Number(formData.adminDiscountAmount || 0) > 0
    );
  }, [formData, selectedProduct, selectedVariant, quantity]);

  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);

  useEffect(() => {
    request('/products').then(data => setProducts((Array.isArray(data) ? data : []).filter(p => p.active).map((product) => summarizeProductForAdmin(product)))).catch(() => addToast('Failed to load products', 'error'));
    request('/settings').then(setSettings).catch(() => {});
  }, [request, addToast]);

  const selectedProductRecord = useMemo(
    () => products.find((product) => product.id === selectedProduct) || null,
    [products, selectedProduct]
  );

  const selectableVariants = useMemo(
    () => (selectedProductRecord?.variants || []).filter((variant) => variant.active !== false),
    [selectedProductRecord]
  );

  const selectedBasePrice = useMemo(
    () => Number(
      selectedProductRecord?.baseDisplayPrice
      ?? selectedProductRecord?.basePrice
      ?? selectedProductRecord?.price
      ?? 0
    ) || 0,
    [selectedProductRecord]
  );

  const selectedBaseOriginalPrice = useMemo(() => {
    const original = Number(selectedProductRecord?.baseOriginalPrice);
    return original > selectedBasePrice ? original : null;
  }, [selectedBasePrice, selectedProductRecord]);

  useEffect(() => {
    if (!selectedProductRecord) {
      setSelectedVariant('');
      return;
    }
    if (!selectedVariant) return;
    if (selectableVariants.some((variant) => variant.id === selectedVariant)) return;
    setSelectedVariant('');
  }, [selectedProductRecord, selectableVariants, selectedVariant]);

  const addItem = () => {
    const product = selectedProductRecord;
    if (!product) return;
    const variant = selectableVariants.find((entry) => entry.id === selectedVariant) || null;

    const itemKey = variant ? `${product.id}:${variant.id}` : product.id;
    const existing = formData.items.find(i => i.itemKey === itemKey);
    const lineName = variant ? `${product.title} - ${variant.label}` : product.title;
    const linePrice = variant ? variant.price : selectedBasePrice;
    if (existing) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(i => i.itemKey === itemKey ? { ...i, quantity: i.quantity + quantity } : i)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          itemKey,
          productId: product.id,
          variantId: variant?.id || null,
          variantLabel: variant?.label || (selectableVariants.length > 0 ? 'Base model' : null),
          variantSku: variant?.sku || null,
          name: lineName,
          price: linePrice,
          originalPrice: variant
            ? (Number(variant.originalPrice) > Number(variant.price) ? Number(variant.originalPrice) : null)
            : (Number(selectedBaseOriginalPrice) > Number(selectedBasePrice) ? Number(selectedBaseOriginalPrice) : null),
          quantity,
          img: variant?.img || product.img || null
        }]
      }));
    }
    setSelectedProduct('');
    setSelectedVariant('');
    setQuantity(1);
  };

  const removeItem = (itemKey) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter(i => i.itemKey !== itemKey) }));
  };

  const calculateTotal = () => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = formData.shippingLocation === 'inside' ? (settings.shippingRates?.inside || 60) : (settings.shippingRates?.outside || 120);
    const bkashCharge = formData.paymentMethod === 'bkash' && settings.bkashCashoutEnabled
      ? Math.max(0, Number(settings.bkashCashoutAmount) || 0)
      : 0;
    const adminDiscount = Math.max(0, Math.round(Number(formData.adminDiscountAmount || 0) || 0));
    return Math.max(0, itemsTotal + shipping + bkashCharge - adminDiscount);
  };
  const createOrderItemsTotal = formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const createOrderShipping = formData.shippingLocation === 'inside' ? (settings.shippingRates?.inside || 60) : (settings.shippingRates?.outside || 120);
  const createOrderBkashCharge = formData.paymentMethod === 'bkash' && settings.bkashCashoutEnabled
    ? Math.max(0, Number(settings.bkashCashoutAmount) || 0)
    : 0;
  const createOrderAdminDiscount = Math.max(0, Math.round(Number(formData.adminDiscountAmount || 0) || 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      addToast('Please add at least one product', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      const orderData = {
        customerName: formData.customerName,
        email: formData.customerEmail,
        phone: formData.customerPhone,
        address: formData.address,
        city: formData.city,
        items: formData.items,
        paymentMethod: formData.paymentMethod,
        shippingCost: formData.shippingLocation === 'inside' ? (settings.shippingRates?.inside || 60) : (settings.shippingRates?.outside || 120),
        adminDiscountAmount: Math.max(0, Math.round(Number(formData.adminDiscountAmount || 0) || 0)),
        total: calculateTotal(),
        notes: formData.notes,
        source: 'admin_custom'
      };
      
      await request('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      
      addToast('Custom order created successfully', 'success');
      onCreated();
    } catch (err) {
      console.error('Create order error:', err);
      const errorMessage = err.error || err.message || 'Failed to create order';
      addToast('Failed to create order: ' + errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">Create</h2>
          <button onClick={requestClose} className="text-gray-400 hover:text-gray-600"><Icon.X className="w-6 h-6" /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" required value={formData.customerName} onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input type="tel" required value={formData.customerPhone} onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                <input type="email" value={formData.customerEmail} onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input type="text" required value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <textarea required rows={2} value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Products</h3>
            <div className="flex gap-2 mb-4">
              <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md">
                <option value="">Select a product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} - {p._variantCount > 0 ? 'From ' : ''}{currency(p._displayPrice)}
                    {Number(p._displayOriginalPrice) > Number(p._displayPrice) ? ` (was ${currency(p._displayOriginalPrice)})` : ''}
                  </option>
                ))}
              </select>
              {selectableVariants.length > 0 && (
                <select value={selectedVariant} onChange={(e) => setSelectedVariant(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">
                    Base model - {currency(selectedBasePrice)}
                    {Number(selectedBaseOriginalPrice) > Number(selectedBasePrice) ? ` (was ${currency(selectedBaseOriginalPrice)})` : ''}
                  </option>
                  {selectableVariants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.label} - {currency(variant.price)}
                      {Number(variant.originalPrice) > Number(variant.price) ? ` (was ${currency(variant.originalPrice)})` : ''}
                    </option>
                  ))}
                </select>
              )}
              <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="w-20 px-3 py-2 border border-gray-300 rounded-md" />
              <button type="button" onClick={addItem} disabled={!selectedProduct} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">Add</button>
            </div>

            {formData.items.length > 0 && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Product</th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Qty</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Price</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Total</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                    <tbody>
                      {formData.items.map(item => (
                      <tr key={item.itemKey || `${item.productId}:${item.variantId || 'base'}`} className="border-t">
                        <td className="px-4 py-2 text-sm">{item.name}</td>
                        <td className="px-4 py-2 text-sm text-center">{item.quantity}</td>
                        <td className="px-4 py-2 text-sm text-right">৳{item.price}</td>
                        <td className="px-4 py-2 text-sm text-right font-medium">৳{item.price * item.quantity}</td>
                        <td className="px-4 py-2 text-right">
                          <button type="button" onClick={() => removeItem(item.itemKey || `${item.productId}:${item.variantId || 'base'}`)} className="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Location</label>
              <select value={formData.shippingLocation} onChange={(e) => setFormData(prev => ({ ...prev, shippingLocation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="inside">Inside Dhaka (৳{settings.shippingRates?.inside || 60})</option>
                <option value="outside">Outside Dhaka (৳{settings.shippingRates?.outside || 120})</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="cod">Cash on Delivery</option>
                <option value="bkash">bKash</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes</label>
              <textarea rows={3} value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Special instructions or notes..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Discount (৳)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.adminDiscountAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, adminDiscountAmount: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="0"
              />
              <p className="mt-1 text-xs text-gray-500">Subtracts directly from this order total without changing product prices.</p>
            </div>
          </div>

          {formData.items.length > 0 && (
            <div className="border-t pt-4">
              <div className="space-y-1 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Items subtotal</span>
                  <span>{currency(createOrderItemsTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>{currency(createOrderShipping)}</span>
                </div>
                {createOrderBkashCharge > 0 && (
                  <div className="flex justify-between">
                    <span>bKash cashout charge</span>
                    <span>{currency(createOrderBkashCharge)}</span>
                  </div>
                )}
                {createOrderAdminDiscount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Special Discount</span>
                    <span>-{currency(createOrderAdminDiscount)}</span>
                  </div>
                )}
              </div>
              <div className="mt-2 flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>{currency(calculateTotal())}</span>
              </div>
              {formData.paymentMethod === 'bkash' && settings.bkashCashoutEnabled && (
                <div className="mt-2 text-xs text-gray-500 text-right">
                  Includes bKash cashout charge: ৳{Math.max(0, Number(settings.bkashCashoutAmount) || 0)}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={requestClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" disabled={submitting}>Cancel</button>
            <button type="submit" disabled={submitting || formData.items.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2">
              {submitting ? (<><Icon.Loading className="w-4 h-4" />Creating...</>) : ('Create Order')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PromotionsManager() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const hasLoadedRef = useRef(false);
  const { request } = useApi();
  const { addToast } = useToasts();

  const loadPromotions = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        if (hasLoadedRef.current) setRefreshing(true);
        else setLoading(true);
      }
      const data = await request('/deals');
      setPromotions(Array.isArray(data) ? data : []);
      hasLoadedRef.current = true;
    } catch (err) {
      if (!silent) addToast('Failed to load promotions', 'error');
    } finally {
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [request, addToast]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const normalizedPromotions = useMemo(() => {
    return promotions.map((promo) => {
      let productCount = 0;
      try {
        if (promo.product_ids) {
          const ids = JSON.parse(promo.product_ids);
          productCount = Array.isArray(ids) ? ids.length : 0;
        }
      } catch {
        productCount = 0;
      }
      const hasCategory = Boolean(String(promo.category || '').trim());
      const scopeType = productCount > 0 ? 'products' : (hasCategory ? 'category' : 'all');
      const now = Date.now();
      const startsAt = promo.start_date ? new Date(promo.start_date).getTime() : null;
      const endsAt = promo.end_date ? new Date(promo.end_date).getTime() : null;
      const isScheduled = startsAt && startsAt > now;
      const isExpired = endsAt && endsAt < now;
      const activeNow = !!promo.active && !isScheduled && !isExpired;
      const status = activeNow ? 'active' : (isScheduled ? 'scheduled' : (promo.active ? 'expired' : 'inactive'));
      const showOnOffers = Number(promo.show_on_offers ?? 1) === 1;
      const showAsPopup = Number(promo.show_as_popup ?? 0) === 1;
      const showAsHeader = Number(promo.show_as_header ?? 0) === 1;
      const hasImage = Boolean(String(promo.image_url || '').trim());
      let displayPages = [];
      try {
        const parsed = promo.display_pages ? JSON.parse(promo.display_pages) : [];
        displayPages = Array.isArray(parsed) ? parsed : [];
      } catch {
        displayPages = [];
      }
      return {
        ...promo,
        _productCount: productCount,
        _scopeType: scopeType,
        _status: status,
        _showOnOffers: showOnOffers,
        _showAsPopup: showAsPopup,
        _showAsHeader: showAsHeader,
        _hasImage: hasImage,
        _displayPages: displayPages,
        _scopeLabel: productCount > 0
          ? `${productCount} product(s)`
          : (hasCategory ? `Category • ${promo.category}` : 'All items'),
        _searchHaystack: `${promo.title || ''} ${promo.description || ''} ${promo.code || ''} ${promo.category || ''} ${promo.header_text || ''} ${promo.offer_details || ''}`.toLowerCase()
      };
    });
  }, [promotions]);

  const promotionStats = useMemo(() => ({
    total: normalizedPromotions.length,
    active: normalizedPromotions.filter((promo) => promo._status === 'active').length,
    scheduled: normalizedPromotions.filter((promo) => promo._status === 'scheduled').length,
    inactive: normalizedPromotions.filter((promo) => promo._status === 'inactive' || promo._status === 'expired').length
  }), [normalizedPromotions]);

  const filteredPromotions = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();
    let list = normalizedPromotions;
    if (query) {
      list = list.filter((promo) => promo._searchHaystack.includes(query));
    }
    if (statusFilter !== 'all') {
      list = list.filter((promo) => promo._status === statusFilter);
    }
    if (scopeFilter === 'all_items') {
      list = list.filter((promo) => promo._scopeType === 'all');
    } else if (scopeFilter !== 'all') {
      list = list.filter((promo) => promo._scopeType === scopeFilter);
    }
    if (typeFilter !== 'all') {
      list = list.filter((promo) => String(promo.type || '') === typeFilter);
    }
    return list;
  }, [normalizedPromotions, debouncedSearchTerm, statusFilter, scopeFilter, typeFilter]);

  const isInteractivePromoRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-promo-row-ignore="true"]'));
  }, []);

  const openPromotionEditor = useCallback((promo) => {
    setEditingPromotion(promo);
    setShowForm(true);
  }, []);

  const openPromotionFromRow = useCallback((event, promo) => {
    if (isInteractivePromoRowTarget(event.target)) return;
    openPromotionEditor(promo);
  }, [isInteractivePromoRowTarget, openPromotionEditor]);

  const handlePromotionRowKeyDown = useCallback((event, promo) => {
    if (isInteractivePromoRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPromotionEditor(promo);
    }
  }, [isInteractivePromoRowTarget, openPromotionEditor]);

  const handleDelete = async (promotionId) => {
    const shouldDelete = window.confirm('Are you sure you want to delete this promotion?');
    if (!shouldDelete) return;

    try {
      await request(`/deals/${promotionId}`, { method: 'DELETE' });
      setPromotions((prev) => prev.filter((promo) => promo.id !== promotionId));
      addToast('Promotion deleted successfully', 'success');
    } catch (err) {
      addToast('Failed to delete promotion', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading promotions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
          <p className="text-gray-600 mt-1">Manage discount rules and storefront placements from one section.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => loadPromotions()}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
          >
            {refreshing && <Icon.Loading className="w-4 h-4" />}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingPromotion(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{promotionStats.total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Active</div>
          <div className="text-2xl font-semibold text-emerald-900 mt-1">{promotionStats.active}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
          <div className="text-xs uppercase tracking-wide text-sky-700">Scheduled</div>
          <div className="text-2xl font-semibold text-sky-900 mt-1">{promotionStats.scheduled}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-700">Inactive/Expired</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{promotionStats.inactive}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by title, description, code, or scope..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="scheduled">Scheduled</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
            <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All scopes</option>
              <option value="all_items">All items</option>
              <option value="category">Category</option>
              <option value="products">Specific products</option>
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </div>
          <div className="text-xs text-gray-500">
            Showing {filteredPromotions.length} of {normalizedPromotions.length} promotions
          </div>
        </div>
        <div className="md:hidden space-y-3 p-3">
          {filteredPromotions.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No promotions match the current filters.</div>
          ) : filteredPromotions.map((promo) => (
            <div
              key={promo.id}
              role="button"
              tabIndex={0}
              onClick={(e) => openPromotionFromRow(e, promo)}
              onKeyDown={(e) => handlePromotionRowKeyDown(e, promo)}
              className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="text-sm font-medium text-gray-900">{promo.title}</div>
              <div className="text-xs text-gray-500 mt-1">{promo.description}</div>
              <div className="text-xs text-gray-400 mt-1">Scope: {promo._scopeLabel}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">{promo.type}</span>
                <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                  {promo.type === 'percentage' ? `${promo.value}%` : currency(promo.value)}
                </span>
                <span className={`px-2 py-1 rounded-full ${
                  promo._status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : promo._status === 'scheduled'
                      ? 'bg-sky-100 text-sky-800'
                      : promo._status === 'expired'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                  {promo._status}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showOnOffers ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showOnOffers ? 'Public offers: on' : 'Public offers: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showAsPopup ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showAsPopup ? 'Popup: on' : 'Popup: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showAsHeader ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showAsHeader ? 'Header: on' : 'Header: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._hasImage ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._hasImage ? 'Image: set' : 'Image: missing'}
                </span>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Pages: {promo._displayPages.length > 0 ? promo._displayPages.join(', ') : 'all pages'}
              </div>
              <div className="mt-2 text-xs text-gray-600">
                Usage: {promo.used_count || 0}{promo.max_uses && ` / ${promo.max_uses}`}
                {promo.max_items && <div>{promo.items_used || 0} / {promo.max_items} items</div>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  data-promo-row-ignore="true"
                  onClick={() => openPromotionEditor(promo)}
                  className="text-blue-600 hover:text-blue-900 text-sm"
                >
                  Edit
                </button>
                <button
                  type="button"
                  data-promo-row-ignore="true"
                  onClick={() => handleDelete(promo.id)}
                  className="text-red-600 hover:text-red-900 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPromotions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No promotions match the current filters.</td>
                </tr>
              ) : filteredPromotions.map((promo) => (
                <tr
                  key={promo.id}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => openPromotionFromRow(e, promo)}
                  onKeyDown={(e) => handlePromotionRowKeyDown(e, promo)}
                  className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-blue-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{promo.title}</div>
                    <div className="text-sm text-gray-500">{promo.description}</div>
                    <div className="text-xs text-gray-400 mt-1">Scope: {promo._scopeLabel}</div>
                    <div className={`text-xs mt-1 ${promo._showOnOffers ? 'text-emerald-600' : 'text-gray-400'}`}>
                      Public offers page: {promo._showOnOffers ? 'Shown' : 'Hidden'}
                    </div>
                    <div className="text-xs mt-1 text-gray-500">
                      Popup: {promo._showAsPopup ? 'Shown' : 'Hidden'} • Header: {promo._showAsHeader ? 'Shown' : 'Hidden'}
                    </div>
                    <div className="text-xs mt-1 text-gray-500">
                      Pages: {promo._displayPages.length > 0 ? promo._displayPages.join(', ') : 'All pages'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {promo.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promo.type === 'percentage' ? `${promo.value}%` : currency(promo.value)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promo.code ? (
                      <code className="bg-gray-100 px-2 py-1 rounded">{promo.code}</code>
                    ) : (
                      <span className="text-gray-400">Auto-apply</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {promo.used_count || 0}
                    {promo.max_uses && ` / ${promo.max_uses}`}
                    {promo.max_items && (
                      <div>{promo.items_used || 0} / {promo.max_items} items</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      promo._status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : promo._status === 'scheduled'
                          ? 'bg-sky-100 text-sky-800'
                          : promo._status === 'expired'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                      {promo._status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-promo-row-ignore="true"
                        onClick={() => openPromotionEditor(promo)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Icon.Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        data-promo-row-ignore="true"
                        onClick={() => handleDelete(promo.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Icon.Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <PromotionForm
          promotion={editingPromotion}
          onClose={() => {
            setShowForm(false);
            setEditingPromotion(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingPromotion(null);
            loadPromotions();
          }}
        />
      )}
    </div>
  );
}

function PromotionForm({ promotion, onClose, onSave }) {
  const availablePromotionPages = [
    'home', 'catalog', 'product', 'offers', 'track', 'help', 'cart', 'checkout', 'thanks', 'terms', 'privacy', 'cookies'
  ];
  const initialProductIds = (() => {
    try {
      if (promotion?.product_ids) return JSON.parse(promotion.product_ids);
      if (promotion?.productIds) return Array.isArray(promotion.productIds) ? promotion.productIds : [];
      return [];
    } catch {
      return [];
    }
  })();
  const initialScope = (() => {
    if (initialProductIds.length > 0) return 'products';
    if (promotion?.category) return 'category';
    return 'all';
  })();
  const initialDisplayPages = (() => {
    try {
      if (!promotion?.display_pages) return [];
      const parsed = JSON.parse(promotion.display_pages);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();
  const initialFormData = useMemo(() => ({
    title: promotion?.title || '',
    description: promotion?.description || '',
    type: promotion?.type || 'percentage',
    value: promotion?.value || '',
    code: promotion?.code || '',
    minOrderAmount: promotion?.min_order_amount || 0,
    maxUses: promotion?.max_uses || '',
    maxItems: promotion?.max_items || '',
    startDate: promotion?.start_date ? promotion.start_date.split('T')[0] : '',
    endDate: promotion?.end_date ? promotion.end_date.split('T')[0] : '',
    scope: initialScope,
    category: promotion?.category || '',
    productIds: initialProductIds,
    imageUrl: promotion?.image_url || '',
    targetUrl: promotion?.target_url || '',
    offerDetails: promotion?.offer_details || '',
    headerText: promotion?.header_text || '',
    showOnOffers: Number(promotion?.show_on_offers ?? 1) === 1,
    showAsPopup: Number(promotion?.show_as_popup ?? 0) === 1,
    showAsHeader: Number(promotion?.show_as_header ?? 0) === 1,
    displayPages: initialDisplayPages,
    active: Number(promotion?.active ?? 1) === 1
  }), [promotion, initialScope, initialProductIds, initialDisplayPages]);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  const { request } = useApi();
  const { addToast } = useToasts();
  const isDirty = useMemo(() => hasFormChanges(formData, initialFormData), [formData, initialFormData]);
  const hasVisualPlacement = formData.showOnOffers || formData.showAsPopup;
  const hasHeaderPlacement = formData.showAsHeader;
  const hasAnyPlacement = hasVisualPlacement || hasHeaderPlacement;
  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);
  const categoryOptions = useMemo(() => {
    const set = new Set();
    (products || []).forEach(p => {
      const c = (p?.category || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return (products || []).filter(p => {
      const title = (p?.title || '').toLowerCase();
      const cat = (p?.category || '').toLowerCase();
      return title.includes(q) || cat.includes(q);
    });
  }, [products, productSearch]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingProducts(true);
        const data = await request('/products');
        if (!mounted) return;
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    })();
    return () => { mounted = false; };
  }, [request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.scope === 'category' && !formData.category.trim()) {
        addToast('Choose a category for this discount scope', 'error');
        setLoading(false);
        return;
      }
      if (formData.scope === 'products' && (!formData.productIds || formData.productIds.length === 0)) {
        addToast('Select at least one product for this discount scope', 'error');
        setLoading(false);
        return;
      }
      const targetUrl = String(formData.targetUrl || '').trim();
      if (hasVisualPlacement && !targetUrl) {
        addToast('Target URL is required for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !targetUrl.startsWith('/') && !/^https?:\/\//i.test(targetUrl)) {
        addToast('Target URL must start with / or http(s)://', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !String(formData.imageUrl || '').trim()) {
        addToast('Set an image for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !String(formData.offerDetails || '').trim()) {
        addToast('Offer details are required for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasHeaderPlacement && !String(formData.headerText || '').trim()) {
        addToast('Header text is required when header placement is enabled', 'error');
        setLoading(false);
        return;
      }
      const submitData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        value: parseFloat(formData.value),
        code: formData.code || null,
        minOrderAmount: parseInt(formData.minOrderAmount) || 0,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        maxItems: formData.maxItems ? parseInt(formData.maxItems) : null,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        imageUrl: hasVisualPlacement ? (formData.imageUrl || null) : null,
        targetUrl: hasVisualPlacement ? targetUrl : null,
        offerDetails: hasVisualPlacement ? (formData.offerDetails || null) : null,
        headerText: hasHeaderPlacement ? (formData.headerText || null) : null,
        showOnOffers: !!formData.showOnOffers,
        showAsPopup: !!formData.showAsPopup,
        showAsHeader: !!formData.showAsHeader,
        displayPages: hasAnyPlacement && Array.isArray(formData.displayPages) ? formData.displayPages : [],
        active: formData.active
      };
      if (formData.scope === 'category') {
        submitData.category = formData.category.trim();
        submitData.productIds = null;
      } else if (formData.scope === 'products') {
        submitData.productIds = formData.productIds || [];
        submitData.category = null;
      } else {
        submitData.category = null;
        submitData.productIds = null;
      }

      if (promotion) {
        await request(`/deals/${promotion.id}`, {
          method: 'PUT',
          body: JSON.stringify(submitData)
        });
        addToast('Promotion updated successfully', 'success');
      } else {
        await request('/deals', {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
        addToast('Promotion created successfully', 'success');
      }
      
      onSave();
    } catch (err) {
      addToast(`Failed to ${promotion ? 'update' : 'create'} promotion`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {promotion ? 'Edit Promotion' : 'Add New Promotion'}
          </h2>
          <button
            onClick={requestClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon.X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed">Fixed Amount Discount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Scope</label>
              <select
                value={formData.scope}
                onChange={(e) => {
                  const nextScope = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    scope: nextScope,
                    category: nextScope === 'category' ? prev.category : '',
                    productIds: nextScope === 'products' ? (prev.productIds || []) : []
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All items (default)</option>
                <option value="category">Specific category</option>
                <option value="products">Specific products</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.type === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (৳)'}
              </label>
              <input
                type="number"
                min="0"
                max={formData.type === 'percentage' ? 100 : undefined}
                step={formData.type === 'percentage' ? 1 : 0.01}
                required
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code (Optional)</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for auto-apply"
              />
            </div>

            {formData.scope === 'category' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  list="promo-category-options"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Bags, Wallets, Accessories"
                />
                <datalist id="promo-category-options">
                  {(categoryOptions || []).map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                {categoryOptions.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">Start typing to use an existing category.</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount (৳)</label>
              <input
                type="number"
                min="0"
                value={formData.minOrderAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.maxUses}
                onChange={(e) => setFormData(prev => ({ ...prev, maxUses: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Unlimited"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Items (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.maxItems}
                onChange={(e) => setFormData(prev => ({ ...prev, maxItems: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="For limited stock discounts"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'active' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Placements</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showOnOffers}
                    onChange={(e) => setFormData(prev => ({ ...prev, showOnOffers: e.target.checked }))}
                  />
                  Show on offers page
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showAsPopup}
                    onChange={(e) => setFormData(prev => ({ ...prev, showAsPopup: e.target.checked }))}
                  />
                  Show as popup
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showAsHeader}
                    onChange={(e) => setFormData(prev => ({ ...prev, showAsHeader: e.target.checked }))}
                  />
                  Show in header bar
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
                       </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Promotion description (optional)"
            />
          </div>

          {formData.scope === 'products' && (
            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-900">Select products</div>
                  <div className="text-xs text-gray-500">{formData.productIds.length} selected</div>
                </div>
                {loadingProducts && <span className="text-xs text-gray-500">Loading...</span>}
              </div>
              <div className="mt-3">
                <div className="relative">
                  <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products by name or category..."
                    className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-3 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white">
                {filteredProducts.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No products match your search.</div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredProducts.map((p) => {
                      const checked = formData.productIds.includes(p.id);
                      return (
                        <li key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(formData.productIds || []);
                              if (e.target.checked) next.add(p.id); else next.delete(p.id);
                              setFormData(prev => ({ ...prev, productIds: Array.from(next) }));
                            }}
                          />
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 truncate">{p.title}</div>
                            <div className="text-xs text-gray-500 truncate">{p.category || 'Uncategorized'}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {hasVisualPlacement && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <MediaPicker
                    label="Promotion Image"
                    value={formData.imageUrl}
                    onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    helpText="Required for popup and offers placements."
                    ratioHint="16:3 wide or 1:1 square"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target URL <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.targetUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetUrl: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="/offers or https://example.com"
                    required={hasVisualPlacement}
                  />
                  <p className="mt-1 text-xs text-gray-500">Required. Must start with `/` or `http(s)://`.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Offer Details</label>
                <textarea
                  rows={4}
                  value={formData.offerDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, offerDetails: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain how the offer works and how customers can avail it."
                  required={hasVisualPlacement}
                />
              </div>
            </>
          )}

          {hasHeaderPlacement && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Bar Text</label>
              <input
                type="text"
                value={formData.headerText}
                onChange={(e) => setFormData(prev => ({ ...prev, headerText: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Short text for the top header bar"
                required={hasHeaderPlacement}
              />
            </div>
          )}

          {hasAnyPlacement && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Show On Pages</label>
              <p className="text-xs text-gray-500 mb-2">Leave all unchecked to show on every page.</p>
              <div className="flex flex-wrap gap-2">
                {availablePromotionPages.map((pageKey) => {
                  const checked = formData.displayPages.includes(pageKey);
                  return (
                    <label key={pageKey} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const next = new Set(prev.displayPages || []);
                            if (e.target.checked) next.add(pageKey);
                            else next.delete(pageKey);
                            return { ...prev, displayPages: Array.from(next) };
                          });
                        }}
                      />
                      <span className="uppercase tracking-wide">{pageKey}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={requestClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Icon.Loading className="w-4 h-4" />}
              {loading ? 'Saving...' : (promotion ? 'Update Promotion' : 'Create Promotion')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// CustomizationManager removed


function ReviewsManager({ storefrontBaseUrl = '' }) {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  
  const load = React.useCallback(async () => { 
    try { 
      setLoading(true); 
      const rows = await request(`/reviews/admin?status=${statusFilter}`); 
      setReviews(rows); 
    } catch { 
      addToast('Failed to load reviews','error'); 
    } finally { 
      setLoading(false);
    } 
  }, [request, addToast, statusFilter]);
  
  useEffect(() => { load(); }, [load]);
  
  const approve = async (id) => { 
    try { 
      await request(`/reviews/${id}/approve`, { method: 'PUT' }); 
      addToast('Review approved','success'); 
      load(); 
    } catch { 
      addToast('Failed to approve','error'); 
    } 
  };
  
  const remove = async (id) => { 
    if (!window.confirm('Are you sure you want to delete this review?')) return; 
    try { 
      await request(`/reviews/${id}`, { method: 'DELETE' }); 
      addToast('Review deleted','success');
      load(); 
    } catch { 
      addToast('Failed to delete','error');
    } 
  };

  const getReviewProductHref = useCallback((review) => {
    const base = String(storefrontBaseUrl || '').replace(/\/+$/, '');
    const slug = String(review?.product_slug || '').trim();
    if (slug) return `${base}/product/${encodeURIComponent(slug)}`;
    const productId = String(review?.product_id || '').trim();
    if (productId) return `${base}/product/${encodeURIComponent(productId)}`;
    return '';
  }, [storefrontBaseUrl]);

  const renderReviewProductLink = useCallback((review) => {
    const label = review?.product_title || review?.product_slug || review?.product_id || 'Unknown product';
    const href = getReviewProductHref(review);
    if (!href) return <span className="text-sm text-gray-700">{label}</span>;
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
        {label}
      </a>
    );
  }, [getReviewProductHref]);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-gray-600">Manage customer reviews and ratings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-lg ${statusFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-4 py-2 rounded-lg ${statusFilter === 'approved' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Approved
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded shadow-sm">
        {loading ? (
          <div className="p-6 text-center">
            <Icon.Loading className="w-6 h-6 inline-block text-blue-600 mr-2" />
            Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No {statusFilter} reviews found
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-3 p-3">
              {reviews.map(r => (
                <div key={r.id} className="border rounded-lg p-3 bg-white shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-gray-500">Product</div>
                      <div>{renderReviewProductLink(r)}</div>
                    </div>
                    <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mt-2 text-sm text-gray-900 line-clamp-2">{r.text}</div>
                  <div className="mt-2 text-xs text-gray-600">Rating: {r.rating}</div>
                  <div className="mt-3 flex items-center gap-2">
                    {statusFilter === 'pending' && (
                      <button
                        onClick={() => approve(r.id)}
                        className="px-3 py-1 rounded-md bg-green-600 text-white text-xs"
                      >
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedReview(r)}
                      className="px-3 py-1 rounded-md border text-xs"
                    >
                      View
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      className="px-3 py-1 rounded-md border border-red-300 text-red-600 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Media</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviews.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{renderReviewProductLink(r)}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-1 text-gray-500">({r.rating})</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm max-w-md">
                      <p className="line-clamp-2">{r.text}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.media_url ? (
                        <button
                          onClick={() => setSelectedReview(r)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Icon.Eye className="w-4 h-4" />
                          View
                        </button>
                      ) : (
                        <span className="text-gray-400">No media</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        {statusFilter === 'pending' && (
                          <button 
                            onClick={() => approve(r.id)} 
                            className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                        )}
                        <button 
                          onClick={() => setSelectedReview(r)} 
                          className="px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
                        >
                          <Icon.Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => remove(r.id)} 
                          className="px-3 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      
      {/* Review Details Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReview(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Review Details</h2>
              <button onClick={() => setSelectedReview(null)} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product ID</label>
                <p className="mt-1">{renderReviewProductLink(selectedReview)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Rating</label>
                <div className="mt-1 flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg key={i} className={`w-5 h-5 ${i < selectedReview.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({selectedReview.rating}/5)</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Review Text</label>
                <p className="mt-1 text-sm text-gray-900 leading-relaxed">{selectedReview.text}</p>
              </div>
              
              {selectedReview.media_url && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Media</label>
                  <div className="mt-2">
                    <img 
                      src={resolveMediaUrl(selectedReview.media_url)} 
                      alt="Review media" 
                      className="w-full rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="hidden w-full h-48 items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
                    >
                      <p className="text-sm text-gray-500">Image failed to load</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    selectedReview.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedReview.status}
                  </span>
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Submitted</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedReview.created_at).toLocaleString()}
                </p>
              </div>
              
              <div className="pt-4 border-t flex flex-col sm:flex-row sm:justify-end gap-2">
                {selectedReview.status === 'pending' && (
                  <button
                    onClick={() => {
                      approve(selectedReview.id);
                      setSelectedReview(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve Review
                  </button>
                )}
                <button
                  onClick={() => {
                    remove(selectedReview.id);
                    setSelectedReview(null);
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  Delete Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditLogsManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [undoingId, setUndoingId] = useState(null);
  const pageSize = 25;

  const truncateText = useCallback((value, max = 96) => {
    const s = String(value ?? '');
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  }, []);

  const parseMeta = useCallback((raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const formatActionLabel = useCallback((action) => {
    return String(action || '')
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, []);

  const normalizeMetaValue = useCallback((value) => {
    if (value === null || typeof value === 'undefined' || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '-';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
    if (typeof value === 'object') return `${Object.keys(value).length} field${Object.keys(value).length === 1 ? '' : 's'}`;
    return String(value);
  }, []);

  const collectDiffKeys = useCallback((before, after) => {
    if (!before || !after || typeof before !== 'object' || typeof after !== 'object' || Array.isArray(before) || Array.isArray(after)) return [];
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    return keys.filter((key) => {
      try {
        return JSON.stringify(before[key]) !== JSON.stringify(after[key]);
      } catch {
        return String(before[key]) !== String(after[key]);
      }
    });
  }, []);

  const summarizeMeta = useCallback((meta) => {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
      return { keyFacts: [], diffKeys: [], diffCount: 0, topLevelCount: 0, hasRawMeta: false };
    }

    const before = meta.before;
    const after = meta.after;
    const diffKeys = collectDiffKeys(before, after);

    const hiddenKeys = new Set(['before', 'after']);
    const knownFactKeys = [
      'target_action',
      'status',
      'paymentStatus',
      'fulfillmentStatus',
      'edited',
      'reason',
      'error',
      'path',
      'name',
      'result'
    ];

    const keyFacts = [];
    if (typeof meta.from !== 'undefined' || typeof meta.to !== 'undefined') {
      keyFacts.push({
        label: 'Transition',
        value: `${normalizeMetaValue(meta.from)} -> ${normalizeMetaValue(meta.to)}`
      });
      hiddenKeys.add('from');
      hiddenKeys.add('to');
    }

    for (const key of knownFactKeys) {
      if (typeof meta[key] === 'undefined') continue;
      keyFacts.push({
        label: key === 'target_action'
          ? 'Target action'
          : key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
        value: normalizeMetaValue(meta[key])
      });
      hiddenKeys.add(key);
    }

    const extraKeys = Object.keys(meta).filter((key) => !hiddenKeys.has(key));
    for (const key of extraKeys.slice(0, 4)) {
      keyFacts.push({
        label: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        value: normalizeMetaValue(meta[key])
      });
    }

    return {
      keyFacts: keyFacts.slice(0, 6),
      diffKeys: diffKeys.slice(0, 8),
      diffCount: diffKeys.length,
      topLevelCount: Object.keys(meta).length,
      hasRawMeta: true
    };
  }, [collectDiffKeys, normalizeMetaValue]);

  const preparedLogs = useMemo(() => {
    return logs.map((log) => {
      const metaObj = parseMeta(log.metadata);
      const summary = summarizeMeta(metaObj);
      const entityLabel = log.entity_type
        ? `${log.entity_type}${log.entity_id ? `:${String(log.entity_id).slice(0, 10)}` : ''}`
        : '-';
      const undoLabel =
        log.undo_status === 'success'
          ? 'Undone'
          : log.undo_status === 'pending'
            ? 'Undo in progress'
            : (log.undoable ? 'Undo available' : (log.undo_reason || 'Not undoable'));

      return {
        log,
        metaObj,
        summary,
        actionLabel: formatActionLabel(log.action),
        entityLabel,
        undoLabel,
        timeLabel: new Date(log.created_at).toLocaleString()
      };
    });
  }, [logs, parseMeta, summarizeMeta, formatActionLabel]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      if (actionFilter.trim()) params.set('action', actionFilter.trim());
      if (actorFilter.trim()) params.set('actor', actorFilter.trim());
      if (entityFilter.trim()) params.set('entityType', entityFilter.trim());
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      const rows = await request(`/audit?${params.toString()}`);
      setLogs(rows);
      setHasMore(rows.length === pageSize);
    } catch (err) {
      addToast('Failed to load audit logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, page, pageSize, actionFilter, actorFilter, entityFilter, fromDate, toDate]);

  useEffect(() => {
    setPage(0);
  }, [actionFilter, actorFilter, entityFilter, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUndo = async (log) => {
    if (!log?.id) return;
    if (!log.undoable) {
      addToast(log.undo_reason || 'Undo not available', 'error');
      return;
    }
    const ok = window.confirm('Undo this action? This will revert changes from this log.');
    if (!ok) return;
    try {
      setUndoingId(log.id);
      await request(`/audit/${log.id}/undo`, { method: 'POST' });
      addToast('Undo completed', 'success');
      load();
    } catch (err) {
      addToast(err.message || 'Undo failed', 'error');
    } finally {
      setUndoingId(null);
    }
  };

  const clearFilters = useCallback(() => {
    setActionFilter('');
    setActorFilter('');
    setEntityFilter('');
    setFromDate('');
    setToDate('');
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600">Track admin actions and critical system changes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (actionFilter.trim()) params.set('action', actionFilter.trim());
              if (actorFilter.trim()) params.set('actor', actorFilter.trim());
              if (entityFilter.trim()) params.set('entityType', entityFilter.trim());
              if (fromDate) params.set('from', fromDate);
              if (toDate) params.set('to', toDate);
              window.open(`/shopapi/audit/export?${params.toString()}`, '_blank');
            }}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs uppercase text-gray-500">Action</label>
            <input
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="order_update, review_approve..."
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">Actor</label>
            <input
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="admin username"
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">Entity Type</label>
            <input
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              placeholder="order, review, settings..."
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
          >
            Clear filters
          </button>
          <div className="text-xs text-gray-500">
            Showing up to {pageSize} logs per page
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm">
        {loading ? (
          <div className="p-6 text-center">
            <Icon.Loading className="w-6 h-6 inline-block text-blue-600 mr-2" />
            Loading audit logs…
          </div>
        ) : preparedLogs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No audit logs found</div>
        ) : (
          <div className="p-3 space-y-3">
            {preparedLogs.map((entry) => (
              <article key={entry.log.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {entry.actionLabel || entry.log.action}
                      </span>
                      <span className="text-xs text-gray-500">{entry.timeLabel}</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{entry.log.actor_username || '-'}</span>
                      <span className="text-gray-400"> · </span>
                      <span>{entry.entityLabel}</span>
                      <span className="text-gray-400"> · </span>
                      <span>{entry.log.ip || '-'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedLog(entry)}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                    >
                      View details
                    </button>
                    {entry.log.undoable && entry.log.undo_status !== 'success' ? (
                      <button
                        type="button"
                        onClick={() => handleUndo(entry.log)}
                        disabled={undoingId === entry.log.id}
                        className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {undoingId === entry.log.id ? 'Undoing…' : 'Undo'}
                      </button>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                        {entry.undoLabel}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-3">
                  <div className="rounded border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Change Summary</div>
                    <div className="text-sm text-gray-700">
                      {entry.summary.diffCount > 0
                        ? `${entry.summary.diffCount} field${entry.summary.diffCount === 1 ? '' : 's'} changed`
                        : (entry.summary.topLevelCount > 0
                          ? `${entry.summary.topLevelCount} metadata field${entry.summary.topLevelCount === 1 ? '' : 's'}`
                          : 'No metadata details')}
                    </div>
                  </div>
                  <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Key Details</div>
                    {entry.summary.keyFacts.length === 0 && entry.summary.diffKeys.length === 0 ? (
                      <div className="text-sm text-gray-500">No structured metadata available</div>
                    ) : (
                      <div className="space-y-2">
                        {entry.summary.keyFacts.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {entry.summary.keyFacts.map((fact, idx) => (
                              <span key={`${fact.label}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="font-medium text-gray-600">{fact.label}:</span>
                                <span>{truncateText(fact.value, 72)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        {entry.summary.diffKeys.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {entry.summary.diffKeys.map((key) => (
                              <span key={key} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 border border-blue-200 text-blue-700">
                                {key}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <button
            className="px-3 py-1 rounded border disabled:opacity-50"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Prev
          </button>
          <div className="text-xs text-gray-500">Page {page + 1}</div>
          <button
            className="px-3 py-1 rounded border disabled:opacity-50"
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
          >
            Next
          </button>
        </div>
      </div>

      {selectedLog && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[92vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selectedLog.actionLabel || selectedLog.log.action}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedLog.timeLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[calc(92vh-148px)] space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Actor</div>
                  <div className="text-sm text-gray-800 mt-1">{selectedLog.log.actor_username || '-'}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Entity</div>
                  <div className="text-sm text-gray-800 mt-1 break-all">{selectedLog.entityLabel}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">IP</div>
                  <div className="text-sm text-gray-800 mt-1 break-all">{selectedLog.log.ip || '-'}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Undo</div>
                  <div className="text-sm text-gray-800 mt-1">{selectedLog.undoLabel}</div>
                </div>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Metadata Highlights</h3>
                {selectedLog.summary.keyFacts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedLog.summary.keyFacts.map((fact, idx) => (
                      <div key={`${fact.label}-${idx}`} className="text-sm text-gray-700">
                        <span className="font-medium text-gray-600">{fact.label}:</span>{' '}
                        <span>{fact.value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No highlight fields</div>
                )}
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Changed Fields</h3>
                {selectedLog.summary.diffKeys.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedLog.summary.diffKeys.map((key) => (
                      <span key={key} className="px-2 py-1 rounded text-xs bg-blue-50 border border-blue-200 text-blue-700">
                        {key}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No before/after diff data in this log</div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Before Snapshot</h3>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                    {selectedLog.metaObj?.before ? JSON.stringify(selectedLog.metaObj.before, null, 2) : 'No before snapshot'}
                  </pre>
                </div>
                <div className="rounded border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">After Snapshot</h3>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                    {selectedLog.metaObj?.after ? JSON.stringify(selectedLog.metaObj.after, null, 2) : 'No after snapshot'}
                  </pre>
                </div>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Raw Metadata JSON</h3>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-80 overflow-auto whitespace-pre-wrap break-all">
                  {selectedLog.summary.hasRawMeta ? JSON.stringify(selectedLog.metaObj, null, 2) : 'No metadata'}
                </pre>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function HelpNotesManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);

  const load = React.useCallback(async () => {
    try { setLoading(true); const rows = await request('/help'); setTickets(Array.isArray(rows) ? rows : []); }
    catch { addToast('Failed to load tickets','error'); }
    finally { setLoading(false);} 
  }, [request, addToast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try {
      const preset = sessionStorage.getItem('pv_admin_tickets_status');
      if (preset) {
        setStatusFilter(preset);
        sessionStorage.removeItem('pv_admin_tickets_status');
      }
    } catch {}
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await request(`/help/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      addToast(status === 'closed' ? 'Ticket closed' : 'Ticket reopened', 'success');
      if (selected?.id === id) setSelected(prev => prev ? { ...prev, status } : prev);
      load();
    } catch { addToast('Failed to update ticket', 'error'); }
  };

  const filtered = tickets.filter(t => statusFilter === 'all' ? true : (t.status || 'open') === statusFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600">Open and manage customer support tickets</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Status</label>
          <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6">Loading.</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No tickets found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{t.name} <span className="text-gray-500">({t.email || t.contact || '-'})</span></td>
                    <td className="px-4 py-2 text-sm">{t.topic || '-'}</td>
                    <td className="px-4 py-2 text-sm max-w-[420px] truncate">{t.message}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ (t.status || 'open') === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800' }`}>
                        {t.status || 'open'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      <button onClick={() => setSelected(t)} className="px-3 py-1 rounded border mr-2">View</button>
                      {(t.status || 'open') === 'open' ? (
                        <button onClick={() => updateStatus(t.id, 'closed')} className="px-3 py-1 rounded border">Close</button>
                      ) : (
                        <button onClick={() => updateStatus(t.id, 'open')} className="px-3 py-1 rounded border">Reopen</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Ticket Details</h2>
                <p className="text-sm text-gray-500">ID: {selected.id}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700"><Icon.X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500">Customer</div>
                  <div className="text-gray-900">{selected.name || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Contact</div>
                  <div className="text-gray-900 flex items-center gap-2">
                    <span>{selected.email || selected.contact || '-'}</span>
                    {(selected.email || selected.contact) && (
                      <button onClick={() => { navigator.clipboard?.writeText(selected.email || selected.contact); addToast('Copied', 'success'); }} className="text-xs px-2 py-0.5 border rounded">Copy</button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Order</div>
                  <div className="text-gray-900">{selected.order_id || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className="text-gray-900">{selected.status || 'open'}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Topic</div>
                <div className="text-gray-900">{selected.topic || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Message</div>
                <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded border">{selected.message}</div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              <div className="text-xs text-gray-500">Created: {selected.created_at ? new Date(selected.created_at).toLocaleString() : '-'}</div>
              <div className="flex gap-2">
                {(selected.status || 'open') === 'open' ? (
                  <button onClick={() => updateStatus(selected.id, 'closed')} className="px-4 py-2 bg-blue-600 text-white rounded">Close Ticket</button>
                ) : (
                  <button onClick={() => updateStatus(selected.id, 'open')} className="px-4 py-2 bg-blue-600 text-white rounded">Reopen Ticket</button>
                )}
                <button onClick={() => setSelected(null)} className="px-4 py-2 border rounded">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function BackupManager() {
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();
  const [backups, setBackups] = useState([]);
  const [localBackups, setLocalBackups] = useState([]);
  const [backupConfig, setBackupConfig] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [creating, setCreating] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('full');
  const [restoreUploads, setRestoreUploads] = useState(true);
  const [restoreScopes, setRestoreScopes] = useState(['products', 'orders', 'promotions', 'reviews', 'settings', 'audit', 'support', 'notifications']);
  const [restoring, setRestoring] = useState(false);
  const [operation, setOperation] = useState({
    active: false,
    label: '',
    received: 0,
    total: 0,
    indeterminate: false
  });

  const restoreScopeOptions = useMemo(() => ([
    { id: 'products', label: 'Products' },
    { id: 'orders', label: 'Orders' },
    { id: 'promotions', label: 'Promotions & banners' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'settings', label: 'Settings, customization, legal pages' },
    { id: 'audit', label: 'Audit logs' },
    { id: 'support', label: 'Help messages & newsletter' },
    { id: 'notifications', label: 'Notification logs' },
    { id: 'users', label: 'Admin/user accounts' }
  ]), []);

  const weekdayOptions = useMemo(() => ([
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ]), []);

  const showOperation = useCallback((label, { indeterminate = true } = {}) => {
    setOperation({
      active: true,
      label: String(label || 'Working...'),
      received: 0,
      total: 0,
      indeterminate
    });
  }, []);

  const updateOperation = useCallback((received, total) => {
    const r = Number(received || 0);
    const t = Number(total || 0);
    setOperation((prev) => ({
      ...prev,
      received: r,
      total: t,
      indeterminate: !(t > 0)
    }));
  }, []);

  const hideOperation = useCallback(() => {
    setOperation({ active: false, label: '', received: 0, total: 0, indeterminate: false });
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request('/backup/list');
      setBackups(data.backups || []);
      setConfigured(!data.message);
    } catch {
      addToast('Failed to load backups', 'error');
      setConfigured(false);
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  const loadBackupConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const data = await request('/backup/config');
      setBackupConfig(data?.config || null);
      setScheduler(data?.scheduler || null);
      setLocalBackups(Array.isArray(data?.localBackups) ? data.localBackups : []);
    } catch {
      addToast('Failed to load backup configuration', 'error');
    } finally {
      setConfigLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => {
    loadBackups();
    loadBackupConfig();
  }, [loadBackups, loadBackupConfig]);

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a new backup? This will upload your database and files to Google Drive.')) return;
    try {
      showOperation('Creating and uploading Google Drive backup...', { indeterminate: true });
      setCreating(true);
      await request('/backup/create', { method: 'POST' });
      addToast('Backup created successfully', 'success');
      await Promise.all([loadBackups(), loadBackupConfig()]);
    } catch (err) {
      addToast(err.message || 'Failed to create backup', 'error');
    } finally {
      setCreating(false);
      hideOperation();
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      showOperation(`Downloading ${fileName || 'backup'}...`, { indeterminate: true });
      await downloadFile(`/backup/download/${fileId}`, fileName || `backup_${Date.now()}.zip`, {
        method: 'POST',
        onProgress: (received, total) => updateOperation(received, total)
      });
      addToast('Backup downloaded', 'success');
    } catch {
      addToast('Failed to download backup', 'error');
    } finally {
      hideOperation();
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Delete backup: ${fileName}?`)) return;
    try {
      await request(`/backup/${fileId}`, { method: 'DELETE' });
      addToast('Backup deleted', 'success');
      await loadBackups();
    } catch {
      addToast('Failed to delete backup', 'error');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleLocalBackupDownload = async () => {
    try {
      showOperation('Preparing and downloading local backup...', { indeterminate: true });
      await downloadFile('/backup/local', `shopstarter_backup_${new Date().toISOString().split('T')[0]}.zip`, {
        onProgress: (received, total) => updateOperation(received, total)
      });
      addToast('Local backup downloaded successfully', 'success');
    } catch (err) {
      addToast('Failed to download local backup: ' + err.message, 'error');
    } finally {
      hideOperation();
    }
  };

  const toggleRestoreScope = useCallback((scopeId) => {
    setRestoreScopes((prev) => (
      prev.includes(scopeId)
        ? prev.filter((item) => item !== scopeId)
        : [...prev, scopeId]
    ));
  }, []);

  const canSubmitSelectiveRestore = restoreMode === 'full' || restoreScopes.length > 0 || restoreUploads;

  const handleRestoreSubmit = async (e) => {
    e.preventDefault();
    if (!restoreFile) return;
    if (!canSubmitSelectiveRestore) {
      addToast('Select at least one restore scope or media files', 'error');
      return;
    }
    const fullRestore = restoreMode === 'full';
    const confirmText = fullRestore
      ? 'WARNING: Full restore will overwrite all current data and media. Continue?'
      : 'WARNING: Selective restore will overwrite selected sections. Continue?';
    if (!window.confirm(confirmText)) return;

    try {
      showOperation(fullRestore ? 'Restoring full backup...' : 'Restoring selected scopes...', { indeterminate: true });
      setRestoring(true);
      const formData = new FormData();
      formData.append('backup', restoreFile);
      formData.append('mode', restoreMode);
      formData.append('scopes', JSON.stringify(restoreScopes));
      formData.append('restoreUploads', String(restoreUploads));
      await request('/backup/restore', { method: 'POST', body: formData });
      addToast('Backup restored successfully! Reloading...', 'success');
      setRestoreModalOpen(false);
      setRestoreFile(null);
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      addToast(err.message || 'Failed to restore backup', 'error');
    } finally {
      setRestoring(false);
      hideOperation();
    }
  };

  const handleSaveConfig = async () => {
    if (!backupConfig) return;
    try {
      setSavingConfig(true);
      const data = await request('/backup/config', {
        method: 'PUT',
        body: JSON.stringify(backupConfig)
      });
      setBackupConfig(data?.config || backupConfig);
      addToast('Backup schedule updated', 'success');
      await loadBackupConfig();
    } catch (err) {
      addToast(err.message || 'Failed to save backup schedule', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const operationPercent = operation.total > 0
    ? Math.max(0, Math.min(100, Math.round((operation.received / operation.total) * 100)))
    : 0;

  const setLocalConfigField = (field, value) => {
    setBackupConfig((prev) => ({
      ...(prev || {}),
      localSchedule: {
        ...(prev?.localSchedule || {}),
        [field]: value
      }
    }));
  };

  const setGoogleConfigField = (field, value) => {
    setBackupConfig((prev) => ({
      ...(prev || {}),
      googleSchedule: {
        ...(prev?.googleSchedule || {}),
        [field]: value
      }
    }));
  };

  const formatScheduleTime = (hour, minute) => {
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');
    return `${h}:${m}`;
  };

  const schedulerLastRun = scheduler?.lastRunAt ? new Date(scheduler.lastRunAt).toLocaleString() : 'Not run yet';

  if (configLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
        <p className="text-sm text-gray-600 mt-2">Loading backup tools...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-gray-600 mt-1">Comprehensive backup, scoped restore, and automated retention</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full md:w-auto">
          <button
            onClick={handleLocalBackupDownload}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 justify-center"
          >
            <Icon.Download className="w-4 h-4" />
            Download Local
          </button>
          <button
            onClick={() => setRestoreModalOpen(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition flex items-center gap-2 justify-center"
          >
            <Icon.Upload className="w-4 h-4" />
            Restore
          </button>
          <button
            onClick={handleCreateBackup}
            disabled={creating || !configured}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
          >
            {creating ? <Icon.Loading className="w-4 h-4" /> : <Icon.Upload className="w-4 h-4" />}
            {creating ? 'Creating...' : 'Google Backup'}
          </button>
        </div>
      </div>

      {operation.active && (
        <div className="bg-white rounded-lg shadow p-4 border border-blue-100">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-gray-900">{operation.label}</div>
            <div className="text-xs text-gray-500">
              {operation.indeterminate ? 'Working...' : `${operationPercent}%`}
            </div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              className={`h-full rounded-full ${operation.indeterminate ? 'animate-pulse bg-blue-500 w-1/2' : 'bg-blue-600'}`}
              style={operation.indeterminate ? undefined : { width: `${operationPercent}%` }}
            />
          </div>
          {!operation.indeterminate && operation.total > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              {formatBytes(operation.received)} / {formatBytes(operation.total)}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Local VPS Automation</h2>
            <p className="text-sm text-gray-600 mt-1">Auto backup to local VPS storage with retention trimming.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.localSchedule?.enabled}
              onChange={(e) => setLocalConfigField('enabled', e.target.checked)}
            />
            Enable scheduled local backups
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Day</label>
              <select
                value={Number(backupConfig?.localSchedule?.dayOfWeek ?? 0)}
                onChange={(e) => setLocalConfigField('dayOfWeek', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                {weekdayOptions.map((day) => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hour (24h)</label>
              <input
                type="number"
                min="0"
                max="23"
                value={Number(backupConfig?.localSchedule?.hour ?? 5)}
                onChange={(e) => setLocalConfigField('hour', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Minute</label>
              <input
                type="number"
                min="0"
                max="59"
                value={Number(backupConfig?.localSchedule?.minute ?? 0)}
                onChange={(e) => setLocalConfigField('minute', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Keep last local backups</label>
            <input
              type="number"
              min="1"
              max="500"
              value={Number(backupConfig?.localSchedule?.keepLast ?? 10)}
              onChange={(e) => setLocalConfigField('keepLast', Number(e.target.value))}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3">
            Next rule: every <strong>{weekdayOptions.find((d) => d.value === Number(backupConfig?.localSchedule?.dayOfWeek ?? 0))?.label || 'Sunday'}</strong> at <strong>{formatScheduleTime(Number(backupConfig?.localSchedule?.hour ?? 5), Number(backupConfig?.localSchedule?.minute ?? 0))}</strong>.
            Local snapshots on VPS: <strong>{localBackups.length}</strong>.
          </div>
          {localBackups.length > 0 && (
            <div className="border rounded-md p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Recent local backups</div>
              <div className="space-y-2">
                {localBackups.slice(0, 5).map((entry) => (
                  <div key={entry.name} className="text-xs text-gray-600 flex items-center justify-between gap-2">
                    <span className="truncate">{entry.name}</span>
                    <span className="shrink-0">{formatBytes(entry.size)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Google Drive Automation</h2>
            <p className="text-sm text-gray-600 mt-1">Optional cloud copy of scheduled backups.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.googleSchedule?.enabled}
              onChange={(e) => setGoogleConfigField('enabled', e.target.checked)}
            />
            Enable Google upload for scheduled runs
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.googleSchedule?.keepAll}
              onChange={(e) => setGoogleConfigField('keepAll', e.target.checked)}
            />
            Keep all Google backups (default)
          </label>
          {!backupConfig?.googleSchedule?.keepAll && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keep last Google backups</label>
              <input
                type="number"
                min="1"
                max="500"
                value={Number(backupConfig?.googleSchedule?.keepLast ?? 50)}
                onChange={(e) => setGoogleConfigField('keepLast', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}
          {!configured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Google Drive is not configured. Add credentials.json and token.json in backend to enable cloud backup.
            </div>
          )}
          <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3">
            Scheduler status: <strong>{scheduler?.running ? 'Running' : 'Idle'}</strong><br />
            Last run: <strong>{schedulerLastRun}</strong>
            {scheduler?.lastError && (
              <>
                <br />
                Last error: <strong className="text-red-600">{scheduler.lastError}</strong>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveConfig}
          disabled={savingConfig || !backupConfig}
          className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-black disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {savingConfig && <Icon.Loading className="w-4 h-4" />}
          {savingConfig ? 'Saving...' : 'Save Backup Settings'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Google Drive Backups</h2>
            <p className="text-sm text-gray-600">Manual and scheduled cloud backups.</p>
          </div>
          <button
            onClick={loadBackups}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
            type="button"
          >
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="text-center py-10">
            <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
            <p className="text-gray-600 mt-2">Loading backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div className="text-sm text-gray-500 py-8 text-center">No Google backups found.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {backups.map((backup) => (
              <div key={backup.id} className="border rounded-lg p-4 bg-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 break-all">{backup.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {formatBytes(backup.size)} · {new Date(backup.createdTime).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleDownload(backup.id, backup.name)}
                    className="px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50"
                    type="button"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => handleDelete(backup.id, backup.name)}
                    className="px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-sm hover:bg-red-50"
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Backup Scope</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Backups package the full database plus all uploaded media files.</li>
          <li>• Full restore can recover a blank instance to prior state in one operation.</li>
          <li>• Selective restore can overwrite only chosen scopes (orders/products/reviews/etc.).</li>
          <li>• Every restore creates an automatic safety backup first.</li>
        </ul>
      </div>

      {restoreModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
          onClick={() => setRestoreModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Restore Backup</h2>
              <button onClick={() => setRestoreModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleRestoreSubmit} className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                Restore overwrites existing data in selected scopes. A safety backup is created before restore.
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Restore mode</label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="full"
                    checked={restoreMode === 'full'}
                    onChange={(e) => {
                      const next = e.target.value;
                      setRestoreMode(next);
                      if (next === 'full') setRestoreUploads(true);
                    }}
                  />
                  Full site restore (all tables + uploads)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="selective"
                    checked={restoreMode === 'selective'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                  />
                  Selective restore (choose sections)
                </label>
              </div>

              {restoreMode === 'selective' && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-800">Selectable scopes</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {restoreScopeOptions.map((scope) => (
                      <label key={scope.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={restoreScopes.includes(scope.id)}
                          onChange={() => toggleRestoreScope(scope.id)}
                        />
                        {scope.label}
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 pt-1 border-t">
                    <input
                      type="checkbox"
                      checked={restoreUploads}
                      onChange={(e) => setRestoreUploads(e.target.checked)}
                    />
                    Restore uploaded media files
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Backup File (.zip)
                </label>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setRestoreFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setRestoreModalOpen(false);
                    setRestoreFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={restoring}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!restoreFile || restoring || !canSubmitSelectiveRestore}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {restoring ? (
                    <>
                      <Icon.Loading className="w-4 h-4" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <Icon.Upload className="w-4 h-4" />
                      Restore Backup
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
function LegalPagesManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [formData, setFormData] = useState({
    page_key: '',
    title: '',
    content: '',
    active: true
  });

  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request('/legal-pages');
      setPages(data);
    } catch {
      addToast('Failed to load legal pages', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await request(`/legal-pages/${editingPage.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
          headers: { 'Content-Type': 'application/json' }
        });
        addToast('Legal page updated', 'success');
      } else {
        await request('/legal-pages', {
          method: 'POST',
          body: JSON.stringify(formData),
          headers: { 'Content-Type': 'application/json' }
        });
        addToast('Legal page created', 'success');
      }
      setShowForm(false);
      setEditingPage(null);
      setFormData({ page_key: '', title: '', content: '', active: true });
      loadPages();
    } catch {
      addToast('Failed to save legal page', 'error');
    }
  };

  const handleEdit = (page) => {
    const next = {
      page_key: page.page_key,
      title: page.title,
      content: page.content,
      active: page.active
    };
    setEditingPage(page);
    setFormData(next);
    setInitialFormData(next);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this legal page?')) return;
    try {
      await request(`/legal-pages/${id}`, { method: 'DELETE' });
      addToast('Legal page deleted', 'success');
      loadPages();
    } catch {
      addToast('Failed to delete', 'error');
    }
  };

  const handleNewPage = () => {
    const next = { page_key: '', title: '', content: '', active: true };
    setEditingPage(null);
    setFormData(next);
    setInitialFormData(next);
    setShowForm(true);
  };

  const closeForm = useCallback(() => {
    const isDirty = showForm && initialFormData ? hasFormChanges(formData, initialFormData) : false;
    if (!confirmDiscardChanges(isDirty)) return;
    setShowForm(false);
  }, [showForm, formData, initialFormData]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Legal Pages</h1>
          <p className="text-gray-600">Manage privacy policy, terms, refund & shipping policies</p>
        </div>
        <button
          onClick={handleNewPage}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Icon.Plus className="w-4 h-4 inline mr-2" />
          Add Legal Page
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pages.map(page => (
                <tr key={page.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{page.page_key}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{page.title}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${page.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {page.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button onClick={() => handleEdit(page)} className="text-blue-600 hover:text-blue-800">Edit</button>
                    <button onClick={() => handleDelete(page.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
              {pages.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    No legal pages yet. Click "Add Legal Page" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">{editingPage ? 'Edit Legal Page' : 'New Legal Page'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Key</label>
                <input
                  type="text"
                  required
                  value={formData.page_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, page_key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., privacy-policy, terms-conditions"
                  disabled={!!editingPage}
                />
                <p className="text-xs text-gray-500 mt-1">URL slug for this page (cannot be changed after creation)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Privacy Policy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  required
                  rows={15}
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  placeholder="HTML content for the legal page..."
                />
                <p className="text-xs text-gray-500 mt-1">Supports HTML formatting</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  id="active-checkbox"
                />
                <label htmlFor="active-checkbox" className="text-sm font-medium text-gray-700">Active (visible to users)</label>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingPage ? 'Update' : 'Create'} Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


function MediaLibrary() {
  const { request } = useApi();
  const auth = useAuth();
  const { addToast } = useToasts();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [viewMode, setViewMode] = useState('list');

  const [dragActive, setDragActive] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);

  const [uploadDedupeMode, setUploadDedupeMode] = useState('reuse');
  const [uploadOptimizeMode, setUploadOptimizeMode] = useState('off');
  const [showLibrarySettings, setShowLibrarySettings] = useState(false);

  const [dialog, setDialog] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);

  const fileInputRef = useRef(null);
  const offsetRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchTerm, 220);
  const MEDIA_PAGE_SIZE = 120;

  const getEntryPath = useCallback((entry) => String(entry?.path || entry?.name || ''), []);
  const encodeMediaPath = useCallback((relPath) => String(relPath || '').split('/').map((part) => encodeURIComponent(part)).join('/'), []);

  const formatFileSize = useCallback((bytes) => {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024;
      unit += 1;
    }
    return `${Math.round(size * 100) / 100} ${units[unit]}`;
  }, []);

  const formatDate = useCallback((epochMs) => {
    const value = Number(epochMs || 0);
    if (!Number.isFinite(value) || value <= 0) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return '-';
    }
  }, []);

  const getEntryKind = useCallback((entry) => {
    if (!entry || entry.type === 'dir') return 'folder';
    const backendType = String(entry.media_type || '').toLowerCase();
    if (['image', 'video', 'audio', 'model', 'document'].includes(backendType)) return backendType;
    const inferred = getMediaKind(entry.url || entry.name || '');
    if (['image', 'video', 'audio', 'model', 'document'].includes(inferred)) return inferred;
    return 'other';
  }, []);

  const getCopyPathSuggestion = useCallback((inputPath) => {
    const fullPath = String(inputPath || '').trim();
    if (!fullPath) return '';
    const slashIndex = fullPath.lastIndexOf('/');
    const dirPrefix = slashIndex >= 0 ? `${fullPath.slice(0, slashIndex + 1)}` : '';
    const leaf = slashIndex >= 0 ? fullPath.slice(slashIndex + 1) : fullPath;
    const dotIndex = leaf.lastIndexOf('.');
    if (dotIndex > 0) {
      return `${dirPrefix}${leaf.slice(0, dotIndex)}-copy${leaf.slice(dotIndex)}`;
    }
    return `${dirPrefix}${leaf}-copy`;
  }, []);

  const load = React.useCallback(async ({ reset = true } = {}) => {
    const nextOffset = reset ? 0 : offsetRef.current;
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams();
      params.set('includeMeta', '1');
      params.set('limit', String(MEDIA_PAGE_SIZE));
      params.set('offset', String(nextOffset));
      params.set('sort', sortBy || 'name_asc');
      if (currentPath) params.set('path', currentPath);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (typeFilter) params.set('type', typeFilter);

      const data = await request(`/media?${params.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
      const nextHasMore = typeof data?.hasMore === 'boolean'
        ? data.hasMore
        : (items.length >= MEDIA_PAGE_SIZE);

      offsetRef.current = nextOffset + items.length;
      setFiles((prev) => reset ? items : [...prev, ...items]);
      setTotalEntries(total);
      setHasMore(nextHasMore);
    } catch {
      addToast('Failed to load media', 'error');
      if (reset) {
        setFiles([]);
        setTotalEntries(0);
        setHasMore(false);
      }
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, [request, addToast, currentPath, debouncedSearch, typeFilter, sortBy]);

  useEffect(() => {
    offsetRef.current = 0;
    load({ reset: true });
  }, [load]);

  useEffect(() => {
    const available = new Set((files || []).map(getEntryPath));
    setSelectedPaths((prev) => prev.filter((pathValue) => available.has(pathValue)));
  }, [files, getEntryPath]);

  const copyUrl = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      addToast('URL copied to clipboard', 'success');
    } catch {
      addToast('Failed to copy URL', 'error');
    }
  };

  const selectedEntries = useMemo(() => {
    const selected = new Set(selectedPaths);
    return (files || []).filter((entry) => selected.has(getEntryPath(entry)));
  }, [files, selectedPaths, getEntryPath]);

  const singleSelectedEntry = selectedEntries.length === 1 ? selectedEntries[0] : null;
  const singleSelectedPath = singleSelectedEntry ? getEntryPath(singleSelectedEntry) : '';
  const singleSelectedKind = singleSelectedEntry ? getEntryKind(singleSelectedEntry) : '';
  const canOptimizeSelection = !!(
    singleSelectedEntry &&
    singleSelectedEntry.type !== 'dir' &&
    singleSelectedKind === 'image'
  );

  const toggleSelection = useCallback((entryPath, forceChecked) => {
    const pathValue = String(entryPath || '');
    if (!pathValue) return;
    setSelectedPaths((prev) => {
      const exists = prev.includes(pathValue);
      const shouldInclude = typeof forceChecked === 'boolean' ? forceChecked : !exists;
      if (shouldInclude && !exists) return [...prev, pathValue];
      if (!shouldInclude && exists) return prev.filter((item) => item !== pathValue);
      return prev;
    });
  }, []);

  const openEntry = useCallback((entry) => {
    if (!entry) return;
    if (entry.type === 'dir') {
      setCurrentPath(getEntryPath(entry));
      setSelectedPaths([]);
      return;
    }
    setPreviewItem(entry);
  }, [getEntryPath]);

  const handleEntryClick = useCallback((entry, event) => {
    const pathValue = getEntryPath(entry);
    if (!pathValue) return;
    if (event?.metaKey || event?.ctrlKey) {
      toggleSelection(pathValue);
      return;
    }
    setSelectedPaths([pathValue]);
  }, [getEntryPath, toggleSelection]);

  const deleteSelection = useCallback(async () => {
    if (!selectedPaths.length) return;
    if (!window.confirm(`Delete ${selectedPaths.length} selected item(s)?`)) return;
    try {
      const results = await Promise.allSettled(
        selectedPaths.map((pathValue) => request(`/media/${encodeMediaPath(pathValue)}`, { method: 'DELETE' }))
      );
      const success = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results.length - success;
      if (success > 0) addToast(`Deleted ${success} item(s)`, 'success');
      if (failed > 0) addToast(`${failed} item(s) failed to delete`, 'error');
      setSelectedPaths([]);
      load({ reset: true });
    } catch {
      addToast('Failed to delete selection', 'error');
    }
  }, [selectedPaths, request, encodeMediaPath, addToast, load]);

  const emptyTrash = useCallback(async () => {
    if (!window.confirm('Permanently delete all files currently in the media trash?')) return;
    try {
      const result = await request('/media-trash', { method: 'DELETE' });
      const removedCount = Number(result?.removedCount || 0);
      addToast(removedCount > 0 ? `Emptied trash (${removedCount} item${removedCount === 1 ? '' : 's'})` : 'Trash was already empty', 'success');
      load({ reset: true });
    } catch (err) {
      addToast(err.message || 'Failed to empty trash', 'error');
    }
  }, [request, addToast, load]);

  const openDialog = useCallback((nextDialog) => {
    setDialogBusy(false);
    setDialog(nextDialog);
  }, []);

  const closeDialog = useCallback(() => {
    if (dialogBusy) return;
    setDialog(null);
  }, [dialogBusy]);

  const openNewFolderDialog = useCallback(() => {
    openDialog({
      type: 'mkdir',
      value: currentPath ? `${currentPath}/new-folder` : 'new-folder'
    });
  }, [currentPath, openDialog]);

  const openRenameDialog = useCallback(() => {
    if (!singleSelectedEntry) return;
    openDialog({
      type: 'rename',
      from: singleSelectedPath,
      value: singleSelectedPath
    });
  }, [singleSelectedEntry, singleSelectedPath, openDialog]);

  const openCopyDialog = useCallback(() => {
    if (!singleSelectedEntry || singleSelectedEntry.type === 'dir') return;
    openDialog({
      type: 'copy',
      from: singleSelectedPath,
      value: getCopyPathSuggestion(singleSelectedPath)
    });
  }, [singleSelectedEntry, singleSelectedPath, getCopyPathSuggestion, openDialog]);

  const openOptimizeDialog = useCallback(() => {
    if (!canOptimizeSelection || !singleSelectedEntry) return;
    openDialog({
      type: 'optimize',
      path: singleSelectedPath,
      maxWidth: 1920,
      quality: 84,
      format: 'keep',
      replace: false,
      onlyIfSmaller: true
    });
  }, [canOptimizeSelection, singleSelectedEntry, singleSelectedPath, openDialog]);

  const submitDialog = useCallback(async (event) => {
    event.preventDefault();
    if (!dialog) return;
    try {
      setDialogBusy(true);
      if (dialog.type === 'mkdir') {
        const name = String(dialog.value || '').trim();
        if (!name) throw new Error('Folder path is required');
        await request('/media/mkdir', {
          method: 'POST',
          body: JSON.stringify({ name })
        });
        addToast('Folder created', 'success');
      } else if (dialog.type === 'rename') {
        const from = String(dialog.from || '').trim();
        const to = String(dialog.value || '').trim();
        if (!from || !to) throw new Error('Source and destination are required');
        await request('/media/rename', {
          method: 'POST',
          body: JSON.stringify({ from, to })
        });
        setSelectedPaths([to]);
        addToast('Updated path', 'success');
      } else if (dialog.type === 'copy') {
        const from = String(dialog.from || '').trim();
        const to = String(dialog.value || '').trim();
        if (!from || !to) throw new Error('Source and destination are required');
        await request('/media/copy', {
          method: 'POST',
          body: JSON.stringify({ from, to })
        });
        setSelectedPaths([to]);
        addToast('Copy created', 'success');
      } else if (dialog.type === 'optimize') {
        const targetPath = String(dialog.path || '').trim();
        if (!targetPath) throw new Error('File path is required');
        const payload = await request('/media/optimize', {
          method: 'POST',
          body: JSON.stringify({
            path: targetPath,
            maxWidth: Number(dialog.maxWidth || 1920),
            quality: Number(dialog.quality || 84),
            format: String(dialog.format || 'keep'),
            replace: !!dialog.replace,
            onlyIfSmaller: !!dialog.onlyIfSmaller
          })
        });
        if (payload?.skipped) {
          addToast('Optimization skipped because output was not smaller', 'info');
        } else {
          addToast(`Optimized successfully (saved ${formatFileSize(payload?.savedBytes || 0)})`, 'success');
          if (payload?.file?.path) setSelectedPaths([payload.file.path]);
        }
      }
      setDialog(null);
      load({ reset: true });
    } catch (err) {
      addToast(err?.message || 'Action failed', 'error');
    } finally {
      setDialogBusy(false);
    }
  }, [dialog, request, addToast, formatFileSize, load]);

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    setUploadProgress(0);

    const fd = new FormData();
    Array.from(fileList).forEach((file) => fd.append('files', file));
    fd.append('dedupe', uploadDedupeMode);
    fd.append('optimize', uploadOptimizeMode);

    try {
      const responseText = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) return;
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText || '{}');
          } else {
            let message = 'Upload failed';
            try {
              const parsed = JSON.parse(xhr.responseText || '{}');
              if (parsed?.error) message = parsed.error;
            } catch {}
            reject(new Error(message));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', '/shopapi/media');
        xhr.withCredentials = true;
        try {
          const headers = auth?.getAuthHeaders ? auth.getAuthHeaders() : {};
          if (currentPath) headers['X-Upload-Folder'] = currentPath;
          Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        } catch {}
        xhr.send(fd);
      });

      let payload = {};
      try {
        payload = JSON.parse(responseText || '{}');
      } catch {}

      const count = Number(payload?.count || fileList.length);
      const duplicateCount = Number(payload?.duplicateCount || 0);
      if (duplicateCount > 0) {
        addToast(`Uploaded ${count} item(s). ${duplicateCount} duplicate(s) reused.`, 'success');
      } else {
        addToast(`Uploaded ${count} item(s).`, 'success');
      }
      await load({ reset: true });
    } catch (err) {
      addToast(err?.message || 'Failed to upload', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      uploadFiles(event.dataTransfer.files);
    }
  };

  const visibleEntries = useMemo(() => files || [], [files]);

  const breadcrumbs = useMemo(() => {
    const segments = currentPath ? currentPath.split('/').filter(Boolean) : [];
    let cursor = '';
    return segments.map((segment) => {
      cursor = cursor ? `${cursor}/${segment}` : segment;
      return { name: segment, path: cursor };
    });
  }, [currentPath]);

  const folderCount = useMemo(() => files.filter((entry) => entry.type === 'dir').length, [files]);
  const fileCount = useMemo(() => files.filter((entry) => entry.type !== 'dir').length, [files]);
  const totalFileBytes = useMemo(() => files.filter((entry) => entry.type !== 'dir').reduce((sum, entry) => sum + Number(entry.size || 0), 0), [files]);

  const renderThumb = (entry, sizeClass = 'h-8 w-8') => {
    if (entry.type === 'dir') {
      return (
        <div className={`${sizeClass} rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700`}>
          <Icon.Folder className="w-4 h-4" />
        </div>
      );
    }

    const kind = getEntryKind(entry);
    if (kind === 'video') {
      return (
        <div className={`${sizeClass} rounded border bg-gray-100 text-gray-700 text-[10px] font-semibold flex items-center justify-center`}>
          VIDEO
        </div>
      );
    }

    if (kind === 'image') {
      const src = resolveMediaUrl(entry.url);
      return (
        <img
          src={src}
          alt={entry.name}
          className={`${sizeClass} rounded object-cover bg-gray-100`}
          loading="lazy"
          decoding="async"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
      );
    }

    if (kind === 'audio') {
      return <div className={`${sizeClass} rounded border bg-purple-50 text-purple-700 text-[10px] font-semibold flex items-center justify-center`}>AUDIO</div>;
    }

    if (kind === 'document') {
      return <div className={`${sizeClass} rounded border bg-blue-50 text-blue-700 text-[10px] font-semibold flex items-center justify-center`}>DOC</div>;
    }

    if (kind === 'model') {
      return <div className={`${sizeClass} rounded border bg-gray-100 text-gray-700 text-[10px] font-semibold flex items-center justify-center`}>3D</div>;
    }

    return <div className={`${sizeClass} rounded border bg-gray-100 text-gray-500 text-[10px] font-semibold flex items-center justify-center`}>FILE</div>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="text-gray-600">Drive-style asset manager with folder control, dedupe, and optional optimization.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              offsetRef.current = 0;
              load({ reset: true });
            }}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowLibrarySettings(true)}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
          >
            Settings
          </button>
          <button
            type="button"
            onClick={emptyTrash}
            className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-sm text-red-700 hover:bg-red-100"
          >
            Empty Trash
          </button>
          <button
            type="button"
            onClick={openNewFolderDialog}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
          >
            New Folder
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click?.()}
            disabled={uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 inline-flex items-center gap-2"
          >
            <Icon.Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept="*/*"
            onChange={(event) => uploadFiles(event.target.files)}
            disabled={uploading}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                setCurrentPath('');
                setSelectedPaths([]);
              }}
              className={`px-2 py-1 rounded ${currentPath ? 'text-blue-700 hover:bg-blue-50' : 'bg-blue-50 text-blue-700 font-semibold'}`}
            >
              Uploads
            </button>
            {breadcrumbs.map((crumb) => (
              <React.Fragment key={crumb.path}>
                <span className="text-gray-400">/</span>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPath(crumb.path);
                    setSelectedPaths([]);
                  }}
                  className={`px-2 py-1 rounded ${crumb.path === currentPath ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-blue-700 hover:bg-blue-50'}`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
            <button
              type="button"
              onClick={() => {
                if (!currentPath) return;
                const upPath = currentPath.split('/').slice(0, -1).join('/');
                setCurrentPath(upPath);
                setSelectedPaths([]);
              }}
              disabled={!currentPath}
              className="ml-auto px-3 py-1.5 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Up
            </button>
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center gap-2">
            <div className="flex-1 flex items-center gap-2">
              <Icon.Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or path..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Types</option>
              <option value="folders">Folders</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="model">3D Models</option>
              <option value="document">Documents</option>
              <option value="other">Other Files</option>
            </select>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="name_asc">Name (A-Z)</option>
              <option value="name_desc">Name (Z-A)</option>
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="size_desc">Largest</option>
              <option value="size_asc">Smallest</option>
            </select>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white hover:bg-gray-50'}`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white hover:bg-gray-50'}`}
              >
                Grid
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 text-sm">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-500">Current folder</div>
              <div className="font-medium text-gray-800 truncate">{currentPath || '/'}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-500">Folder summary</div>
              <div className="font-medium text-gray-800">{folderCount} folder(s), {fileCount} file(s) shown</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Total matching items: {totalEntries}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-500">Storage in this folder</div>
              <div className="font-medium text-gray-800">{formatFileSize(totalFileBytes)}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div
            className={`p-4 border-r border-gray-200 min-h-[420px] ${dragActive ? 'bg-blue-50' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading && (
              <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                <div className="flex items-center justify-between text-xs text-blue-900 mb-1">
                  <span>Uploading files</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-blue-100 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-16">
                <Icon.Loading className="w-7 h-7 mx-auto text-blue-600" />
                <p className="text-gray-600 mt-2">Loading media...</p>
              </div>
            ) : visibleEntries.length === 0 ? (
              <div className="text-center py-16 text-gray-500 border-2 border-dashed rounded-lg">
                <p className="font-medium">No items found</p>
                <p className="text-sm mt-1">{searchTerm ? 'Try another search or filter.' : 'Upload files or create a folder to get started.'}</p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left"></th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Size</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Modified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {visibleEntries.map((entry) => {
                      const pathValue = getEntryPath(entry);
                      const selected = selectedPaths.includes(pathValue);
                      const kind = getEntryKind(entry);
                      return (
                        <tr
                          key={pathValue}
                          onClick={(event) => handleEntryClick(entry, event)}
                          onDoubleClick={() => openEntry(entry)}
                          className={`cursor-pointer ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                          title={entry.type === 'dir' ? 'Double click to open folder' : 'Double click to preview file'}
                        >
                          <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(event) => toggleSelection(pathValue, event.target.checked)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 min-w-[240px]">
                              {renderThumb(entry)}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{entry.name}</div>
                                <div className="text-xs text-gray-500 truncate">{pathValue}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-700 capitalize">{entry.type === 'dir' ? 'Folder' : kind}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{entry.type === 'dir' ? '-' : formatFileSize(entry.size)}</td>
                          <td className="px-3 py-2 text-gray-700">{formatDate(entry.modified_at || entry.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3">
                {visibleEntries.map((entry) => {
                  const pathValue = getEntryPath(entry);
                  const selected = selectedPaths.includes(pathValue);
                  const kind = getEntryKind(entry);
                  return (
                    <div
                      key={pathValue}
                      onClick={(event) => handleEntryClick(entry, event)}
                      onDoubleClick={() => openEntry(entry)}
                      className={`border rounded-lg p-2 cursor-pointer transition ${
                        selected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200 hover:shadow-sm'
                      }`}
                      title={entry.type === 'dir' ? 'Double click to open folder' : 'Double click to preview file'}
                    >
                      <div className="h-28 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                        {entry.type === 'dir' ? (
                          <Icon.Folder className="w-10 h-10 text-amber-500" />
                        ) : (
                          renderThumb(entry, 'h-full w-full')
                        )}
                      </div>
                      <div className="pt-2">
                        <div className="font-medium text-sm text-gray-900 truncate">{entry.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{entry.type === 'dir' ? 'Folder' : kind}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hasMore && (
              <div className="pt-3 text-center">
                <button
                  type="button"
                  onClick={() => load({ reset: false })}
                  disabled={loadingMore}
                  className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading more...' : `Load more (${visibleEntries.length}/${totalEntries})`}
                </button>
              </div>
            )}
          </div>

          <aside className="p-4 space-y-4 border-t xl:border-t-0">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-900">Selection</div>
                <div className="text-xs text-gray-500">{selectedEntries.length} selected</div>
              </div>

              {selectedEntries.length === 0 ? (
                <div className="text-sm text-gray-500">Select an item. Double click opens folders or previews files.</div>
              ) : (
                <div className="space-y-3">
                  {singleSelectedEntry ? (
                    <div className="rounded border border-gray-200 bg-gray-50 p-2">
                      <div className="flex items-center gap-2 mb-2">
                        {renderThumb(singleSelectedEntry)}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{singleSelectedEntry.name}</div>
                          <div className="text-xs text-gray-500 truncate">{singleSelectedPath}</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Type: {singleSelectedEntry.type === 'dir' ? 'folder' : singleSelectedKind}</div>
                        <div>Size: {singleSelectedEntry.type === 'dir' ? '-' : formatFileSize(singleSelectedEntry.size)}</div>
                        <div>Modified: {formatDate(singleSelectedEntry.modified_at || singleSelectedEntry.created_at)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600">Multi-select is active. Bulk delete is available.</div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => singleSelectedEntry && openEntry(singleSelectedEntry)}
                      disabled={!singleSelectedEntry}
                      className="px-2 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={() => singleSelectedEntry?.url && copyUrl(singleSelectedEntry.url)}
                      disabled={!singleSelectedEntry || singleSelectedEntry.type === 'dir'}
                      className="px-2 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Copy URL
                    </button>
                    <button
                      type="button"
                      onClick={openRenameDialog}
                      disabled={!singleSelectedEntry}
                      className="px-2 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Rename / Move
                    </button>
                    <button
                      type="button"
                      onClick={openCopyDialog}
                      disabled={!singleSelectedEntry || singleSelectedEntry.type === 'dir'}
                      className="px-2 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      onClick={openOptimizeDialog}
                      disabled={!canOptimizeSelection}
                      className="px-2 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Optimize
                    </button>
                    <button
                      type="button"
                      onClick={deleteSelection}
                      className="px-2 py-2 rounded border border-red-300 bg-red-50 text-red-700 text-sm hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {previewItem && (
        <MediaPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
      )}

      {showLibrarySettings && (
        <div className="fixed inset-0 z-[240] bg-black/45 flex items-center justify-center p-4" onClick={() => setShowLibrarySettings(false)}>
          <div className="w-full max-w-xl bg-white rounded-xl shadow-xl border border-gray-200" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Media Library Settings</h3>
              <button
                type="button"
                onClick={() => setShowLibrarySettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon.X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-900 mb-3">Upload Defaults</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duplicate handling</label>
                    <select
                      value={uploadDedupeMode}
                      onChange={(event) => setUploadDedupeMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="reuse">Reuse identical files (recommended)</option>
                      <option value="allow">Allow duplicates</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Auto optimize on upload</label>
                    <select
                      value={uploadOptimizeMode}
                      onChange={(event) => setUploadOptimizeMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="off">Off (keep original quality)</option>
                      <option value="auto">Auto (only large images)</option>
                      <option value="aggressive">Aggressive (smaller variants)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-900 mb-3">Library Preferences</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default view</label>
                    <select
                      value={viewMode}
                      onChange={(event) => setViewMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="list">List</option>
                      <option value="grid">Grid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default sort</label>
                    <select
                      value={sortBy}
                      onChange={(event) => setSortBy(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="name_asc">Name (A-Z)</option>
                      <option value="name_desc">Name (Z-A)</option>
                      <option value="recent">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="size_desc">Largest</option>
                      <option value="size_asc">Smallest</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Original files are preserved by default. Dedupe avoids storing identical copies.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setShowLibrarySettings(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-[250] bg-black/50 flex items-center justify-center p-4" onClick={closeDialog}>
          <form
            onSubmit={submitDialog}
            className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {dialog.type === 'mkdir' && 'Create Folder'}
                {dialog.type === 'rename' && 'Rename / Move'}
                {dialog.type === 'copy' && 'Duplicate File'}
                {dialog.type === 'optimize' && 'Optimize Image'}
              </h3>
              <button type="button" onClick={closeDialog} disabled={dialogBusy} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {(dialog.type === 'mkdir' || dialog.type === 'rename' || dialog.type === 'copy') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {dialog.type === 'mkdir' ? 'Folder path (relative to uploads)' : 'Target path (relative to uploads)'}
                  </label>
                  <input
                    type="text"
                    value={dialog.value || ''}
                    onChange={(event) => setDialog((prev) => ({ ...prev, value: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="products/new-folder"
                    autoFocus
                  />
                  {dialog.type !== 'mkdir' && (
                    <div className="text-xs text-gray-500 mt-1">From: {dialog.from}</div>
                  )}
                </div>
              )}

              {dialog.type === 'optimize' && (
                <div className="space-y-3">
                  <div className="text-xs text-gray-600">Path: {dialog.path}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max width</label>
                      <input
                        type="number"
                        min={320}
                        max={4096}
                        value={dialog.maxWidth ?? 1920}
                        onChange={(event) => setDialog((prev) => ({ ...prev, maxWidth: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quality</label>
                      <input
                        type="number"
                        min={45}
                        max={96}
                        value={dialog.quality ?? 84}
                        onChange={(event) => setDialog((prev) => ({ ...prev, quality: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Output format</label>
                    <select
                      value={dialog.format || 'keep'}
                      onChange={(event) => setDialog((prev) => ({ ...prev, format: event.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="keep">Keep existing format</option>
                      <option value="webp">WebP</option>
                      <option value="jpeg">JPEG</option>
                      <option value="png">PNG</option>
                      <option value="avif">AVIF</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!dialog.onlyIfSmaller}
                      onChange={(event) => setDialog((prev) => ({ ...prev, onlyIfSmaller: event.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    Save only if output is smaller
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!dialog.replace}
                      onChange={(event) => setDialog((prev) => ({ ...prev, replace: event.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    Replace original file
                  </label>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeDialog}
                disabled={dialogBusy}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={dialogBusy}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 inline-flex items-center gap-2"
              >
                {dialogBusy && <Icon.Loading className="w-4 h-4" />}
                {dialog.type === 'mkdir' && 'Create'}
                {dialog.type === 'rename' && 'Save'}
                {dialog.type === 'copy' && 'Copy'}
                {dialog.type === 'optimize' && 'Run'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


function SettingsManager({ notificationSoundPreference = DEFAULT_ADMIN_NOTIFICATION_SOUND, onNotificationSoundPreferenceChange, notificationSoundUrl = '', onNotificationSoundUrlChange, onPreviewNotificationSound, onFaviconUrlChange }) {
  const [settings, setSettings] = useState({
    siteName: '',
    siteTitle: DEFAULT_SITE_TITLE,
    tagline: DEFAULT_SITE_TAGLINE,
    currency: '৳',
    facebookUrl: '',
    instagramUrl: '',
    supportEmail: '',
    homeHeroDescription: DEFAULT_HOME_HERO_DESCRIPTION,
    shippingRates: { inside: 60, outside: 120 },
    bkashNumber: '',
    bkashQrUrl: '',
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20,
    orderIdPrefix: 'PV',
    orderIdSuffix: '',
    enableCampaignPopup: true,
    campaignPopupDelaySeconds: 3,
    notificationSoundUrl: '',
    logoUrl: '/logo.png',
    logoAccentUrl: '',
    faviconUrl: '',
    shareImageUrl: '',
    metaPixelId: DEFAULT_META_PIXEL_ID,
    accentColor: '#d3763b',
    heroBanners: [],
    homepageBanners: [],
    smsNotifications: DEFAULT_SMS_NOTIFICATIONS,
    demoMode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [restartingServices, setRestartingServices] = useState(false);
  const [restartConfirmText, setRestartConfirmText] = useState('');
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [selectedSmsLog, setSelectedSmsLog] = useState(null);
  const [smsActionLoading, setSmsActionLoading] = useState('');
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTestMessage, setSmsTestMessage] = useState(DEFAULT_SMS_TEMPLATES.confirmed);
  const [smsBalance, setSmsBalance] = useState(null);
  const [smsHelpDismissed, setSmsHelpDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SMS_HELP_DISMISSED_KEY) === 'true';
  });
  const smsTemplateRefs = useRef({});
  
  const { request } = useApi();
  const { addToast } = useToasts();
  const { user } = useAuth();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await request('/settings');
        const shippingInside = Number(data?.shippingRates?.inside ?? 60);
        const shippingOutside = Number(data?.shippingRates?.outside ?? 120);
        setSettings({
          siteName: data.siteName || '',
          siteTitle: typeof data.siteTitle === 'string' && data.siteTitle.trim().length > 0 ? data.siteTitle : DEFAULT_SITE_TITLE,
          tagline: typeof data.tagline === 'string' ? data.tagline : DEFAULT_SITE_TAGLINE,
          currency: data.currency || '৳',
          facebookUrl: data.facebookUrl || '',
          instagramUrl: data.instagramUrl || '',
          supportEmail: data.supportEmail || '',
          homeHeroDescription: typeof data.homeHeroDescription === 'string' ? data.homeHeroDescription : DEFAULT_HOME_HERO_DESCRIPTION,
          shippingRates: { inside: shippingInside, outside: shippingOutside },
          bkashNumber: data.bkashNumber || '',
          bkashQrUrl: data.bkashQrUrl || '',
          bkashCashoutEnabled: data.bkashCashoutEnabled ?? false,
          bkashCashoutAmount: Math.max(0, Number(data.bkashCashoutAmount ?? 20) || 20),
          orderIdPrefix: data.orderIdPrefix || 'PV',
          orderIdSuffix: data.orderIdSuffix || '',
          enableCampaignPopup: data.enableCampaignPopup ?? true,
          campaignPopupDelaySeconds: typeof data.campaignPopupDelaySeconds === 'number' ? data.campaignPopupDelaySeconds : 3,
          notificationSoundUrl: data.notificationSoundUrl || '',
          logoUrl: data.logoUrl || '/logo.png',
          logoAccentUrl: data.logoAccentUrl || '',
          faviconUrl: data.faviconUrl || '',
          shareImageUrl: data.shareImageUrl || '',
          metaPixelId: data.metaPixelId || DEFAULT_META_PIXEL_ID,
          accentColor: normalizeCssColorToHex(data.accentColor) || '#d3763b',
          heroBanners: normalizeHeroBanners(data.heroBanners),
          homepageBanners: normalizeHomepageBanners(data.homepageBanners),
          smsNotifications: normalizeSmsNotifications(data.smsNotifications),
          demoMode: data.demoMode ?? false
        });
      } catch (err) {
        addToast('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [request, addToast]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!hasUserPermission(user, 'settings:write')) {
      addToast('You do not have permission to update settings', 'error');
      return;
    }
    setSaving(true);

    try {
      const sanitizedShipping = {
        inside: Number(settings.shippingRates?.inside ?? 60),
        outside: Number(settings.shippingRates?.outside ?? 120)
      };
      const timestamp = Date.now();
      const sanitizedHeroBanners = (settings.heroBanners || []).map((banner, idx) => {
        const rawImg = (banner?.image ?? '').toString().trim();
        const cleanImg = rawImg.replace(/^['"]|['"]$/g, '').trim();
        return {
          id: banner?.id && banner.id.toString().trim().length > 0 ? banner.id : `hero_${timestamp}_${idx}`,
          headline: banner?.headline?.trim() || '',
          subheadline: banner?.subheadline?.trim() || '',
          image: cleanImg,
          ctaLabel: banner?.ctaLabel?.trim() || '',
          href: banner?.href?.trim() || '',
          imageHref: banner?.imageHref?.trim() || '',
          active: banner?.active === false ? false : true
        };
      });
      const sanitizedHomepageBanners = (settings.homepageBanners || []).map((banner, idx) => {
        const rawImg = (banner?.image ?? '').toString().trim();
        const cleanImg = rawImg.replace(/^['"]|['"]$/g, '').trim();
        const pos = (banner?.position || '').toString().trim();
        const allowed = HOMEPAGE_BANNER_POSITIONS;
        const position = allowed.includes(pos) ? pos : 'between_featured';
        return {
          id: banner?.id && banner.id.toString().trim().length > 0 ? banner.id : `home_banner_${timestamp}_${idx}`,
          image: cleanImg,
          href: banner?.href?.trim() || '',
          position,
          active: banner?.active === false ? false : true
        };
      });
      const payload = {
        ...settings,
        siteTitle: String(settings.siteTitle || '').trim(),
        tagline: String(settings.tagline || '').trim(),
        supportEmail: String(settings.supportEmail || '').trim(),
        homeHeroDescription: String(settings.homeHeroDescription || '').trim(),
        faviconUrl: String(settings.faviconUrl || '').trim(),
        shareImageUrl: String(settings.shareImageUrl || '').trim(),
        metaPixelId: String(settings.metaPixelId || '').trim(),
        accentColor: normalizeCssColorToHex(settings.accentColor) || '#d3763b',
        shippingRates: sanitizedShipping,
        bkashCashoutEnabled: settings.bkashCashoutEnabled === true,
        bkashCashoutAmount: Math.max(0, Number(settings.bkashCashoutAmount ?? 20) || 20),
        campaignPopupDelaySeconds: Math.max(0, Number(settings.campaignPopupDelaySeconds ?? 0)),
        heroBanners: sanitizedHeroBanners,
        homepageBanners: sanitizedHomepageBanners,
        smsNotifications: normalizeSmsNotifications(settings.smsNotifications)
      };

      const savedSettings = await request('/settings', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (savedSettings && typeof savedSettings === 'object') {
        setSettings(prev => ({
          ...prev,
          ...savedSettings,
          shippingRates: {
            inside: Number(savedSettings?.shippingRates?.inside ?? payload.shippingRates.inside),
            outside: Number(savedSettings?.shippingRates?.outside ?? payload.shippingRates.outside)
          },
          heroBanners: normalizeHeroBanners(savedSettings.heroBanners),
          homepageBanners: normalizeHomepageBanners(savedSettings.homepageBanners),
          smsNotifications: normalizeSmsNotifications(savedSettings.smsNotifications),
          accentColor: normalizeCssColorToHex(savedSettings.accentColor) || payload.accentColor
        }));
      }
      if (typeof onNotificationSoundUrlChange === 'function') {
        onNotificationSoundUrlChange(String(payload.notificationSoundUrl || '').trim());
      }
      if (typeof onFaviconUrlChange === 'function') {
        onFaviconUrlChange(String(payload.faviconUrl || '').trim());
      }
      addToast('Settings updated successfully', 'success');
    } catch (err) {
      addToast('Failed to update settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const can = useCallback((permissionKey) => hasUserPermission(user, permissionKey), [user]);
  const canWriteSettings = can('settings:write');
  const canRestartSystem = can('system:restart');
  const isRestartArmed = restartConfirmText.trim().toUpperCase() === 'RESTART';
  const handleRestartServices = useCallback(async () => {
    if (!canRestartSystem || restartingServices || !isRestartArmed) return;
    const ok = window.confirm('Confirm backend container restart? API and admin requests will disconnect briefly.');
    if (!ok) return;
    try {
      setRestartingServices(true);
      const result = await request('/system/restart', {
        method: 'POST',
        body: JSON.stringify({})
      });
      setRestartConfirmText('');
      addToast(result?.message || 'Restart initiated. Services should recover shortly.', 'success');
    } catch (err) {
      addToast(err?.message || 'Failed to start restart', 'error');
    } finally {
      setRestartingServices(false);
    }
  }, [canRestartSystem, restartingServices, isRestartArmed, request, addToast]);
  const loadSmsLogs = useCallback(async () => {
    if (!hasUserPermission(user, 'notifications:manage')) return;
    setSmsLogsLoading(true);
    try {
      const rows = await request('/sms/logs?limit=50');
      const nextRows = Array.isArray(rows) ? rows : [];
      setSmsLogs(nextRows);
      setSelectedSmsLog(prev => prev ? (nextRows.find(row => row.id === prev.id) || prev) : prev);
    } catch (err) {
      addToast(err?.message || 'Failed to load SMS logs', 'error');
    } finally {
      setSmsLogsLoading(false);
    }
  }, [request, addToast, user]);

  useEffect(() => {
    if (activeTab === 'sms') {
      loadSmsLogs();
    }
  }, [activeTab, loadSmsLogs]);

  const handleSmsTest = useCallback(async () => {
    setSmsActionLoading('test');
    try {
      const result = await request('/sms/test', {
        method: 'POST',
        body: JSON.stringify({ phone: smsTestPhone, message: smsTestMessage })
      });
      addToast(result?.message || 'SMS test completed', 'success');
      await loadSmsLogs();
    } catch (err) {
      addToast(err?.message || 'Failed to test SMS', 'error');
    } finally {
      setSmsActionLoading('');
    }
  }, [request, smsTestPhone, smsTestMessage, addToast, loadSmsLogs]);

  const handleSmsBalance = useCallback(async () => {
    setSmsActionLoading('balance');
    try {
      const result = await request('/sms/balance', { method: 'POST', body: JSON.stringify({}) });
      setSmsBalance(result);
      addToast('SMS balance checked', 'success');
    } catch (err) {
      addToast(err?.message || 'Failed to check SMS balance', 'error');
    } finally {
      setSmsActionLoading('');
    }
  }, [request, addToast]);

  const getSmsLogDetailFields = useCallback((log) => {
    if (!log) return [];
    const readableOrderId = log.order_display_id || log.display_order_id || log.order_id || '-';
    return [
      ['Log ID', log.id],
      ['Order ID', readableOrderId],
      ['Database order ID', log.order_id && log.order_id !== readableOrderId ? log.order_id : '-'],
      ['Event type', log.event_type || '-'],
      ['Status from', log.status_from || '-'],
      ['Status to', log.status_to || '-'],
      ['Recipient', log.recipient_phone || '-'],
      ['Log status', log.status || '-'],
      ['Manual send', log.manual ? 'Yes' : 'No'],
      ['Attempts', log.attempts ?? 0],
      ['Estimated segments', log.estimated_segments ?? 0],
      ['Provider SMS count', log.provider_sms_count ?? '-'],
      ['Provider status code', log.provider_status_code || '-'],
      ['Provider status', log.provider_status || '-'],
      ['Provider result', log.provider_response?.responseResult || '-'],
      ['Provider tracking ID', log.provider_tracking_id || '-'],
      ['Provider transaction ID', log.provider_trxn_id || '-'],
      ['Skip reason', log.skip_reason || '-'],
      ['Error', log.error || '-'],
      ['Created', log.created_at ? new Date(log.created_at).toLocaleString() : '-'],
      ['Sent at', log.sent_at ? new Date(log.sent_at).toLocaleString() : '-']
    ];
  }, []);

  const formatSmsLogForCopy = useCallback((log) => {
    if (!log) return '';
    const providerResponse = log.provider_response
      ? JSON.stringify(log.provider_response, null, 2)
      : '-';
    return [
      'SMS Log Details',
      ...getSmsLogDetailFields(log).map(([label, value]) => `${label}: ${String(value ?? '-')}`),
      '',
      'Message:',
      log.message || '-',
      '',
      'Raw provider response:',
      providerResponse
    ].join('\n');
  }, [getSmsLogDetailFields]);

  const copySmsLogDetails = useCallback(async (log) => {
    const text = formatSmsLogForCopy(log);
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      addToast('SMS log details copied', 'success');
    } catch (err) {
      addToast('Failed to copy SMS log details', 'error');
    }
  }, [formatSmsLogForCopy, addToast]);

  const insertSmsTemplateToken = useCallback((status, token) => {
    const safeStatus = String(status || '').trim();
    const safeToken = String(token || '').trim();
    if (!safeStatus || !safeToken) return;
    const input = smsTemplateRefs.current[safeStatus];
    const currentTemplate = getSmsTemplateValue(settings.smsNotifications, safeStatus);
    const start = Number.isInteger(input?.selectionStart) ? input.selectionStart : currentTemplate.length;
    const end = Number.isInteger(input?.selectionEnd) ? input.selectionEnd : start;
    const nextTemplate = `${currentTemplate.slice(0, start)}${safeToken}${currentTemplate.slice(end)}`.slice(0, 500);
    setSettings(prev => ({
      ...prev,
      smsNotifications: normalizeSmsNotifications({
        ...prev.smsNotifications,
        templates: {
          ...(prev.smsNotifications?.templates || {}),
          [safeStatus]: nextTemplate
        }
      })
    }));
    window.setTimeout(() => {
      const nextInput = smsTemplateRefs.current[safeStatus];
      if (!nextInput) return;
      const caret = Math.min(start + safeToken.length, nextTemplate.length);
      nextInput.focus();
      try { nextInput.setSelectionRange(caret, caret); } catch {}
    }, 0);
  }, [settings.smsNotifications]);

  const tabs = useMemo(() => ([
    { id: 'general', name: 'General', icon: Icon.Settings, enabled: can('settings:read') || can('settings:write') },
    { id: 'branding', name: 'Branding', icon: Icon.Palette, enabled: can('settings:read') || can('settings:write') },
    { id: 'payment', name: 'Payment & Shipping', icon: Icon.Tag, enabled: can('settings:read') || can('settings:write') },
    { id: 'features', name: 'Features', icon: Icon.Package, enabled: can('settings:read') || can('settings:write') },
    { id: 'sms', name: 'SMS', icon: Icon.Megaphone, enabled: can('notifications:manage') },
    { id: 'users', name: 'Users', icon: Icon.User, enabled: can('users:read') },
    { id: 'legal', name: 'Legal Pages', icon: Icon.Package, enabled: can('legal:write') },
    { id: 'backup', name: 'Backup', icon: Icon.Download, enabled: can('backup:manage') || can('backup:restore') }
  ].filter(tab => tab.enabled)), [can]);
  const tabIds = useMemo(() => tabs.map(tab => tab.id), [tabs]);
  const tabInitRef = useRef(false);

  useEffect(() => {
    if (!tabIds.length) return;
    const fallbackTab = tabIds[0];
    if (!tabInitRef.current) {
      let desiredTab = fallbackTab;
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const urlTab = params.get('tab');
        if (urlTab && tabIds.includes(urlTab)) {
          desiredTab = urlTab;
        }
      }
      tabInitRef.current = true;
      if (desiredTab !== activeTab) {
        setActiveTab(desiredTab);
      }
      return;
    }
    if (!tabIds.includes(activeTab)) {
      setActiveTab(fallbackTab);
    }
  }, [tabIds, activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!tabInitRef.current) return;
    if (!tabIds.length || !tabIds.includes(activeTab)) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') !== 'settings' || params.get('tab') !== activeTab) {
      params.set('page', 'settings');
      params.set('tab', activeTab);
      const qs = params.toString();
      const nextUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [activeTab, tabIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading settings...</span>
      </div>
    );
  }

  const smsLogDetailsModal = selectedSmsLog ? (
    <div
      className="fixed inset-0 z-[240] bg-black/60 backdrop-blur-[1px] flex items-start sm:items-center justify-center p-4"
      onClick={() => setSelectedSmsLog(null)}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 p-6 border-b border-gray-200 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">SMS Log Details</h2>
            <p className="mt-1 text-sm text-gray-500">Use this data to debug provider rejects, whitelist issues, invalid phone numbers, and segment billing.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => copySmsLogDetails(selectedSmsLog)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Copy details
            </button>
            <button
              type="button"
              onClick={() => setSelectedSmsLog(null)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close SMS log details"
            >
              <Icon.X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto min-h-0 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 text-sm">
            {getSmsLogDetailFields(selectedSmsLog).map(([label, value]) => (
              <div key={label} className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                <div className="mt-1 break-words text-gray-900">{String(value ?? '-')}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Message</div>
              <div className="whitespace-pre-wrap break-words text-sm text-gray-900">{selectedSmsLog.message || '-'}</div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-950 p-4 shadow-sm">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-300">Raw provider response</div>
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-100">
                {selectedSmsLog.provider_response
                  ? JSON.stringify(selectedSmsLog.provider_response, null, 2)
                  : '-'}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const settingsContent = (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings & Customization</h1>
        <p className="text-gray-600 mt-1">Configure your store appearance and functionality</p>
      </div>

      {/* Tabs */}
      <div className="relative bg-white rounded-lg shadow-sm overflow-hidden">
        {(activeTab !== 'users' && activeTab !== 'legal' && activeTab !== 'backup') && (
          <div className="absolute right-4 top-1 z-20 hidden sm:block">
            <button
              type="submit"
              form="settings-customization-form"
              disabled={saving || !canWriteSettings}
              className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-blue-700/10 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Icon.Loading className="w-4 h-4" />}
              {saving ? 'Saving...' : (canWriteSettings ? 'Save Settings' : 'Read Only')}
            </button>
          </div>
        )}
        {(activeTab !== 'users' && activeTab !== 'legal' && activeTab !== 'backup') && (
          <div className="border-b border-gray-100 bg-gray-50/70 px-3 py-2 sm:hidden">
            <button
              type="submit"
              form="settings-customization-form"
              disabled={saving || !canWriteSettings}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-blue-700/10 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Icon.Loading className="w-4 h-4" />}
              {saving ? 'Saving...' : (canWriteSettings ? 'Save Settings' : 'Read Only')}
            </button>
          </div>
        )}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto flex-nowrap sm:pr-44">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'users' && can('users:read') && (<div className="p-6"><UsersManager /></div>)}
        {activeTab === 'legal' && can('legal:write') && (<div className="p-6"><LegalPagesManager /></div>)}
        {activeTab === 'backup' && (can('backup:manage') || can('backup:restore')) && (<div className="p-6"><BackupManager /></div>)}
        {(activeTab !== 'users' && activeTab !== 'legal' && activeTab !== 'backup') && (
        <form id="settings-customization-form" onSubmit={handleSave} className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Store Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site Name (Optional)</label>
                    <input
                      type="text"
                      value={settings.siteName}
                      onChange={(e) => setSettings(prev => ({ ...prev, siteName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ItemSmith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Store Title / SEO Title (Optional)</label>
                    <input
                      type="text"
                      value={settings.siteTitle}
                      onChange={(e) => setSettings(prev => ({ ...prev, siteTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={DEFAULT_SITE_TITLE}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency Symbol (Optional)</label>
                    <input
                      type="text"
                      value={settings.currency}
                      onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="৳"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL (Optional)</label>
                    <input
                      type="url"
                      value={settings.facebookUrl}
                      onChange={(e) => setSettings(prev => ({ ...prev, facebookUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL (Optional)</label>
                    <input
                      type="url"
                      value={settings.instagramUrl}
                      onChange={(e) => setSettings(prev => ({ ...prev, instagramUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://instagram.com/yourhandle"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Support Email (Optional)</label>
                    <input
                      type="email"
                      value={settings.supportEmail}
                      onChange={(e) => setSettings(prev => ({ ...prev, supportEmail: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="support@yourdomain.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Store Tagline / SEO Description (Optional)</label>
                    <textarea
                      value={settings.tagline}
                      onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={DEFAULT_SITE_TAGLINE}
                    />
                    <p className="mt-1 text-xs text-gray-500">Used for search snippets, link previews, and storefront marketing copy.</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Homepage Intro Text (Optional)</label>
                    <textarea
                      value={settings.homeHeroDescription}
                      onChange={(e) => setSettings(prev => ({ ...prev, homeHeroDescription: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={DEFAULT_HOME_HERO_DESCRIPTION}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Pixel ID (Optional)</label>
                    <input
                      type="text"
                      value={settings.metaPixelId}
                      onChange={(e) => setSettings(prev => ({ ...prev, metaPixelId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123456789012345"
                    />
                    <p className="mt-1 text-xs text-gray-500">Enables PageView, ViewContent, AddToCart, InitiateCheckout, and Purchase events for Meta ads and boosted products.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Order ID Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prefix (Optional)</label>
                    <input
                      type="text"
                      value={settings.orderIdPrefix}
                      onChange={(e) => setSettings(prev => ({ ...prev, orderIdPrefix: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="PV"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suffix (Optional)</label>
                    <input
                      type="text"
                      value={settings.orderIdSuffix}
                      onChange={(e) => setSettings(prev => ({ ...prev, orderIdSuffix: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Leave empty if not needed"
                    />
                  </div>
                </div>
                <div className="mt-2 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Preview:</strong> <code className="bg-white px-2 py-0.5 rounded">{settings.orderIdPrefix}83425190{settings.orderIdSuffix}</code>
                  </p>
                </div>
              </div>

            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color (Optional)</label>
                    <input
                      type="color"
                      value={normalizeCssColorToHex(settings.accentColor) || '#d3763b'}
                      onChange={(e) => setSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                      className="w-20 h-10 p-0 border border-gray-300 rounded-md"
                      title="Pick the site accent color"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                      <span className="inline-block w-6 h-6 rounded" style={{ backgroundColor: settings.accentColor || '#d3763b' }} />
                      <div className="text-sm text-gray-700">Used for primary highlights, buttons, and accents on the storefront.</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Logos</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <MediaPicker
                      label="Primary Logo (Optional)"
                      value={settings.logoUrl}
                      onChange={(url) => setSettings(prev => ({ ...prev, logoUrl: url }))}
                      helpText="Main logo displayed in header"
                      ratioHint="1:1 square"
                    />
                  </div>
                  <div>
                    <MediaPicker
                      label="Accent Logo (Optional)"
                      value={settings.logoAccentUrl}
                      onChange={(url) => setSettings(prev => ({ ...prev, logoAccentUrl: url }))}
                      helpText="Alternative logo for scrolled header"
                      ratioHint="1:1 square"
                    />
                  </div>
                  <div>
                    <MediaPicker
                      label="Share Preview Image (Optional)"
                      value={settings.shareImageUrl}
                      onChange={(url) => setSettings(prev => ({ ...prev, shareImageUrl: url }))}
                      helpText="Used for social sharing and marketing previews when a product-specific image is not available."
                      ratioHint="1200 x 630 recommended"
                    />
                  </div>
                  <div>
                    <MediaPicker
                      label="Favicon (Optional)"
                      value={settings.faviconUrl}
                      onChange={(url) => {
                        setSettings(prev => ({ ...prev, faviconUrl: url }));
                        if (typeof onFaviconUrlChange === 'function') {
                          onFaviconUrlChange(String(url || '').trim());
                        }
                      }}
                      helpText="Browser tab icon for both storefront and admin. Supports .ico, .png, .svg."
                      accept="image/*,.ico"
                      ratioHint="1:1 square"
                    />
                  </div>
                </div>
              </div>

              {/* Hero Banners */}
              <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Hero Banners</h3>
              <button
                type="button"
                onClick={() => setSettings(prev => ({ ...prev, heroBanners: [...(prev.heroBanners || []), createHeroBanner()] }))}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                <Icon.Plus className="w-4 h-4" />
                Add Banner
              </button>
            </div>
            <p className="mb-3 text-xs text-gray-500">Image is the only required field for hero banners. All text/link fields are optional.</p>
            <div className="space-y-4">
              {(settings.heroBanners || []).length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  No hero banners yet. Click "Add Banner" to create one.
                </p>
              ) : (
                (settings.heroBanners || []).map((banner, index) => (
                  <div key={banner.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Banner {index + 1}</span>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={banner.active !== false}
                            onChange={(e) => {
                              const newBanners = [...settings.heroBanners];
                              newBanners[index] = { ...newBanners[index], active: e.target.checked };
                              setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                            }}
                            className="rounded"
                          />
                          Active
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const newBanners = settings.heroBanners.filter((_, i) => i !== index);
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Headline (Optional)</label>
                        <input
                          type="text"
                          value={banner.headline || ''}
                          onChange={(e) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], headline: e.target.value };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Main heading"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline (Optional)</label>
                        <input
                          type="text"
                          value={banner.subheadline || ''}
                          onChange={(e) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], subheadline: e.target.value };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Subtitle text"
                        />
                      </div>
                      <div>
                        <MediaPicker
                          label="Banner Image"
                          value={banner.image || ''}
                          onChange={(url) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], image: url };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          helpText="Select from library, upload, or paste URL"
                          ratioHint="5:4 (cover)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Label (Optional)</label>
                        <input
                          type="text"
                          value={banner.ctaLabel || ''}
                          onChange={(e) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], ctaLabel: e.target.value };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Shop Now"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Link URL (Optional)</label>
                        <input
                          type="text"
                          value={banner.href || ''}
                          onChange={(e) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], href: e.target.value };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="/catalog or https://..."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image Click URL (Optional)</label>
                        <input
                          type="text"
                          value={banner.imageHref || ''}
                          onChange={(e) => {
                            const newBanners = [...settings.heroBanners];
                            newBanners[index] = { ...newBanners[index], imageHref: e.target.value };
                            setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="/catalog or https://..."
                        />
                        <p className="mt-1 text-xs text-gray-500">When set, clicking the banner image follows this URL. The button still uses the Button Link URL above.</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

              {/* Homepage Wide Banners */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Homepage Full-Width Banners</h3>
                    <p className="text-xs text-gray-500">16:3 banners, image-only, clickable.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSettings(prev => ({ ...prev, homepageBanners: [...(prev.homepageBanners || []), createHomepageBanner()] }))}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    <Icon.Plus className="w-4 h-4" />
                    Add Banner
                  </button>
                </div>
                <div className="space-y-4">
                  {(settings.homepageBanners || []).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                      No homepage banners yet. Click "Add Banner" to create one.
                    </p>
                  ) : (
                    (settings.homepageBanners || []).map((banner, index) => (
                      <div key={banner.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Banner {index + 1}</span>
                          <div className="flex items-center gap-2">
                            <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={banner.active !== false}
                                onChange={(e) => {
                                  const next = [...settings.homepageBanners];
                                  next[index] = { ...next[index], active: e.target.checked };
                                  setSettings(prev => ({ ...prev, homepageBanners: next }));
                                }}
                                className="rounded"
                              />
                              Active
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const next = settings.homepageBanners.filter((_, i) => i !== index);
                                setSettings(prev => ({ ...prev, homepageBanners: next }));
                              }}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <Icon.Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <MediaPicker
                              label="Banner Image"
                              value={banner.image || ''}
                              onChange={(url) => {
                                const next = [...settings.homepageBanners];
                                next[index] = { ...next[index], image: url };
                                setSettings(prev => ({ ...prev, homepageBanners: next }));
                              }}
                              helpText="Select from library, upload, or paste URL"
                              ratioHint="16:3 wide"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Click URL (Optional)</label>
                            <input
                              type="text"
                              value={banner.href || ''}
                              onChange={(e) => {
                                const next = [...settings.homepageBanners];
                                next[index] = { ...next[index], href: e.target.value };
                                setSettings(prev => ({ ...prev, homepageBanners: next }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              placeholder="/catalog or https://..."
                            />
                            <p className="mt-1 text-xs text-gray-500">When set, the banner image links to this URL.</p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                            <select
                              value={banner.position || 'between_featured'}
                              onChange={(e) => {
                                const next = [...settings.homepageBanners];
                                next[index] = { ...next[index], position: e.target.value };
                                setSettings(prev => ({ ...prev, homepageBanners: next }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="top_of_home">Top of homepage</option>
                              <option value="below_hero">Below hero section</option>
                              <option value="above_featured">Above featured products</option>
                              <option value="between_featured">Between featured products</option>
                              <option value="below_featured">Below featured products</option>
                              <option value="above_closing">Above closing CTA</option>
                              <option value="below_closing">Below closing CTA</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment & Shipping Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Shipping Rates</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inside Dhaka (৳)</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.shippingRates.inside}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        shippingRates: { ...prev.shippingRates, inside: parseInt(e.target.value) || 0 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Flat rate for deliveries inside Dhaka city</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Outside Dhaka (৳)</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.shippingRates.outside}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        shippingRates: { ...prev.shippingRates, outside: parseInt(e.target.value) || 0 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Flat rate for deliveries outside Dhaka</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Methods</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">bKash Number</label>
                    <input
                      type="text"
                      value={settings.bkashNumber}
                      onChange={(e) => setSettings(prev => ({ ...prev, bkashNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="01XXXXXXXXX"
                    />
                    <p className="mt-1 text-xs text-gray-500">Customers will send payments to this number</p>
                  </div>
                  <div>
                    <MediaPicker
                      label="bKash QR Code"
                      value={settings.bkashQrUrl}
                      onChange={(url) => setSettings(prev => ({ ...prev, bkashQrUrl: url }))}
                      helpText="Optional: Upload or pick a QR image customers can scan at checkout"
                      ratioHint="1:1 square"
                    />
                    {settings.bkashQrUrl && (
                      <div className="mt-2 inline-flex items-center gap-3 rounded-md border border-gray-200 bg-white p-2">
                        <img src={settings.bkashQrUrl} alt="bKash QR preview" className="h-24 w-24 object-contain" />
                        <div className="text-xs text-gray-600">
                          <div>Displayed on checkout when bKash is selected.</div>
                          <div className="text-[10px] text-gray-400 truncate max-w-[220px]">{settings.bkashQrUrl}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={settings.bkashCashoutEnabled}
                        onChange={(e) => setSettings(prev => ({ ...prev, bkashCashoutEnabled: e.target.checked }))}
                        className="rounded"
                      />
                      Include bKash cashout charge on all bKash orders
                    </label>
                    {settings.bkashCashoutEnabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cashout charge amount (৳)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={settings.bkashCashoutAmount}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bkashCashoutAmount: Math.max(0, Number(e.target.value) || 0)
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">This amount is added to checkout totals whenever customers choose bKash.</p>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Payment Methods Supported:</h4>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>✓ bKash Personal</li>
                      <li>✓ Cash on Delivery (COD)</li>
                      <li>✓ Bank Transfer (on request)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Demo Mode</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <label className="inline-flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.demoMode}
                      onChange={(e) => setSettings(prev => ({ ...prev, demoMode: e.target.checked }))}
                      className="rounded w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Enable Demo Mode</p>
                      <p className="text-sm text-gray-600">Show demo data and features for testing purposes</p>
                    </div>
                  </label>
                  {settings.demoMode && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> Demo mode is enabled. This may display sample data to customers.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance Mode</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <label className="inline-flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.maintenanceMode || false}
                      onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMode: e.target.checked }))}
                      className="rounded w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Enable Maintenance Mode</p>
                      <p className="text-sm text-gray-600">Hide the public site with an under maintenance message (admins unaffected).</p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Discount Popup</h3>
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <label className="inline-flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.enableCampaignPopup}
                        onChange={(e) => setSettings(prev => ({ ...prev, enableCampaignPopup: e.target.checked }))}
                        className="rounded w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Enable Discount Popup</p>
                        <p className="text-sm text-gray-600">Show promotional popup on homepage</p>
                      </div>
                    </label>
                  </div>
                  
                  {settings.enableCampaignPopup && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Popup Delay (seconds)</label>
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={settings.campaignPopupDelaySeconds}
                        onChange={(e) => setSettings(prev => ({ ...prev, campaignPopupDelaySeconds: Math.max(0, Math.min(60, Number(e.target.value))) }))}
                        className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Delay before popup appears (0 = immediate, max 60 seconds)</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Notifications</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Notification Sound</label>
                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <select
                      value={notificationSoundPreference}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (typeof onNotificationSoundPreferenceChange === 'function') {
                          onNotificationSoundPreferenceChange(next);
                        }
                      }}
                      className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="classic">Classic</option>
                        <option value="custom">Custom</option>
                      <option value="beep">Beep</option>
                      <option value="off">Off</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof onPreviewNotificationSound === 'function') {
                          onPreviewNotificationSound();
                        }
                      }}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Preview
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Applies to realtime admin alerts in this browser.</p>
                  <div className="pt-2">
                    <MediaPicker
                      label="Custom Notification Sound (optional)"
                      value={settings.notificationSoundUrl || ''}
                      onChange={(url) => {
                        const next = String(url || '').trim();
                        setSettings(prev => ({ ...prev, notificationSoundUrl: next }));
                        if (typeof onNotificationSoundUrlChange === 'function') {
                          onNotificationSoundUrlChange(next);
                        }
                      }}
                      helpText="Upload/select an MP3/WAV from media library. Used when sound is set to Classic."
                      accept="*/*"
                      ratioHint="Audio"
                    />
                  </div>
                </div>
              </div>

              {canRestartSystem && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Container Restart</h3>
                  <div className="rounded-lg border border-red-300 bg-red-50 p-4 space-y-3">
                    <p className="text-sm text-red-800">
                      Restarts the backend container without rebooting the VPS. Docker restart policy must be enabled.
                    </p>
                    <label className="block text-sm font-medium text-red-900">
                      Type <code className="bg-white px-1 py-0.5 rounded border border-red-200">RESTART</code> to enable the button
                    </label>
                    <input
                      type="text"
                      value={restartConfirmText}
                      onChange={(e) => setRestartConfirmText(e.target.value)}
                      placeholder="RESTART"
                      className="w-full md:w-64 px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                      autoComplete="off"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleRestartServices}
                        disabled={saving || restartingServices || !isRestartArmed}
                        className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        {restartingServices && <Icon.Loading className="w-4 h-4" />}
                        {restartingServices ? 'Restarting container...' : 'Restart Backend Container'}
                      </button>
                      {!isRestartArmed && (
                        <span className="text-xs text-red-700">Safety lock active</span>
                      )}
                    </div>
                    <p className="text-xs text-red-700">
                      This action is permission-protected and audited.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sms' && (
            <div className="space-y-5">
              {!smsHelpDismissed && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-medium text-blue-950 mb-1">SMS Notifications</h3>
                      <p className="text-sm text-blue-800">
                        Automatic SMS is Bangladesh-mobile-only and deduped once per order status. Keep templates in Bangla Unicode for BTRC/MiMSMS compliance; order IDs and URLs can remain English.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSmsHelpDismissed(true);
                        if (typeof window !== 'undefined') {
                          window.localStorage.setItem(SMS_HELP_DISMISSED_KEY, 'true');
                        }
                      }}
                      className="shrink-0 rounded-md border border-blue-200 bg-white px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4">
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Delivery Rules</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        ['enabled', 'Enable automatic SMS', 'Allows selected status changes to send SMS.'],
                        ['dryRun', 'Dry-run mode', 'Logs SMS without spending provider credit.'],
                        ['oneSegmentOnly', 'Max 1 segment', 'Blocks long automatic messages before provider call.']
                      ].map(([key, label, help]) => (
                        <label key={key} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={key === 'enabled' ? settings.smsNotifications?.enabled === true : settings.smsNotifications?.[key] !== false}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              smsNotifications: normalizeSmsNotifications({ ...prev.smsNotifications, [key]: e.target.checked })
                            }))}
                            className="mt-0.5 rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span>
                            <span className="block text-sm font-semibold text-gray-900">{label}</span>
                            <span className="block text-xs text-gray-500">{help}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-end sm:justify-between">
                      <h3 className="text-lg font-medium text-gray-900">Status Triggers</h3>
                      <p className="text-xs text-gray-500">Default: confirmed, shipped, cancelled. Delivered is optional.</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {SMS_STATUS_EVENTS.map((status) => (
                        <label key={status} className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                          <span className="font-medium capitalize text-gray-800">{status}</span>
                          <input
                            type="checkbox"
                            checked={settings.smsNotifications?.statuses?.[status] === true}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              smsNotifications: normalizeSmsNotifications({
                                ...prev.smsNotifications,
                                statuses: {
                                  ...(prev.smsNotifications?.statuses || {}),
                                  [status]: e.target.checked
                                }
                              })
                            }))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Templates</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {SMS_STATUS_EVENTS.map((status) => {
                        const template = getSmsTemplateValue(settings.smsNotifications, status);
                        const estimate = estimateSmsTemplate(template, status, settings);
                        return (
                          <div key={status} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                            <div className="mb-2 flex flex-col gap-2">
                              <div className="flex items-center justify-between gap-2">
                                <label className="text-sm font-semibold text-gray-800 capitalize">{status}</label>
                                <span className={`text-xs font-semibold ${estimate.segments > 1 ? 'text-orange-600' : 'text-gray-500'}`}>
                                  {estimate.length} chars / {estimate.segments} seg
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {SMS_TEMPLATE_TOKENS.map((token) => (
                                  <button
                                    key={`${status}-${token}`}
                                    type="button"
                                    onClick={() => insertSmsTemplateToken(status, token)}
                                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 hover:border-blue-300 hover:text-blue-700"
                                  >
                                    {token}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <textarea
                              ref={(el) => { smsTemplateRefs.current[status] = el; }}
                              value={template}
                              rows={3}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                smsNotifications: normalizeSmsNotifications({
                                  ...prev.smsNotifications,
                                  templates: {
                                    ...(prev.smsNotifications?.templates || {}),
                                    [status]: e.target.value
                                  }
                                })
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                            />
                            <div className="mt-1 text-xs text-gray-500">
                              {estimate.encoding.toUpperCase()} estimate uses the current store URL, order ID format, and site name.
                              {' '}
                              Limit: {estimate.encoding === 'unicode' ? '70 chars for 1 SMS, then 67 chars/segment' : '160 chars for 1 SMS, then 153 chars/segment'}.
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Message Preview</h3>
                    <div className="mx-auto max-w-[320px] rounded-[24px] border border-black/35 bg-[#f6f7fb] p-3 shadow-inner">
                      <div
                        className="max-h-[360px] space-y-2 overflow-y-auto pr-1 [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      >
                        {SMS_STATUS_EVENTS.filter((status) => settings.smsNotifications?.statuses?.[status] === true).length === 0 ? (
                          <div className="rounded-2xl bg-white p-3 text-sm text-gray-500 shadow-sm">
                            No enabled SMS triggers to preview.
                          </div>
                        ) : SMS_STATUS_EVENTS.filter((status) => settings.smsNotifications?.statuses?.[status] === true).map((status) => {
                          const template = getSmsTemplateValue(settings.smsNotifications, status);
                          const message = renderSmsTemplateEstimate(template, status, settings);
                          const estimate = estimateSmsSegmentsForText(message);
                          return (
                            <div key={`phone-preview-${status}`} className="space-y-0.5">
                              <div className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">{status}</div>
                              <div
                                className="ml-auto max-w-[90%] rounded-2xl rounded-br-md bg-[#d9fdd3] px-3 py-2 text-sm leading-snug text-gray-900 shadow-sm"
                                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                              >
                                {message}
                              </div>
                              <div className="text-right text-[10px] text-gray-400">
                                {estimate.length} chars / {estimate.segments} seg
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Provider Tools</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Provider number format</label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            ['e164', '8801...', 'Matches MiMSMS docs.'],
                            ['local', '01...', 'Try if provider rejects 880 format.']
                          ].map(([value, label, help]) => {
                            const active = (settings.smsNotifications?.phoneFormat || 'e164') === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setSettings(prev => ({
                                  ...prev,
                                  smsNotifications: normalizeSmsNotifications({
                                    ...prev.smsNotifications,
                                    phoneFormat: value
                                  })
                                }))}
                                className={`rounded-md border px-3 py-2 text-left transition ${
                                  active
                                    ? 'border-blue-500 bg-blue-50 text-blue-900 shadow-sm'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50/50'
                                }`}
                              >
                                <span className="block text-sm font-semibold">{label}</span>
                                <span className="block text-[11px] text-gray-500">{help}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test phone</label>
                        <input
                          type="text"
                          value={smsTestPhone}
                          onChange={(e) => setSmsTestPhone(e.target.value)}
                          placeholder="017XXXXXXXX"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test message</label>
                        <textarea
                          value={smsTestMessage}
                          onChange={(e) => setSmsTestMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {(() => {
                          const estimate = estimateSmsTemplate(smsTestMessage, 'confirmed', settings);
                          return <div className="mt-1 text-xs text-gray-500">{estimate.length} estimated chars, {estimate.segments} segment{estimate.segments === 1 ? '' : 's'} using realistic token values.</div>;
                        })()}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={handleSmsTest}
                          disabled={smsActionLoading === 'test'}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {smsActionLoading === 'test' ? 'Testing...' : 'Send Test SMS'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSmsBalance}
                          disabled={smsActionLoading === 'balance'}
                          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          {smsActionLoading === 'balance' ? 'Checking...' : 'Check Balance'}
                        </button>
                      </div>
                      {smsBalance && (
                        <pre className="max-h-48 overflow-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">{JSON.stringify(smsBalance, null, 2)}</pre>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Recent SMS Logs</h3>
                  <button type="button" onClick={loadSmsLogs} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50">
                    {smsLogsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                <div className="max-h-[340px] overflow-auto rounded-md border border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Order</th>
                        <th className="px-3 py-2 text-left">Event</th>
                        <th className="px-3 py-2 text-left">Recipient</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Segments</th>
                        <th className="px-3 py-2 text-left">Provider</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {smsLogs.length === 0 ? (
                        <tr><td colSpan="6" className="px-3 py-6 text-center text-gray-500">No SMS logs yet.</td></tr>
                      ) : smsLogs.map((log) => (
                        <tr
                          key={log.id}
                          onClick={() => setSelectedSmsLog(log)}
                          className={`cursor-pointer transition hover:bg-blue-50 ${selectedSmsLog?.id === log.id ? 'bg-blue-50' : ''}`}
                          title="Open SMS log details"
                        >
                          <td className="px-3 py-2 text-gray-700">{log.order_display_id || log.display_order_id || log.order_id || '-'}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900">{log.event_type}</div>
                            <div className="text-xs text-gray-500">{log.status_from || '-'} → {log.status_to || '-'}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-700">{log.recipient_phone || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              log.status === 'sent' ? 'bg-green-100 text-green-700' :
                              log.status === 'failed' ? 'bg-red-100 text-red-700' :
                              log.status === 'skipped' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {log.status}
                            </span>
                            {(log.error || log.skip_reason) && <div className="mt-1 max-w-[220px] truncate text-xs text-gray-500">{log.skip_reason || log.error}</div>}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{log.estimated_segments || 0}{log.provider_sms_count ? ` / billed ${log.provider_sms_count}` : ''}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {log.provider_tracking_id || log.provider_trxn_id || log.provider_status || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </form>
        )}
      </div>
    </div>
  );

  return (
    <>
      {settingsContent}
      {typeof document !== 'undefined' && smsLogDetailsModal ? createPortal(smsLogDetailsModal, document.body) : smsLogDetailsModal}
    </>
  );
}

// OrderDetailsModal component for viewing and editing order details, including tracking URL update

function OrderDetailsModal({ order, onClose, onUpdate }) {
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || '');
  const [savingTracking, setSavingTracking] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20
  });
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [smsActionLoading, setSmsActionLoading] = useState('');
  const [changingOrderStatus, setChangingOrderStatus] = useState(false);
  const { request } = useApi();
  const { addToast } = useToasts();

  useEffect(() => {
    request('/settings')
      .then((data) => {
        setSettings({
          bkashCashoutEnabled: data?.bkashCashoutEnabled === true,
          bkashCashoutAmount: Math.max(0, Number(data?.bkashCashoutAmount ?? 20) || 20)
        });
      })
      .catch(() => {});
    request('/products')
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [request]);

  const loadOrderSmsLogs = useCallback(async () => {
    if (!order?.id) return;
    setSmsLogsLoading(true);
    try {
      const rows = await request(`/sms/logs?orderId=${encodeURIComponent(order.id)}&limit=30`);
      setSmsLogs(Array.isArray(rows) ? rows : []);
    } catch {
      setSmsLogs([]);
    } finally {
      setSmsLogsLoading(false);
    }
  }, [request, order?.id]);

  useEffect(() => {
    loadOrderSmsLogs();
  }, [loadOrderSmsLogs]);

  const handleRetrySms = useCallback(async (log) => {
    if (!log?.id) return;
    const ok = window.confirm(`Retry this existing SMS log to ${log.recipient_phone || 'customer'}?\n\nThis resends the exact logged message, not the latest template.\n\nMessage:\n${log.message || ''}\n\nEstimated segments: ${log.estimated_segments || 0}. This may cost SMS credit if dry-run is off.`);
    if (!ok) return;
    setSmsActionLoading(log.id);
    try {
      const result = await request(`/sms/${log.id}/retry`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      addToast(result?.message || 'SMS retry completed', 'success');
      await loadOrderSmsLogs();
    } catch (err) {
      addToast(err?.message || 'Failed to retry SMS', 'error');
    } finally {
      setSmsActionLoading('');
    }
  }, [request, addToast, loadOrderSmsLogs]);

  const handleManualSmsResend = useCallback(async (status) => {
    if (!SMS_STATUS_EVENTS.includes(status)) {
      addToast('This order status does not have an SMS template', 'info');
      return;
    }
    setSmsActionLoading(`manual-${status}`);
    try {
      const preview = await request(`/orders/${order.id}/sms/preview?status=${encodeURIComponent(status)}`);
      const ok = window.confirm(`Manually resend ${status} SMS?\n\nRecipient: ${preview?.recipientPhone || 'No phone on order'}${preview?.recipientValid === false ? ` (${preview.recipientReason || 'invalid'})` : ''}\nPreview: ${preview?.message || ''}\nEstimated segments: ${preview?.estimate?.segments ?? 0}\n\nThis bypasses automatic dedupe and may cost SMS credit if dry-run is off.`);
      if (!ok) return;
      const result = await request(`/orders/${order.id}/sms/resend`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
      addToast(result?.message || 'Manual SMS logged', 'success');
      await loadOrderSmsLogs();
    } catch (err) {
      addToast(err?.message || 'Failed to resend SMS', 'error');
    } finally {
      setSmsActionLoading('');
    }
  }, [request, addToast, order, loadOrderSmsLogs]);

  const buildDraft = useCallback((o) => {
    const cust = o.customer || {};
    const name = cust.name || `${cust.firstName || ''} ${cust.lastName || ''}`.trim();
    const firstName = cust.firstName || (name ? name.split(' ')[0] : '');
    const lastName = cust.lastName || (name ? name.split(' ').slice(1).join(' ') : '');
    const incomingAdjustments = Array.isArray(o.manualAdjustments) ? o.manualAdjustments : [];
    const hasRequiredPaymentAmount =
      o.requiredPaymentAmount !== '' &&
      o.requiredPaymentAmount !== null &&
      o.requiredPaymentAmount !== undefined &&
      Number.isFinite(Number(o.requiredPaymentAmount));
    const resolvedRequiredPaymentAmount = hasRequiredPaymentAmount
      ? Math.max(0, Number(o.requiredPaymentAmount))
      : (incomingAdjustments.some((adj) => String(adj?.label || '') === REQUIRED_PAYMENT_OVERRIDE_LABEL)
        ? Math.max(0, Number(o.total) || 0)
        : '');
    return {
      status: o.status || 'pending',
      paymentStatus: o.payment_status || (o.payment?.method === 'bkash' ? 'pending' : 'unpaid'),
      fulfillmentStatus: o.fulfillment_status || 'unfulfilled',
      customer: {
        firstName,
        lastName,
        name: name || `${firstName} ${lastName}`.trim(),
        email: cust.email || '',
        phone: cust.phone || ''
      },
      shipping: { ...(o.shipping || {}) },
      payment: { ...(o.payment || {}) },
      items: (o.items || []).map(it => ({
        id: it.id,
        productId: it.productId || it.id,
        variantId: it.variantId || it.variant_id || null,
        variantLabel: it.variantLabel || null,
        variantSku: it.variantSku || null,
        variantOptions: it.variantOptions || null,
        title: it.title,
        price: Number(it.price) || 0,
        originalPrice: Number(it.originalPrice) > Number(it.price) ? Number(it.originalPrice) : null,
        qty: Math.max(1, parseInt(it.qty) || 1),
        img: it.img || null,
        category: it.category || null,
        color: it.color || null,
        stock: it.stock || null,
        customization: it.customization || null
      })),
      adminDiscountAmount: Math.max(0, Number(o.discount_amount || 0) || 0),
      requiredPaymentAmount: resolvedRequiredPaymentAmount,
      adminNotes: o.admin_notes || '',
      refundAmount: Number(o.refund_amount || 0),
      cancelReason: o.cancel_reason || ''
    };
  }, []);

  const [draft, setDraft] = useState(() => buildDraft(order));

  useEffect(() => {
    setDraft(buildDraft(order));
    setTrackingUrl(order.tracking_url || '');
    setEditMode(false);
  }, [order, buildDraft]);

  // Extract details
  const customer = order.customer || {};
  const shipping = order.shipping || {};
  const payment = order.payment || {};
  const items = useMemo(() => (Array.isArray(order.items) ? order.items : []), [order.items]);
  const productLookup = useMemo(
    () => new Map((Array.isArray(products) ? products : []).map((product) => [String(product.id || '').trim(), product])),
    [products]
  );
  const statusHistory = useMemo(() => (
    Array.isArray(order.statusHistory) ? order.statusHistory : []
  ), [order.statusHistory]);
  const handleHeaderStatusChange = useCallback(async (nextStatus) => {
    const status = String(nextStatus || '').trim();
    if (!status || status === order.status) return;
    const isSmsCapableStatus = SMS_STATUS_EVENTS.includes(status);
    const statusWasUsedBefore = isSmsCapableStatus && (
      statusHistory.some((entry) => String(entry?.action || '') === 'status_update' && String(entry?.to || '') === status)
      || smsLogs.some((log) => String(log?.status_to || '') === status)
    );
    if (statusWasUsedBefore) {
      const ok = window.confirm(`This order has already been moved to ${status} before. Continue changing it again? Automatic SMS is deduped, so a repeat SMS requires the Resend SMS button.`);
      if (!ok) return;
    }
    setChangingOrderStatus(true);
    try {
      await request(`/orders/${order.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      const now = new Date().toISOString();
      const nextHistory = Array.isArray(statusHistory) ? [...statusHistory] : [];
      nextHistory.push({ at: now, action: 'status_update', from: order.status, to: status, by: 'admin' });
      const updatedOrder = {
        ...order,
        status,
        payment_status: status === 'delivered' ? 'paid' : order.payment_status,
        statusHistory: nextHistory
      };
      onUpdate(updatedOrder);
      setDraft(prev => ({
        ...prev,
        status,
        paymentStatus: status === 'delivered' ? 'paid' : prev.paymentStatus
      }));
      addToast('Order status updated', 'success');
      await loadOrderSmsLogs();
    } catch (err) {
      addToast(err?.message || 'Failed to update order status', 'error');
    } finally {
      setChangingOrderStatus(false);
    }
  }, [request, order, statusHistory, smsLogs, onUpdate, addToast, loadOrderSmsLogs]);
  const orderAdjustments = useMemo(() => (
    Array.isArray(order.manualAdjustments)
      ? order.manualAdjustments.filter((adj) => String(adj?.label || '') !== REQUIRED_PAYMENT_OVERRIDE_LABEL)
      : []
  ), [order.manualAdjustments]);
  const computedSubtotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);
  const computedProductDiscount = items.reduce((sum, it) => {
    const qty = Math.max(1, Number(it?.qty) || 1);
    const price = Math.max(0, Number(it?.price) || 0);
    const originalPrice = Math.max(0, Number(it?.originalPrice) || 0);
    return sum + (originalPrice > price ? (originalPrice - price) * qty : 0);
  }, 0);
  const computedOriginalSubtotal = computedSubtotal + computedProductDiscount;
  const computedAdjustments = orderAdjustments.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0);
  const computedGrandTotal = Number.isFinite(Number(order.total))
    ? Math.max(0, Number(order.total))
    : Math.max(0, computedSubtotal + (Number(shipping.cost) || 0) + computedAdjustments - (Number(order.discount_amount) || 0));
  const discountedSummaryItems = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const safeDiscount = Math.max(0, Number(order.discount_amount || 0));
    const pricedItems = safeItems.map((item, index) => {
      const qty = Math.max(1, Number(item?.qty) || 1);
      const unitPrice = Math.max(0, Number(item?.price) || 0);
      const lineTotal = unitPrice * qty;
      return { item, index, qty, unitPrice, lineTotal };
    });
    const subtotal = pricedItems.reduce((sum, entry) => sum + entry.lineTotal, 0);
    if (!safeDiscount || subtotal <= 0) {
      return pricedItems.map(({ item, qty, unitPrice, lineTotal }) => ({
        ...item,
        qty,
        originalUnitPrice: null,
        effectiveUnitPrice: unitPrice,
        originalLineTotal: null,
        effectiveLineTotal: lineTotal
      }));
    }

    const cappedDiscount = Math.min(safeDiscount, subtotal);
    const allocations = pricedItems.map(({ lineTotal, index }) => {
      const exact = lineTotal > 0 ? (lineTotal / subtotal) * cappedDiscount : 0;
      const base = Math.floor(exact);
      return { index, base, remainder: exact - base };
    });
    let remaining = Math.round(cappedDiscount - allocations.reduce((sum, entry) => sum + entry.base, 0));
    allocations
      .slice()
      .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
      .forEach((entry) => {
        if (remaining <= 0) return;
        entry.base += 1;
        remaining -= 1;
      });
    const discountByIndex = new Map(allocations.map((entry) => [entry.index, entry.base]));

    return pricedItems.map(({ item, index, qty, unitPrice, lineTotal }) => {
      const lineDiscount = Math.max(0, Number(discountByIndex.get(index) || 0));
      const effectiveLineTotal = Math.max(0, lineTotal - lineDiscount);
      return {
        ...item,
        qty,
        originalUnitPrice: lineDiscount > 0 ? unitPrice : null,
        effectiveUnitPrice: effectiveLineTotal / qty,
        originalLineTotal: lineDiscount > 0 ? lineTotal : null,
        effectiveLineTotal
      };
    });
  }, [items, order.discount_amount]);
  const normalizeTrackingLink = useCallback((value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^[a-z]+:\/\//i.test(raw)) return raw;
    if (raw.includes('.') && !/\s/.test(raw)) return `https://${raw}`;
    return null;
  }, []);

  const handleSaveTrackingUrl = async () => {
    setSavingTracking(true);
    try {
      await request(`/orders/${order.id}/tracking`, {
        method: 'PUT',
        body: JSON.stringify({ trackingUrl: trackingUrl.trim() })
      });
      addToast('Tracking URL updated successfully', 'success');
      const updatedOrder = { ...order, tracking_url: trackingUrl.trim() };
      onUpdate(updatedOrder);
    } catch (err) {
      addToast('Failed to update tracking URL', 'error');
    } finally {
      setSavingTracking(false);
    }
  };

  const draftTotals = useMemo(() => {
    const itemsSubtotal = (draft.items || []).reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);
    const productDiscount = (draft.items || []).reduce((sum, it) => {
      const qty = Math.max(1, Number(it?.qty) || 1);
      const price = Math.max(0, Number(it?.price) || 0);
      const originalPrice = Math.max(0, Number(it?.originalPrice) || 0);
      return sum + (originalPrice > price ? (originalPrice - price) * qty : 0);
    }, 0);
    const shippingCost = Number(draft.shipping?.cost || 0);
    const adjustments = orderAdjustments.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const discount = Math.max(0, Number(draft.adminDiscountAmount ?? order.discount_amount ?? 0) || 0);
    const paymentCashoutCharge = draft.payment?.method === 'bkash' && settings.bkashCashoutEnabled
      ? Math.max(0, Number(draft.payment?.cashoutCharge ?? settings.bkashCashoutAmount) || 0)
      : 0;
    const hasRequiredPaymentAmount = draft.requiredPaymentAmount !== '' && draft.requiredPaymentAmount !== null && Number.isFinite(Number(draft.requiredPaymentAmount));
    const total = hasRequiredPaymentAmount
      ? Math.max(0, Math.round(Number(draft.requiredPaymentAmount)))
      : Math.max(0, Math.round(itemsSubtotal + shippingCost + adjustments + paymentCashoutCharge - discount));
    return { itemsSubtotal, productDiscount, shippingCost, adjustments, discount, paymentCashoutCharge, hasRequiredPaymentAmount, total };
  }, [draft, order.discount_amount, orderAdjustments, settings]);
  const currentStatusSupportsSms = SMS_STATUS_EVENTS.includes(order.status);

  const saveOrderChanges = async () => {
    setSavingOrder(true);
    try {
      const now = new Date().toISOString();
      const resolvedPaymentStatus = draft.status === 'delivered' ? 'paid' : draft.paymentStatus;
      const payload = {
        status: draft.status,
        paymentStatus: resolvedPaymentStatus,
        fulfillmentStatus: draft.fulfillmentStatus,
        customer: {
          firstName: draft.customer.firstName,
          lastName: draft.customer.lastName,
          name: `${draft.customer.firstName || ''} ${draft.customer.lastName || ''}`.trim() || draft.customer.name,
          email: draft.customer.email,
          phone: draft.customer.phone
        },
        shipping: { ...draft.shipping },
        payment: { ...draft.payment },
        items: draft.items,
        adminDiscountAmount: Math.max(0, Math.round(Number(draft.adminDiscountAmount || 0) || 0)),
        requiredPaymentAmount: draftTotals.hasRequiredPaymentAmount ? Number(draft.requiredPaymentAmount) : null,
        adminNotes: draft.adminNotes,
        refundAmount: draft.refundAmount,
        cancelReason: draft.cancelReason,
        shippingOverride: Number(draft.shipping?.cost || 0)
      };
      await request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      const nextHistory = Array.isArray(statusHistory) ? [...statusHistory] : [];
      if (draft.status !== order.status) {
        nextHistory.push({ at: now, action: 'status_update', from: order.status, to: draft.status, by: 'admin' });
      }
      if (resolvedPaymentStatus !== order.payment_status) {
        nextHistory.push({ at: now, action: 'payment_status', from: order.payment_status, to: resolvedPaymentStatus, by: 'admin' });
      }
      if (draft.fulfillmentStatus !== order.fulfillment_status) {
        nextHistory.push({ at: now, action: 'fulfillment_status', from: order.fulfillment_status, to: draft.fulfillmentStatus, by: 'admin' });
      }
      nextHistory.push({ at: now, action: 'order_edit', by: 'admin' });
      const updatedOrder = {
        ...order,
        status: draft.status,
        payment_status: resolvedPaymentStatus,
        fulfillment_status: draft.fulfillmentStatus,
        customer: payload.customer,
        shipping: payload.shipping,
        payment: payload.payment,
        items: payload.items,
        manualAdjustments: order.manualAdjustments,
        requiredPaymentAmount: payload.requiredPaymentAmount,
        admin_notes: payload.adminNotes,
        refund_amount: payload.refundAmount,
        cancel_reason: payload.cancelReason,
        statusHistory: nextHistory,
        discount_amount: payload.adminDiscountAmount,
        total: draftTotals.total
      };
      onUpdate(updatedOrder);
      addToast('Order updated', 'success');
      setEditMode(false);
    } catch (err) {
      addToast('Failed to update order', 'error');
    } finally {
      setSavingOrder(false);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[220] bg-black/60 backdrop-blur-[1px] flex items-start sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-4 p-6 border-b border-gray-200 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Order Details</h2>
            <p className="text-sm text-gray-500 mt-1">Order ID: {order.display_order_id || order.id}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={order.status || 'pending'}
              onChange={(e) => handleHeaderStatusChange(e.target.value)}
              disabled={changingOrderStatus}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Change order status"
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button
              type="button"
              onClick={() => handleManualSmsResend(order.status)}
              disabled={!currentStatusSupportsSms || smsActionLoading === `manual-${order.status}`}
              title={currentStatusSupportsSms ? 'Resend the SMS for this order status' : 'SMS resend is only available for confirmed, shipped, delivered, or cancelled orders'}
              className={`px-3 py-1.5 text-sm rounded-md border ${
                currentStatusSupportsSms
                  ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
              } disabled:opacity-70`}
            >
              {smsActionLoading === `manual-${order.status}` ? 'Sending SMS...' : 'Resend SMS'}
            </button>
            <button
              type="button"
              onClick={() => openShippingPaper(order.id)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Print shipping slip
            </button>
            <button
              type="button"
              onClick={() => openInvoice(order.id)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              See invoice
            </button>
            <div className="hidden h-8 w-px bg-gray-200 sm:block" aria-hidden="true" />
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Edit order
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <Icon.X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto min-h-0">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
            <div className="space-y-6">
          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Customer</h3>
              <div className="text-gray-800 font-semibold">{customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '-'}</div>
              <div className="text-gray-600 text-sm">Email: {customer.email}</div>
              <div className="text-gray-600 text-sm">Phone: {customer.phone}</div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Shipping</h3>
              <div className="text-gray-800">{shipping.address || shipping.house}, {shipping.city}</div>
              <div className="text-gray-600 text-sm">Option: {shipping.option || shipping.location || '-'}</div>
              <div className="text-gray-600 text-sm">Cost: {shipping.cost ? `${shipping.cost}৳` : '-'}</div>
            </div>
          </div>
          {/* Order Status + Payment */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Order Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Status</div>
                <div className="text-gray-900 font-medium">{order.status}</div>
              </div>
              <div>
                <div className="text-gray-500">Payment status</div>
                <div className="text-gray-900 font-medium">{order.payment_status || (payment.method === 'bkash' ? 'pending' : 'unpaid')}</div>
              </div>
              <div>
                <div className="text-gray-500">Fulfillment status</div>
                <div className="text-gray-900 font-medium">{order.fulfillment_status || 'unfulfilled'}</div>
              </div>
              <div>
                <div className="text-gray-500">Payment method</div>
                <div className="text-gray-900 font-medium">{payment.method || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Created</div>
                <div className="text-gray-900 font-medium">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</div>
              </div>
              {payment.bkashFrom && (
                <div>
                  <div className="text-gray-500">bKash from</div>
                  <div className="text-gray-900 font-medium">{payment.bkashFrom}</div>
                </div>
              )}
              {payment.bkashTxn && (
                <div>
                  <div className="text-gray-500">bKash transaction</div>
                  <div className="text-gray-900 font-medium">{payment.bkashTxn}</div>
                </div>
              )}
            </div>
            {payment.bkashScreenshotUrl && (
              <div className="mt-4">
                <div className="text-sm text-gray-700 mb-1">Payment screenshot</div>
                <a href={(String(payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${payment.bkashScreenshotUrl}` : payment.bkashScreenshotUrl)} target="_blank" rel="noopener noreferrer">
                  <img src={(String(payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${payment.bkashScreenshotUrl}` : payment.bkashScreenshotUrl)} alt="bKash receipt" className="h-24 w-24 object-cover rounded border" />
                </a>
              </div>
            )}
          </div>
          {editMode && (
            <div
              className="fixed inset-0 z-[230] bg-black/60 flex items-center justify-center p-4"
              onClick={() => {
                setDraft(buildDraft(order));
                setEditMode(false);
              }}
            >
              <div
                className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[92vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Edit Order</h3>
                    <p className="text-sm text-gray-500 mt-1">Order ID: {order.display_order_id || order.id}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDraft(buildDraft(order));
                      setEditMode(false);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon.X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(92vh-96px)]">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order status</label>
                    <select
                      value={draft.status}
                      onChange={(e) => setDraft(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment status</label>
                    <select
                      value={draft.paymentStatus}
                      onChange={(e) => setDraft(prev => ({ ...prev, paymentStatus: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="refunded">Refunded</option>
                      <option value="partial_refund">Partial Refund</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fulfillment status</label>
                    <select
                      value={draft.fulfillmentStatus}
                      onChange={(e) => setDraft(prev => ({ ...prev, fulfillmentStatus: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="unfulfilled">Unfulfilled</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="returned">Returned</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Customer</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={draft.customer.firstName}
                        onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, firstName: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="First name"
                      />
                      <input
                        type="text"
                        value={draft.customer.lastName}
                        onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, lastName: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Last name"
                      />
                    </div>
                    <input
                      type="email"
                      value={draft.customer.email}
                      onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, email: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      value={draft.customer.phone}
                      onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, phone: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Shipping</h4>
                    <input
                      type="text"
                      value={draft.shipping.house || draft.shipping.address || ''}
                      onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, house: e.target.value, address: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Address"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={draft.shipping.city || ''}
                        onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, city: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="City"
                      />
                      <input
                        type="number"
                        min="0"
                        value={Number(draft.shipping.cost || 0)}
                        onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, cost: parseInt(e.target.value) || 0 } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Shipping cost"
                      />
                    </div>
                    <input
                      type="text"
                      value={draft.shipping.option || ''}
                      onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, option: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Shipping option"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Payment</h4>
                    <select
                      value={draft.payment.method || 'cod'}
                      onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, method: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="cod">Cash on Delivery</option>
                      <option value="bkash">bKash</option>
                      <option value="bank">Bank transfer</option>
                    </select>
                    {draft.payment.method === 'bkash' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={draft.payment.bkashFrom || ''}
                          onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, bkashFrom: e.target.value } }))}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="bKash from"
                        />
                        <input
                          type="text"
                          value={draft.payment.bkashTxn || ''}
                          onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, bkashTxn: e.target.value } }))}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="bKash txn"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Required payment amount</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft.requiredPaymentAmount}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setDraft(prev => ({
                            ...prev,
                            requiredPaymentAmount: raw === '' ? '' : Math.max(0, Number(raw) || 0)
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Leave empty to auto-calculate"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Special Discount</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft.adminDiscountAmount}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setDraft(prev => ({
                            ...prev,
                            adminDiscountAmount: raw === '' ? '' : Math.max(0, Number(raw) || 0)
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Admin notes</label>
                      <textarea
                        rows={1}
                        value={draft.adminNotes}
                        onChange={(e) => setDraft(prev => ({ ...prev, adminNotes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Internal notes visible to admins only"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Line items</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-left">Qty</th>
                          <th className="px-3 py-2 text-left">Price</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {draft.items.map((it, idx) => (
                          <tr key={it.id || idx}>
                            <td className="px-3 py-2">
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={it.title}
                                  onChange={(e) => {
                                    const next = [...draft.items];
                                    next[idx] = { ...next[idx], title: e.target.value };
                                    setDraft(prev => ({ ...prev, items: next }));
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                />
                                {getAdminOrderOptionLabel(it, productLookup) && (
                                  <div className="text-xs text-gray-500">
                                    Option: {getAdminOrderOptionLabel(it, productLookup)}
                                  </div>
                                )}
                                {it.variantSku && (
                                  <div className="text-xs text-gray-500">SKU: {it.variantSku}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 w-24">
                              <input
                                type="number"
                                min="1"
                                value={it.qty}
                                onChange={(e) => {
                                  const next = [...draft.items];
                                  next[idx] = { ...next[idx], qty: Math.max(1, parseInt(e.target.value) || 1) };
                                  setDraft(prev => ({ ...prev, items: next }));
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                              />
                            </td>
                            <td className="px-3 py-2 w-28">
                              <input
                                type="number"
                                min="0"
                                value={it.price}
                                onChange={(e) => {
                                  const next = [...draft.items];
                                  next[idx] = { ...next[idx], price: Number(e.target.value) || 0 };
                                  setDraft(prev => ({ ...prev, items: next }));
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                              />
                            </td>
                            <td className="px-3 py-2">{currency((Number(it.price) || 0) * (Number(it.qty) || 0))}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setDraft(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDraft(prev => ({ ...prev, items: [...prev.items, { id: `manual_${Date.now()}`, title: 'New item', price: 0, qty: 1 }] }))}
                    className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    Add item
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancel reason</label>
                    <input
                      type="text"
                      value={draft.cancelReason}
                      onChange={(e) => setDraft(prev => ({ ...prev, cancelReason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Reason for cancellation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund amount</label>
                    <input
                      type="number"
                      min="0"
                      value={draft.refundAmount}
                      onChange={(e) => setDraft(prev => ({ ...prev, refundAmount: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Items subtotal</span>
                    <span className="font-medium">{currency(draftTotals.itemsSubtotal + draftTotals.productDiscount)}</span>
                  </div>
                  {draftTotals.productDiscount > 0 && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Product discount</span>
                      <span className="font-medium">-{currency(draftTotals.productDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>Shipping</span>
                    <span className="font-medium">{currency(draftTotals.shippingCost)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Manual adjustments</span>
                    <span className="font-medium">{currency(draftTotals.adjustments)}</span>
                  </div>
                  {draftTotals.paymentCashoutCharge > 0 && (
                    <div className="flex items-center justify-between">
                      <span>bKash cashout charge</span>
                      <span className="font-medium">{currency(draftTotals.paymentCashoutCharge)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>{order.promo_code ? `Order discount (${order.promo_code})` : 'Special Discount'}</span>
                    <span className="font-medium text-green-700">-{currency(draftTotals.discount)}</span>
                  </div>
                  {draftTotals.hasRequiredPaymentAmount && (
                    <div className="flex items-center justify-between">
                      <span>Amount to collect</span>
                      <span className="font-medium text-blue-700">{currency(draftTotals.total)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-base font-semibold mt-1">
                    <span>Total</span>
                    <span>{currency(draftTotals.total)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDraft(buildDraft(order));
                      setEditMode(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveOrderChanges}
                    disabled={savingOrder}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingOrder ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </div>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Activity</h3>
              {statusHistory.length === 0 ? (
                <div className="text-sm text-gray-500">No activity history yet.</div>
              ) : (
                <div className="space-y-2 text-sm text-gray-600 max-h-44 overflow-auto">
                  {statusHistory.slice().reverse().map((entry, idx) => (
                    <div key={`${entry.at}-${idx}`} className="flex items-start gap-2">
                      <div className="text-xs text-gray-400 min-w-[120px]">{entry.at ? new Date(entry.at).toLocaleString() : '-'}</div>
                      <div>
                        <div className="text-gray-800">
                          {entry.action || 'update'} {entry.from ? `: ${entry.from}` : ''}{entry.to ? ` → ${entry.to}` : ''}
                        </div>
                        {entry.by && <div className="text-xs text-gray-500">by {entry.by}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-lg font-medium text-gray-900">SMS History</h3>
                <button
                  type="button"
                  onClick={loadOrderSmsLogs}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {smsLogsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              <div className="max-h-44 overflow-auto space-y-2 pr-1">
                {smsLogs.length === 0 ? (
                  <div className="text-sm text-gray-500">No SMS history for this order.</div>
                ) : smsLogs.map((log) => (
                  <div key={log.id} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">
                          {log.status_to || log.event_type} {log.manual ? '(manual)' : ''}
                        </div>
                        <div className="text-gray-500">{log.recipient_phone || '-'}</div>
                        <div className="mt-1 truncate text-gray-600" title={log.message || ''}>{log.message || '-'}</div>
                        {(log.skip_reason || log.error) && (
                          <div className="mt-1 text-orange-700">{log.skip_reason || log.error}</div>
                        )}
                        {(log.provider_tracking_id || log.provider_trxn_id) && (
                          <div className="mt-1 text-gray-500">Provider: {log.provider_tracking_id || log.provider_trxn_id}</div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${
                          log.status === 'sent' ? 'bg-green-100 text-green-700' :
                          log.status === 'failed' ? 'bg-red-100 text-red-700' :
                          log.status === 'skipped' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {log.status}
                        </span>
                        <div className="mt-1 text-gray-500">{log.estimated_segments || 0} seg</div>
                      </div>
                    </div>
                    {(log.status === 'failed' || log.status === 'skipped') && (
                      <button
                        type="button"
                        onClick={() => handleRetrySms(log)}
                        disabled={smsActionLoading === log.id}
                        className="mt-2 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {smsActionLoading === log.id ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
          <aside className="space-y-4 xl:sticky xl:top-0 self-start">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Summary</h3>
              <div className="mb-3 pb-3 border-b border-gray-200">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Items</h4>
                {items.length === 0 ? (
                  <div className="text-sm text-gray-500">No items</div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-auto pr-1">
                    {discountedSummaryItems.map((item, idx) => (
                      <div key={`${item.id || item.title || 'item'}-${idx}`} className="flex items-start justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <div className="text-gray-900 font-medium truncate">{item.title || 'Untitled item'}</div>
                          {getAdminOrderOptionLabel(item, productLookup) && (
                            <div className="text-xs text-gray-500">Option: {getAdminOrderOptionLabel(item, productLookup)}</div>
                          )}
                          {item.variantSku && <div className="text-xs text-gray-500">SKU: {item.variantSku}</div>}
                          <div className="text-xs text-gray-500">{item.qty}x</div>
                        </div>
                        <AdminPriceDisplay
                          price={item.effectiveLineTotal}
                          originalPrice={item.originalLineTotal}
                          compact
                          align="right"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span className="font-medium">{currency(computedOriginalSubtotal)}</span>
                </div>
                {computedProductDiscount > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Product discount</span>
                    <span className="font-medium">-{currency(computedProductDiscount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>{order.promo_code ? `Order discount (${order.promo_code})` : 'Special Discount'}</span>
                    <span className="font-medium">-{currency(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-gray-700">
                  <span>Shipping</span>
                  <span className="font-medium">{currency(shipping.cost || 0)}</span>
                </div>
                <div className="flex items-center justify-between text-base font-semibold pt-2 mt-2 border-t border-gray-200">
                  <span>Grand Total</span>
                  <span>{currency(computedGrandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Tracking</h3>
              <input
                type="text"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                placeholder="Enter tracking URL or tracking number"
              />
              <button
                onClick={handleSaveTrackingUrl}
                disabled={savingTracking}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingTracking && <Icon.Loading className="w-4 h-4" />}
                {savingTracking ? 'Saving...' : 'Save Tracking'}
              </button>
              {(order.tracking_url || trackingUrl) && (
                <div className="mt-2">
                  {normalizeTrackingLink(order.tracking_url || trackingUrl) ? (
                    <a href={normalizeTrackingLink(order.tracking_url || trackingUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">
                      Open Tracking
                    </a>
                  ) : (
                    <div className="text-sm text-gray-600 break-all">{order.tracking_url || trackingUrl}</div>
                  )}
                </div>
              )}
            </div>

          </aside>
        </div>
      </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
}



















